import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * The event vocabulary — one closed union, shared by every consumer.
 *
 * Boilerplate plan §3.D.4, §2.6 and §4.1. `00-stack-recommendation.md` §4.1
 * observes that five requirements across four documents (`FR-COLLAB-017`,
 * `FR-NOTIF-001`, `FR-COLLAB-025`, `FR-AUTO-034`, `FR-AUTO-003`) are one
 * mechanism: an append-only stream written in the same transaction as every
 * mutation and fanned out to five consumers. Phase 1 consumes it twice — the
 * comment stream and live board updates — which is exactly why it is cheap to
 * put in now and a rewrite of every write path to add at Phase 3.
 *
 * DECISION RECORDED (§2.6): `activity_entries` is the spine. It already carries
 * actor, type, a JSONB payload, board and card scoping, and it is already in
 * the port's parity gate. A separate `events` table with `activity_entries` as
 * a projection is cleaner in principle and costs a second write path plus
 * duplicated rows; it was not chosen.
 *
 * OUTSTANDING MIGRATION: the discriminator column that says whether an entry
 * surfaces in the user-visible feed does not exist yet — see §5 and
 * `apps/server/README.md`. `surfacesInFeed` below is where that value comes
 * from once the column lands; until then it is carried in code only.
 */

/**
 * `activity_entries.type` is `varchar(32)`. A longer name is not a type error,
 * it is a runtime insert failure — so the registry is checked against it.
 */
export const EVENT_TYPE_MAX_LENGTH = 32;

export interface EventTypeDeclaration {
  /**
   * Whether this event is shown to users as activity, as opposed to being
   * infrastructure that only drives live updates, search indexing or
   * automation. This is the §2.6 discriminator.
   */
  readonly surfacesInFeed: boolean;
}

/**
 * The registry. Every event the product can emit is declared here and nowhere
 * else — the writer, the WebSocket layer, the search indexer and later the
 * automation matcher all read this one object.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * `skeleton.noop` is a PLACEHOLDER and is retained on purpose, not by
 * oversight. It carries no product meaning; it is the fixture
 * `apps/server/src/ws/board-socket.integration.test.ts` publishes to prove
 * the Redis → socket fan-out works without depending on any feature's
 * vocabulary. Delete it only together with that test's payload.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Extending the union is the sanctioned way to add an event type (plan §7,
 * SC-1). Adding a `type` string at a call site instead is a runtime insert
 * failure against `activity_entries.type`, not a compile error.
 */
export const EVENT_TYPES = {
  'skeleton.noop': { surfacesInFeed: false },

  // ---------------------------------------------------------------------
  // Profile — B06, `FR-PERM-002`. One event per field, mirroring the
  // field-scoped patch routes (SC-4). A single `user.profile_updated` with
  // the changed field named in the payload was the alternative and was
  // rejected: it forces every reader — the feed, and later the automation
  // matcher, which matches on `type` — to switch on `payload` instead, so
  // the closed union stops being the thing that enumerates what can happen.
  //
  // `surfacesInFeed: false` on all three. The user-visible feed is
  // board-scoped and these entries carry `boardId: null`, so surfacing them
  // would mean deciding which boards a profile edit appears on — a question
  // `FR-PERM-002` never asks. It specifies only that the *current* name and
  // avatar are what appear wherever the member is referenced, which is a
  // read-time join, not a feed entry. They are recorded because SC-1 admits
  // no unrecorded mutation: an event history never written cannot be
  // recovered, and "who changed this display name, and when" is an audit
  // question that outlives Phase 1.
  // ---------------------------------------------------------------------
  'user.display_name_changed': { surfacesInFeed: false },
  'user.bio_changed': { surfacesInFeed: false },
  'user.avatar_changed': { surfacesInFeed: false },
  // BE-44.6, `FR-NOTIF-033` AC3/AC4. The rename, mirroring the three sibling
  // field-scoped profile events above: recorded, never surfaced (the feed is
  // board-scoped and this entry carries `boardId: null`), admitted to both
  // live CHECK constraints by `db/postgres/migrations/0059_username_history`.
  'user.username_changed': { surfacesInFeed: false },

  // ---------------------------------------------------------------------
  // Workspaces — B07, `FR-PERM-004`, `FR-PERM-014`, `FR-PERM-021`.
  //
  // Dotted, and `db/postgres/migrations/0012_workspace_activity.sql` is
  // where that was settled for every event from here on: this registry is
  // the only thing that writes `activity_entries.type` (`insertEvents`
  // parses through `eventInputSchema`, whose `z.enum` is built from
  // `EVENT_TYPE_NAMES`). **That rename has landed.**
  // `db/postgres/migrations/0013_board_activity.sql` restated
  // `ck_activity_entries_type` with all forty-nine snake_case placeholders
  // renamed to dotted, so the constraint and this registry now speak one
  // vocabulary and the question is closed. The placeholders are still
  // placeholders — nothing emits them — and a block that first emits one
  // registers it here then, not in advance.
  //
  // `surfacesInFeed: false` on all three, for the reason the profile block
  // above gives: the user-visible feed is board-scoped and these entries
  // carry `boardId: null`, so surfacing one would mean deciding which
  // boards a workspace membership change appears on — a question none of
  // the three requirements asks. They are recorded because SC-1 admits no
  // unrecorded mutation, and "who added this person, and when" is an audit
  // question that outlives Phase 1. Note the practical consequence, stated
  // rather than discovered: `publishEvents` fans out only entries carrying
  // a `boardId`, so these are written and never broadcast — correct, since
  // no workspace room exists to broadcast them to.
  //
  // The workspace id travels in `payload`. `activity_entries` has actor,
  // board and card foreign keys and **no `workspace_id` column**; 0012's
  // header records that trade and names the block that should add the
  // column if a workspace-scoped feed is ever specified.
  // ---------------------------------------------------------------------
  'workspace.created': { surfacesInFeed: false },
  'workspace.profile_changed': { surfacesInFeed: false },
  'workspace.visibility_changed': { surfacesInFeed: false },
  'workspace.deleted': { surfacesInFeed: false },
  'workspace.member_added': { surfacesInFeed: false },
  'workspace.member_role_changed': { surfacesInFeed: false },
  'workspace.member_removed': { surfacesInFeed: false },
  'workspace.member_left': { surfacesInFeed: false },
  'user.deactivated': { surfacesInFeed: false },
  'user.deleted': { surfacesInFeed: false },

  // ---------------------------------------------------------------------
  // Boards — B08, `FR-BOARD-001`, `FR-BOARD-003`, `FR-EXP-012` (which
  // absorbs `FR-BOARD-007` and `FR-BOARD-008`).
  //
  // THESE ARE THE FIRST EVENTS IN THIS PRODUCT THAT CARRY A REAL `boardId`,
  // and that is a behaviour change in the event spine rather than five more
  // rows in a table. `publishEvents` (`apps/server/src/events/writer.ts`)
  // filters on `boardId !== null` and publishes what survives to
  // `boardChannel(boardId)` after the transaction commits. Every event
  // registered before this one — the three profile events and the three
  // workspace events — carries `boardId: null` and is therefore written and
  // never broadcast, so `molt:board:*` has so far only ever carried
  // `skeleton.noop` from an integration fixture. From B08 onwards the fan-out
  // is live: emitting one of the five below puts a message on a real board
  // room, and anything subscribed to that room receives it. That is what
  // `FR-COLLAB-025` (live board updates, B18) will consume, and it is the
  // mechanism `FR-BOARD-008`'s edge case needs — "members currently viewing
  // the board when it is deleted must be moved to their boards list with an
  // explanation rather than left on a broken page" is a client reacting to
  // `board.deleted` arriving on the room it is sitting in.
  //
  // NOTE what does **not** gate the fan-out: `surfacesInFeed`. It is the
  // §2.6 feed discriminator and nothing else reads it, so a `false` here
  // still broadcasts. `board.deleted` relies on exactly that.
  //
  // `board.created` is published to a room nobody can be subscribed to — the
  // board did not exist when any socket joined a room — so it is a broadcast
  // with no listener by construction. Stated rather than discovered, so that
  // nobody later "fixes" it by suppressing the event: it is the first entry
  // in the board's own history and SC-1 admits no unrecorded mutation.
  //
  // All five satisfy `ck_activity_entries_scope`'s first disjunct because
  // they carry `board_id`, so 0013 added no by-name exemption — including
  // `board.deleted`, because deletion is a soft delete (D2 of the block
  // brief) and the row it points at survives. `fk_activity_entries_board_id`
  // is `ON DELETE CASCADE`: a hard delete would take the board's entire
  // activity history with it, and a `board.deleted` entry would delete
  // itself on commit.
  // ---------------------------------------------------------------------

  /**
   * A board came into existence — `FR-BOARD-001`. Surfaces: it is the first
   * line of the board's own activity, and the one entry that explains where
   * everything after it happened.
   *
   * ⚠ **TWO ADDITIVE, MUTUALLY EXCLUSIVE KEYS HAVE JOINED THIS PAYLOAD SINCE
   * B08 SHIPPED, AND THIS ENTRY IS WHERE BOTH ARE RECORDED — THE FIRST TIME
   * EITHER HAS BEEN, RATHER THAN LEFT TO THE ROUTE FILE THAT WRITES IT.**
   * `POST /boards/:id/copy` (B35) adds `copiedFromBoardId`;
   * `POST /boards/:id/create-from-template` (B36) adds
   * `createdFromTemplateBoardId`. Both are additive JSONB on B08's own
   * `{ workspaceId, title }`, on `board.renamed`'s `previousTitle` precedent
   * ("extend the payload, do not mint a name") — B35's own docstring in
   * `apps/server/src/routes/board-copy.ts` argues the first key at the point
   * it is written, and neither key changes this entry's registration or its
   * `surfacesInFeed` flag.
   *
   * **A given row carries AT MOST ONE of the two, and they are not
   * synonyms.** `copiedFromBoardId` names the source of a PLAIN copy —
   * `POST /boards/:id/copy`, which may copy a template or an ordinary board
   * alike, because a template is still an ordinary board on every surface
   * that does not ask `isTemplate`. `createdFromTemplateBoardId` names the
   * template a NEW board was instantiated FROM —
   * `POST /boards/:id/create-from-template`, which calls the same
   * `copyBoardInto` engine (`apps/server/src/copy/copy-board.ts`) but is a
   * sibling route, not a call through the generic copy route, and it is the
   * one surface `boards.created_from_template_board_id` is written from. An
   * ordinary `POST /boards` create carries neither key. A reader who cannot
   * tell "this board is a copy" from "this board was instantiated from a
   * template" from the payload alone has lost a fact every other block that
   * has extended this entry's payload has taken care to keep recoverable.
   */
  'board.created': { surfacesInFeed: true },
  /**
   * `FR-BOARD-003` is the only requirement in this block that *specifies* an
   * activity entry — "the rename is recorded in board activity (see
   * 03-collaboration.md)" — so this one is `true` by the requirement rather
   * than by judgement. Note its counterpart: AC2 says an in-progress rename
   * abandoned with Escape creates no entry, which is a client-side promise
   * the server keeps trivially by never being asked.
   *
   * ⚠ **Since B28, the payload carries `previousTitle` beside `title`** —
   * `apps/server/src/routes/boards.ts`'s `PATCH /boards/:id/title` handler,
   * additive JSONB, no migration. `FR-COLLAB-018` names *"renamed this card
   * (from &lt;old name&gt;)"* as the canonical sentence, and B28's grammar
   * renders that from `previousTitle` (the board-level counterpart, "renamed
   * this board"). **Entries written before this change do not carry the
   * key** — the whole historical corpus — so the grammar has a fallback
   * branch for its absence rather than assuming every `board.renamed` row
   * looks alike; see `packages/shared/src/activity.ts`'s header for how that
   * fallback is expressed as a neutral placeholder rather than a second
   * template.
   */
  'board.renamed': { surfacesInFeed: true },
  /**
   * `FR-EXP-012`. Surfaces, because a closed board is still readable — AC2
   * requires its URL to show the board in a read-only state with a Reopen
   * action "rather than a not-found page" — so its feed is reachable and
   * "who closed this, and when" is the question a member arriving on it
   * asks first.
   */
  'board.closed': { surfacesInFeed: true },
  /** `FR-EXP-012`, the other half. Same reasoning as `board.closed`. */
  'board.reopened': { surfacesInFeed: true },
  /**
   * `FR-EXP-012` AC3, `FR-BOARD-008`. **The one board event that does not
   * surface**, and the reason is not squeamishness: after it is emitted the
   * board's URL "returns not-found for everyone", every read path carries
   * `deleted_at IS NULL`, and there is no route that restores it — so the
   * feed this entry would appear in is unreachable for ever. An entry in a
   * feed nobody can open is not a feed entry; it is an audit record, which
   * is why it is written at all (SC-1) and why it is `false` here.
   *
   * It is still broadcast, and that is the point — see the note above.
   *
   * This name is the one the placeholder set did not contain;
   * `0013_board_activity.sql` added it.
   */
  'board.deleted': { surfacesInFeed: false },

  // ---------------------------------------------------------------------
  // Boards, continued — B09, `FR-PERM-012` (change a board's visibility) and
  // `FR-PERM-015` (board roles).
  //
  // Both carry a real `boardId`, so everything the B08 block above says about
  // the fan-out applies unchanged: `publishEvents` filters on
  // `boardId !== null` and puts these on `boardChannel(boardId)` after the
  // transaction commits, and both satisfy `ck_activity_entries_scope`'s first
  // disjunct — so the migration below added **no** by-name exemption to that
  // constraint.
  //
  // THE ASYMMETRY WORTH RECORDING, because it will not be guessable later:
  // **one of these needed DDL and the other did not.**
  // `board.visibility_changed` has been a member of
  // `ck_activity_entries_type` since `0013_board_activity.sql` — it was one of
  // the placeholders `0004_collaboration_and_activity.sql` enumerated as
  // `board_visibility_changed`, and 0013's mechanical first-underscore-to-dot
  // rename produced the dotted spelling. So B09 is the first block to register
  // a name the constraint already admitted, which is exactly the case 0013's
  // header anticipated when it said the placeholders stay placeholders and "a
  // block that first emits one registers it here then, not in advance".
  // `board.member_role_changed` was in the constraint in no spelling — the
  // only `*.member_role_changed` there is `workspace.member_role_changed` — so
  // it needed `0014_board_roles_activity.sql`, which restates the constraint
  // with it added. 25 characters, inside `EVENT_TYPE_MAX_LENGTH`. (The B09
  // README section names this file `0014_board_membership_activity.sql` in one
  // place — a one-word doc bug fixed at the point of writing this comment; the
  // file on disk, and the one to grep for, is `0014_board_roles_activity.sql`.
  // Do not confuse it with B10's own `0015_board_membership_activity.sql`
  // below, which is a different migration entirely.)
  //
  // A note for whoever greps the migrations for either name: match the
  // **dotted** form. The pre-0013 snake_case spelling
  // `board_visibility_changed` still appears in 0004, 0011 and 0012, and those
  // are historical constraint bodies, not the live vocabulary.
  // ---------------------------------------------------------------------

  /**
   * A board's visibility changed — `FR-PERM-012`.
   *
   * `true` **by the requirement rather than by judgement**, the same footing
   * `board.renamed` is on: `FR-PERM-012` AC4 says "given a board admin widens
   * visibility, when the change is saved, then it is recorded in the board's
   * activity feed".
   *
   * Note the scope this registration deliberately exceeds. AC4 says *widens*
   * and is silent on narrowing; every change is recorded, because
   * `FR-PERM-025` AC4 — "given a board admin changes any setting, when the
   * board activity feed is read, then the change is recorded" — is the same
   * document's superset, and a feed that shows a board going public but not
   * coming back is worse than either alternative. The payload carries the
   * previous and the new level for the same reason: `BE-28.3` can then render
   * "widened" against "narrowed" without a second query.
   */
  'board.visibility_changed': { surfacesInFeed: true },
  /**
   * A board's shared background changed — `FR-EXP-025` / `FR-BOARD-002`, B42.
   *
   * Payload convention: `{ workspaceId, previous, current }`, where the two
   * images are compact snapshots of the stored board background columns rather
   * than provider response blobs. `board_id` is on the column, never only in
   * the payload, so the event reaches the board socket room.
   *
   * `true`, on `board.visibility_changed` and `board.permission_changed`'s
   * footing: a board-level setting visible to every board member changed, and
   * the feed should answer who changed it and when.
   */
  'board.background_changed': { surfacesInFeed: true },
  /**
   * A board's card key changed — `PATCH /boards/:id/key`,
   * `docs/notes/card-keys.md` (owner-directed, amending `FR-CARD-005`).
   *
   * Payload convention: `{ workspaceId, previousKey, key }`. `board_id` is on
   * the column so the board socket room receives it (`publishEvents` fans out
   * on that column alone).
   *
   * `true`, on `board.visibility_changed`'s footing: a board-level setting
   * every member sees changed — every card reference on the board now starts
   * differently — and `FR-PERM-025` AC4 asks the feed to record any setting a
   * board admin changes. Admitted to `ck_activity_entries_type` by `0067`.
   */
  'board.key_changed': { surfacesInFeed: true },
  /**
   * A shared dashboard tile was created, changed or removed — `FR-VIEW-027`,
   * B53.
   *
   * These are board configuration events rather than private view-state events:
   * dashboard tiles are shared by every board member, mutated by admins, and
   * read by anyone who can read the board. They therefore follow
   * `board.background_changed`'s footing: `board_id` belongs on the column so
   * the board socket room receives the change, `card_id` stays null, and the
   * board activity feed can answer who changed the dashboard.
   *
   * Payload convention:
   * - create/delete: `{ tileId, facet, chartType }`
   * - change: `{ tileId, previous: { facet, chartType }, current: { facet, chartType } }`
   *
   * These names need a Postgres-only migration extending
   * `ck_activity_entries_type` before server emitters can safely write them.
   */
  'dashboard.tile_created': { surfacesInFeed: true },
  'dashboard.tile_changed': { surfacesInFeed: true },
  'dashboard.tile_deleted': { surfacesInFeed: true },

  /**
   * A card location was set, changed or removed — B54, `FR-VIEW-028` /
   * `FR-VIEW-029`.
   *
   * These are card-scoped and board-scoped content events: the location is a
   * visible card field that drives the shared Map view, so `board_id` and
   * `card_id` both belong on the columns. Payloads carry compact previous and
   * current location snapshots only: `{ previous, current }`, where each side
   * is either `{ text, lat, lng }` or null. No provider/geocoder response is
   * part of the event. Location text is searchable; coordinates are not.
   */
  'card.location_set': { surfacesInFeed: true },
  'card.location_changed': { surfacesInFeed: true },
  'card.location_removed': { surfacesInFeed: true },

  /**
   * A board member's role changed — `FR-PERM-015`, "admins can change any board
   * member's role".
   *
   * `false`, and unlike the entry above this one **is** a judgement, so here is
   * the argument. No requirement in B09 asks for it to surface; `B28` owns the
   * activity feed; and surfacing this one membership event while add-member
   * (`BE-10.1`) and remove-member (`BE-10.2`) are still unbuilt would produce a
   * feed that tells half a story — a role appearing to change for somebody the
   * feed never showed arriving. It is written regardless, because SC-1 admits
   * no unrecorded mutation and "who made this person an admin, and when" is an
   * audit question that outlives Phase 1.
   *
   * **B09 asked the block that builds the membership events to revisit this
   * flag together with theirs, as one decision — this is that revisit, and the
   * answer is unchanged.** `board.member_added` and `board.member_removed`
   * below land at `false` for their own reasons (neither `FR-PERM-018` nor
   * `FR-PERM-029` names the activity feed — see their entries), which means the
   * half-story objection above is now **moot rather than inherited**: all
   * three membership events are `false`, uniformly, so there is no longer a
   * role appearing to change for somebody the feed never showed arriving,
   * because the feed shows none of the three. A future block reopening any one
   * of them should reopen all three together, for the same reason.
   */
  'board.member_role_changed': { surfacesInFeed: false },

  // ---------------------------------------------------------------------
  // Boards, continued — B10, `FR-PERM-018` (add a member) and `FR-PERM-029`
  // (remove one). `FR-PERM-032` (last-admin protection) emits no event of its
  // own — it is a refusal, not a mutation, so there is nothing for SC-1 to
  // record; the operation it blocks (a role patch or a removal) simply does
  // not reach `emit()`.
  //
  // Both carry a real `boardId` and satisfy `ck_activity_entries_scope`'s
  // first disjunct exactly as every other board event above does, so
  // `publishEvents` fans both out to `boardChannel(boardId)` after commit and
  // neither needed a scope exemption.
  //
  // `db/postgres/migrations/0015_board_membership_activity.sql` is what makes
  // both insertable — restating `ck_activity_entries_type` with these two
  // dotted names added, on the same "extend the union, then the migration"
  // order every event above followed.
  //
  // ⚠ **`member.added` and `member.removed` are ALREADY members of
  // `ck_activity_entries_type`, and they are NOT these two events.** They are
  // `FR-CARD-007`/`FR-CARD-008` — *card*-membership assignment, owned by `B14`
  // — dotted by `0013_board_activity.sql`'s mechanical rename exactly like
  // every other 0004 placeholder. `board.member_added` and
  // `board.member_removed` are new names, not reuses of those two: this
  // registry is keyed by the full dotted string, so `member.added` and
  // `board.member_added` are two distinct rows in the same CHECK constraint,
  // and confusing them at this call site would record a board-membership
  // change under a type that means something else — a *card* assignment.
  // `0015` adds the two *board*-scoped names; it does not touch `member.added`
  // / `member.removed`.
  //
  // ⚠ **"and still unemitted by anything" stood here through B13 and is
  // corrected in place, because the correction makes this warning MORE useful
  // rather than retiring it.** B14 registered `member.added`, `member.removed`,
  // `member.self_joined` and `member.self_left` (see the B14 block at the foot
  // of this registry) and emits all four from `POST /cards/:id/members` and
  // `DELETE /cards/:id/members/:userId`. So the collision this paragraph warns
  // about is no longer between one live name and one placeholder — it is
  // between **two live names**, both reachable, both writing rows, differing
  // only by a `board.` prefix. A mistyped `member.added` at a board-membership
  // call site now writes a plausible row that B28 will render as somebody
  // joining a *card*, and nothing anywhere will fail.
  // ---------------------------------------------------------------------

  /**
   * A member was added to a board — `FR-PERM-018`.
   *
   * `false`. Checked against the same authority `board.visibility_changed`
   * above is held to, and it fails it: neither `FR-PERM-018`'s acceptance
   * criteria nor its edge case say the word "feed" or "activity" — they cover
   * the board appearing in the new member's board list, the no-account path,
   * workspace scoping for a cross-workspace add, and the add-member control
   * being withheld from a member without invite rights. That is a materially
   * different footing from `board.renamed` and `board.visibility_changed`,
   * both of which are `true` **because a requirement's own sentence names the
   * activity feed**, not by symmetry with either of them.
   *
   * The one requirement worth ruling out by name, because it is the one a
   * future reader could otherwise cite: `FR-PERM-025` AC4 — "given a board
   * admin changes any *setting*, … the change is recorded" — is the sentence
   * `board.visibility_changed`'s docstring already reads as a superset, and
   * its own text enumerates what a "setting" is: visibility, commenting
   * permission, add/remove-member permission (`FR-PERM-027`), the self-join
   * toggle, close and delete. **The *permission governing* board membership is
   * a setting; a membership itself is not.** Toggling FR-PERM-027 from
   * "admins" to "all members" would be AC4's business were that setting built;
   * a person joining or leaving the board is not, and reading AC4 to cover it
   * would also (by the identical argument) make `workspace.member_added`
   * `true`, which B07 already decided against on `boardId: null` grounds — see
   * that entry above.
   *
   * `FR-COLLAB-020` (the board activity feed itself, `03-collaboration.md`)
   * does not settle it either way and should not be read as settling it: its
   * behaviour text is card/list-worded ("entries from all cards and lists on
   * the board … each entry naming its card"), which would exclude
   * `board.created` and `board.renamed` too if read strictly, and those are
   * already `true` above. This registry has consistently read that sentence
   * loosely — as "the board's activity feed shows board-level events too", not
   * as a literal card/list scope — so a strict reading adopted now, only to
   * exclude these two, would be inconsistent with every board-level event
   * already registered rather than a genuine distinguishing feature of them.
   *
   * It is still recorded, because SC-1 admits no unrecorded mutation and "who
   * added this person, and when" is exactly the audit question
   * `workspace.member_added` was written for.
   *
   * ---------------------------------------------------------------------
   * ⚠⚠ B38 EXTENDS THE PAYLOAD: `{ workspaceId, userId, role, via }`, WRITTEN
   * AT ALL THREE EMIT SITES
   * ---------------------------------------------------------------------
   * ```
   * via: 'direct' | 'invite_link' | 'self_join'
   * ```
   *
   *   - `'direct'` — `POST /boards/:id/members` (B10's existing emit, **edited
   *     to write the key**), `FR-PERM-018`.
   *   - `'invite_link'` — `POST /board-invites/:token/accept` (`BE-38.3`,
   *     `FR-PERM-019`), **plus `inviteLinkId`**, which is the only key that
   *     ties a membership back to the link that produced it once the link has
   *     been revoked and replaced.
   *   - `'self_join'` — `POST /boards/:id/join` (`BE-38.5`, `FR-PERM-028`).
   *
   * ⚠ **`userId`, NOT `memberUserId`.** B10's shipped emit is
   * `payload: { workspaceId, userId }`, and `board.member_removed` and
   * `board.member_role_changed` use the same key. **Keep it.** Renaming it
   * would silently break any consumer of three shipped names for a cosmetic
   * gain. B38 **adds** `role` and `via`; it renames nothing and it removes
   * nothing.
   *
   * ⚠⚠ **THIS IS B36'S `board.created` SHAPE AND EMPHATICALLY NOT B37'S
   * REJECTED ONE, and the two tests that distinguish them both pass here.**
   * B37 refused a `createdFromTemplateCardId` key on `card.copied_from_card`
   * because it "would never vary from its own neighbour" — permanently equal to
   * a field the payload already carried — and because "copied this card from
   * {card}" is **false** of an instantiation, so the two facts needed two
   * sentences and therefore two names. Apply both tests to `via`:
   *
   *   1. **It genuinely varies across three writers**, which is exactly the
   *      property B36's `createdFromTemplateBoardId` had and B37's proposed key
   *      lacked. A payload key whose value is fixed by its own event name is
   *      dead weight; one whose value distinguishes three live code paths is
   *      the only record of which path ran.
   *   2. **"Alice added Bob to this board" is a true sentence on all three
   *      paths.** A self-join and a link redemption are both "somebody is now
   *      on this board", differing in *how* rather than in *what*. So one name
   *      with a discriminator is right and three names would be three spellings
   *      of one fact — the same trade `board.permission_changed`'s `setting`
   *      key makes one screen down, reached from the other direction.
   *
   * ⚠ **On the `'self_join'` and `'invite_link'` paths the ACTOR IS THE PERSON
   * JOINING**, so `actorUserId === payload.userId`, and that is not a defect to
   * be normalised away. `member.self_joined` exists one noun down (a *card*)
   * precisely because B14 chose the opposite answer for card membership; board
   * membership takes this one because `board.member_added` was already in the
   * constraint and `via` already had to exist for the link path. A renderer
   * wanting "joined this board" rather than "added themselves" reads `via`.
   *
   * ⚠⚠ **`events.ts` BINDS NO PAYLOAD SHAPE TO A NAME AT THE TYPE LEVEL**
   * (`payload: z.record(z.string(), z.unknown())`), so the key is a **docstring
   * convention enforced only at the write site** — updating B10's existing emit
   * is a manual obligation no compiler will catch, and a missing `via` reads as
   * a well-formed row for ever afterwards on an append-only table. **Grep every
   * `board.member_added` emit and confirm each writes `via`.**
   *
   * ⚠ **`surfacesInFeed` stays `false`**, so `via` never reaches an
   * `ACTIVITY_TEMPLATES` slot. It exists for SC-1 and for whatever `B28` does
   * with membership if the flag is ever reopened — and the note above still
   * holds: reopen all three membership events together or none.
   */
  'board.member_added': { surfacesInFeed: false },
  /**
   * A member was removed from a board — `FR-PERM-029`.
   *
   * `false`, on the identical footing to the entry above and not repeated at
   * length: `FR-PERM-029`'s acceptance criteria are card unassignment across
   * five cards, a removed member's authored content staying attributed, board
   * access being refused post-removal, and an owned automation stopping — no
   * criterion and no edge case names the activity feed. The same `FR-PERM-025`
   * AC4 question applies and resolves the same way: removal is not a change to
   * the add/remove-member *setting*, it is an instance of someone leaving under
   * whatever that setting currently permits.
   *
   * Recorded regardless, for the reason `board.member_added` is: SC-1, and "who
   * removed this person, and when" outlives Phase 1 even though nothing shows
   * it today.
   */
  'board.member_removed': { surfacesInFeed: false },

  // ---------------------------------------------------------------------
  // Board templates — B36, `FR-TMPL-001` (mark), `FR-TMPL-007` (unmark) and
  // `FR-TMPL-004` (describe). THREE NEW NAMES, none of them a `0004`
  // placeholder — `db/postgres/migrations/0026_board_template_activity.sql`
  // is the file that admits them: measured one at a time, dotted, against the
  // LIVE `ck_activity_entries_type` before this file was written, all three
  // came back `f`. The constraint's 77 members held `board.created`,
  // `list.created` and `card.copied_from_card` (B08, B11 and B35's own —
  // reused below, unchanged, by `POST /boards/:id/create-from-template`) and
  // nothing that reads like a template facet. `grep template` against that
  // same live constraint is ZERO hits; `grep board\.` is TEN, all genuinely
  // this product's own (`0026`'s own header carries the full per-name table
  // and corrects an earlier, unmeasured "twelve" in this block's own design
  // brief) — a prefix grep is simply the wrong question here, as it has been
  // for every block since `0015`.
  //
  // NAMING: `<facet>_<participle>`, NOT a bare participle. `board.` splits
  // two ways in this registry — bare participles for whole-board state
  // (`board.closed`, `board.reopened`, `board.deleted`) and
  // `<facet>_<participle>` for a facet of it (`board.member_added`,
  // `board.visibility_changed`, `board.permission_changed`). The template
  // flag and its description are facets of the board row
  // (`boards.is_template`, `boards.template_description`), not whole-board
  // lifecycle, so the second form wins, and the flag's pair is spelt
  // SYMMETRICALLY — `template_marked` / `template_unmarked` — rather than a
  // `marked`/`removed` pair whose two verbs would read as two different
  // operations on two different things, when they are one lifecycle toggled
  // in both directions (`list.archived` / `list.unarchived`'s precedent).
  //
  // ⚠ ALL THREE SURFACE (`surfacesInFeed: true`), even though a TEMPLATE
  // board hides its own comment and activity surfaces (`FR-TMPL-001`'s own
  // behaviour text). That is not a reason to withhold the entry: it is a
  // statement about which SURFACE renders the feed (`FE-36.1`'s business),
  // not about whether the row exists, and these rows are exactly what the
  // feed shows the moment `FR-TMPL-007` turns the board back into a working
  // one — `board.visibility_changed`'s footing, a board-level change other
  // members can observe and dispute. An entry withheld while the surface
  // happens to be hidden is unrecoverable: SC-1 admits no unrecorded
  // mutation, and there is no second mechanism that could backfill "was this
  // board ever a template, and when" once the fact has gone unwritten.
  //
  // ⚠⚠ ALL THREE CARRY `{ workspaceId, title }` AND NOTHING RICHER — the
  // smallest shape that makes a feed sentence renderable, on
  // `board.renamed`'s own precedent. There is no `previousIsTemplate` on the
  // first two: `POST`/`DELETE /boards/:id/template` are IDEMPOTENT with NO
  // event on a no-op (`apps/server/src/routes/board-templates.ts`'s
  // conditional `UPDATE … WHERE is_template = <opposite>`), so the value
  // before a MARK row is written is always `false` and before an UNMARK row
  // is always `true` — a stored value that never varies is dead weight on
  // every row this pair can ever produce.
  // ---------------------------------------------------------------------

  /**
   * A board admin marked this board as a template — `FR-TMPL-001`,
   * `POST /boards/:id/template`.
   *
   * `{ workspaceId, title }`, the block comment's smallest shape: no
   * description text (that is `board.template_description_saved`'s entry,
   * below, and a single write to `boards.is_template` never touches
   * `boards.template_description` in the same statement) and no
   * `previousIsTemplate` (see the block comment — the value before this row
   * is written is always `false`, by construction of the idempotent route).
   *
   * `true`, on the block comment's footing. Idempotent at the write layer
   * too: a `POST` on a board that is already a template matches zero rows in
   * the conditional `UPDATE`, writes nothing and emits nothing, so this row
   * exists exactly once per board that is ever templated — never once per
   * retried click of a "Make template" action.
   */
  'board.template_marked': { surfacesInFeed: true },
  /**
   * A board admin removed the template flag — `FR-TMPL-007`,
   * `DELETE /boards/:id/template`.
   *
   * `{ workspaceId, title }`, `board.template_marked`'s precedent exactly,
   * and idempotent the identical way: a `DELETE` on a board that is not a
   * template matches zero rows, writes nothing and emits nothing.
   *
   * `true`, on the block comment's footing above. `FR-TMPL-007` AC1 —
   * "given I am the admin of a template board, when I remove the template
   * flag, then the comment composer and activity feed are available
   * again" — makes this event's OWN entry the first thing the reopened feed
   * shows, which is the strongest possible case for surfacing it: the
   * requirement's own acceptance criterion is about the feed becoming
   * visible again, and an invisible first entry in it would undercut the
   * very criterion this route exists to satisfy.
   */
  'board.template_unmarked': { surfacesInFeed: true },
  /**
   * A template's "about this template" description was written — `FR-TMPL-004`,
   * `PATCH /boards/:id/template-description`.
   *
   * `{ workspaceId, title }`. ⚠ **The description text is NOT in the
   * payload**, on `card.description_edited`'s own precedent one container
   * up: an activity entry records THAT the description changed, not what it
   * now says. `activity_entries.payload` is JSONB on an append-only table
   * nothing prunes, and a *public* board (`FR-PERM-010`) makes that history
   * readable by anyone with the link — a disclosure of text an author may
   * since have shortened or withdrawn. Nor is there a `previousDescription`:
   * `board.visibility_changed` carries a previous value only because the
   * visibility chain has no anchor entry to read a "before" from, and this
   * facet has one — `boards.template_description` itself, readable back
   * through `GET /boards/:id/template` — so there is nothing here worth a
   * second, append-only copy.
   *
   * **One name for BOTH directions a text field can move** — writing a
   * description and clearing one to `null` — `card.description_edited`'s
   * "one name, one route" shape again: `PATCH /boards/:id/template-
   * description` is one route with one payload shape
   * (`{ description: string | null }`), and a route this small does not owe
   * two names for a fact one nullable column already tells apart at read
   * time. Unlike `card.description_edited`, there is no split key for this
   * name at the `packages/shared/src/activity.ts` layer either — this
   * block's design fixes exactly one `ACTIVITY_TEMPLATES` sentence here, on
   * the judgement that a template's description is a materially less
   * prominent facet than a card's own body, and the smallest shape that
   * renders one feed sentence is the one this file ships.
   *
   * ⚠⚠ **THE VERB IS `saved`, NOT `edited` — AND THIS IS A CORRECTION TO
   * THE BLOCK'S OWN DESIGN BRIEF, NOT A STYLE CHOICE.** The brief's working
   * name, `board.template_description_edited`, is 33 characters — ONE OVER
   * `EVENT_TYPE_MAX_LENGTH` / `activity_entries.type varchar(32)`.
   * `board.template_description_saved` is 32, tying `checklist.item_
   * converted_to_card` at the ceiling this constraint has held since `0004`
   * and moving it no further. `saved` is not a weaker word chosen to make
   * room: `FR-TMPL-004` AC1 is verbatim "I can write and SAVE a description
   * that is distinct from the board's own description" — the requirement
   * supplies BOTH verbs, and this name is built from the one that is five
   * characters shorter. The feed sentence in `packages/shared/src/
   * activity.ts` uses the same word ("saved the template description on
   * this board"), so the stored `type` and the rendered sentence agree.
   *
   * `true`, on the block comment's footing above.
   */
  'board.template_description_saved': { surfacesInFeed: true },
  /**
   * A board template's public-gallery category changed — `FR-TMPL-005`,
   * `PATCH /boards/:id/template-category`.
   *
   * New in `db/postgres/migrations/0042_template_category_activity.sql`. 31
   * characters, within the event-type column ceiling. Payload:
   * `{ workspaceId, title, previousCategory, category }`, where both category
   * fields may be null.
   */
  'board.template_category_changed': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Board invite links — B38, `FR-PERM-019` (generate a link that adds
  // whoever opens it) and `FR-PERM-020` (revoke one). TWO NEW NAMES, neither
  // of them a `0004` placeholder —
  // `db/postgres/migrations/0028_board_permission_defaults_and_invite_link_
  // activity.sql` is the file that admits them. Measured one at a time,
  // dotted, against the LIVE `ck_activity_entries_type` before this file was
  // written: both came back `f`. 25 characters each, seven under
  // `EVENT_TYPE_MAX_LENGTH`.
  //
  // ⚠ **THE COUNT: the constraint goes 83 → 85, not the 82 → 84 this block's
  // own design brief states.** The brief's figure was carried forward from an
  // earlier block's prose rather than measured; three independent measurements
  // of the live constraint agreed on 83 before `0028` (a raw count, a
  // `DISTINCT` count and a line count of the sorted dump), `0027`'s own header
  // states eighty-three as its post-apply total, and a sorted before/after
  // diff shows exactly two additions, nothing removed and nothing renamed.
  // **85 is the number to recount against**, and the standing instruction
  // applies: recount, do not increment.
  //
  // ⚠⚠ THREE WORKSPACE-SCOPED NAMES WERE CHECKED AND **DECLINED**, AND THE
  // ASYMMETRY IS STRUCTURAL RATHER THAN A MATTER OF TASTE. `BE-38.4` builds
  // workspace join requests (`FR-PERM-022`, see
  // `packages/shared/src/workspace-join-request.ts`) and could plausibly have
  // wanted `workspace.join_requested`, `workspace.join_request_approved` and
  // `workspace.join_request_declined`. All three are absent from the live
  // constraint in every spelling, all three fit the column, and all three are
  // refused:
  //
  //   1. ⚠⚠ **A workspace-scoped name needs a SECOND constraint widened, and
  //      it is easy to miss.** `ck_activity_entries_scope`, read off the live
  //      constraint rather than reconstructed, is `card_id IS NOT NULL OR
  //      board_id IS NOT NULL OR (type IN (<six names>) AND actor_user_id IS
  //      NOT NULL)`, and the six are **measured**: `user.display_name_changed`,
  //      `user.bio_changed`, `user.avatar_changed`, `workspace.created`,
  //      `workspace.member_added`, `workspace.member_role_changed` — every
  //      registered name in this file that carries `boardId: null`. A
  //      join-request entry has no board and no card, so it lands on the third
  //      disjunct and its type would have to be added to **that** enumeration
  //      too. The two invite-link names carry `board_id` and satisfy the second
  //      disjunct standing alone — one constraint, not two.
  //   2. **`publishEvents` fans out on `board_id` alone**, so a
  //      workspace-scoped event reaches no socket, ever. The three declined
  //      names would be write-only rows.
  //   3. **No requirement asks for them.** `FR-PERM-022` has four acceptance
  //      criteria and not one mentions a feed or an activity entry — contrast
  //      `FR-PERM-025` AC4, which is what put `board.permission_changed` in
  //      this registry. B32's precedent governs: mint nothing where no
  //      requirement asks.
  //   4. **The durable fact is already recorded.** Approving a request creates
  //      a workspace member and emits **`workspace.member_added`**, which is in
  //      *both* constraints' lists already. A *declined* request creates no
  //      membership, and `workspace_join_requests.status` is itself the record.
  //
  // NAMING: `<facet>_<participle>`, B36's rule one block over — `board.` splits
  // into bare participles for whole-board state (`board.closed`,
  // `board.reopened`, `board.deleted`) and `<facet>_<participle>` for a facet
  // of it (`board.member_added`, `board.visibility_changed`,
  // `board.permission_changed`, `board.template_marked`). An invite link is a
  // facet of the board — a row in `board_invite_links` keyed by `board_id` —
  // not whole-board lifecycle, so the second form wins.
  //
  // ⚠ **`_revoked`, NOT `_deleted`.** `0021`'s rule reserves `deleted` for a
  // row that is gone (the soft deletes `board.deleted` and `list.deleted`, and
  // the one hard subject delete `card.deleted`), and revocation here **keeps
  // the row**: `FR-PERM-020` AC4 requires a revoked URL to stay dead, so
  // deadness is stated in `is_active` rather than inferred from a token's
  // absence, and the surviving row is what `inviteLinkId` refers to. A
  // `_deleted` spelling would promise a `DELETE` this block deliberately does
  // not do.
  //
  // ⚠ **BOTH `surfacesInFeed: true`, on `board.permission_changed`'s own
  // footing** — `FR-PERM-025` AC4's "any setting" reading, plus SC-1's audit
  // argument in its sharpest form: a link circulating outside the team's
  // control is exactly the fact an admin must be able to reconstruct
  // afterwards, and there is no second mechanism that could backfill it.
  //
  // ⚠ **NO `previous` / `next` ON EITHER.** Unlike `board.permission_changed`
  // these are not a settings change: creation has no previous, and
  // revocation's next is "dead", which the name already says. B36's
  // `previousIsTemplate` argument reaches the same answer — a stored value that
  // never varies is dead weight on every row the pair can produce.
  //
  // ⚠ **`apps/server/src/search/refresh-index.ts` is NOT touched.** That set
  // holds names that dirty a **card's** text; neither name here names a card.
  // B37's §2.3 trap does not apply — recorded so nobody goes looking for a
  // missing line.
  // ---------------------------------------------------------------------

  /**
   * A board's shareable invite link was generated — `FR-PERM-019`,
   * `POST /boards/:id/invite-link`, `BE-38.3`.
   *
   * Payload: `{ workspaceId, inviteLinkId, roleGranted }`.
   *
   * ⚠⚠ **THE TOKEN IS NEVER IN THE PAYLOAD, AND THIS IS THE HIGHEST-CONSEQUENCE
   * LINE IN THIS ENTRY.** `activity_entries` is append-only with no backfill and
   * `FR-COLLAB-020`'s board feed is readable by **every board member**, so a
   * token written here is a **credential that outlives its own revocation** —
   * the row survives (`_revoked`, above), the feed keeps serving it for ever,
   * and every member who ever reads the feed holds a working membership grant
   * that no `DELETE /boards/:id/invite-link` can take back. On a **public**
   * board (`FR-PERM-010`) the audience is the internet. Carry
   * `inviteLinkId` — the row's primary key, which resolves to nothing anybody
   * can redeem — and nothing else. `packages/shared/src/board-invite-link.ts`
   * states the same rule from the contract side; this is the side where it is
   * broken, because `payload` is `z.record(z.string(), z.unknown())` and no
   * type will catch a `token` key added at a write site.
   *
   * `roleGranted` rides along because it is what a redeemer *becomes* and it is
   * the one thing about the link that a board admin reading the feed months
   * later cannot otherwise reconstruct from a row that may since have been
   * revoked and replaced. It is a role name, not a credential.
   *
   * `workspaceId` is the shape every board-scoped event has carried since B08:
   * `activity_entries` has no workspace column, so a consumer holding only the
   * entry could not otherwise say where it happened.
   *
   * ⚠ **`board_id` IS ON THE COLUMN, not in the payload**, which is what makes
   * this event reach a socket at all — `publishEvents` fans out on that column
   * alone. `cardId` is `null`: an invite link belongs to no card and naming one
   * would be a lie. It passes `ck_activity_entries_scope` on the second
   * disjunct, `board_id IS NOT NULL` standing alone, which is why this name
   * needed one constraint widened and the three declined workspace names would
   * have needed two.
   *
   * ⚠ **A `POST` while an active link already exists emits BOTH this name and
   * `board.invite_link_revoked`, in that transaction, revoked first.**
   * `FR-PERM-019` fixes one active link per board at a time, and `FR-PERM-020`
   * AC4 makes the old URL dead from that moment; a feed recording only the
   * creation would leave a board's history saying two links exist. Two rows for
   * one gesture is right **here** and would be wrong for a visibility cascade —
   * the distinction `board.permission_changed`'s docstring draws is whether the
   * second row describes a fact somebody actually performed, and revoking the
   * previous link is one.
   *
   * `true` — see the block comment.
   */
  'board.invite_link_created': { surfacesInFeed: true },
  /**
   * A board's invite link was revoked — `FR-PERM-020`,
   * `DELETE /boards/:id/invite-link` and the implicit revocation a fresh
   * `POST` performs. `BE-38.3`.
   *
   * Payload: `{ workspaceId, inviteLinkId }`. **Two keys, and no token** — the
   * warning at `board.invite_link_created` applies here **unchanged and with
   * one extra edge**: this is the row that records a link being *killed*, so a
   * token written into it would be a credential preserved by the very entry
   * that says it stopped working. There is no `roleGranted` either: the row
   * survives revocation and `inviteLinkId` resolves to it, so the role is
   * readable rather than worth a second append-only copy — the same call
   * `board.template_description_saved` makes about a description it declines to
   * duplicate.
   *
   * ⚠ **`revoked`, not `deleted`, and the row is not deleted.** See the block
   * comment: `is_active = false` is a conditional `UPDATE … WHERE board_id = :id
   * AND is_active = true` whose `.returning()` is used **only** as the *did it
   * match* test, so two concurrent revocations cannot both emit this event. A
   * `DELETE` with no active link is an **idempotent 200 that writes nothing and
   * emits nothing** — `POST /boards/:id/close`'s shape, and B36's mark/unmark
   * pair's, so this row exists once per link that is ever revoked rather than
   * once per retried click.
   *
   * ⚠ **Revoking removes nobody.** `FR-PERM-020`'s first edge case — "deleting
   * a link must not remove anyone who already joined through it" — is vacuous
   * by construction: revocation touches `board_invite_links` and never
   * `board_members`, so no `board.member_removed` accompanies this and none
   * should ever be added. A handler that "tidied up" the memberships a dead
   * link produced would pass every symmetry intuition and contradict the one
   * sentence in the corpus that addresses the case.
   *
   * ⚠ **The OTHER way a link stops working emits NOTHING, and that is
   * deliberate.** `FR-PERM-020`'s implicit revocation — a link is honoured only
   * while its creator still holds invite rights *at the moment of redemption*,
   * and starts working again if they are reinstated — is a **live predicate**
   * evaluated at redemption, not a state change. There is no moment to attach
   * an event to (removing the creator from the board emits
   * `board.member_removed`, which is the fact that actually happened), and an
   * event claiming the link was revoked would be attributed to an actor who
   * never touched it — the lie `board.permission_changed`'s docstring refuses
   * for visibility cascades. Do not add one.
   *
   * `board_id` on the column, `cardId: null`, `true` — as above.
   */
  'board.invite_link_revoked': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Lists — B11, `FR-BOARD-009` (add), `FR-BOARD-010` (rename),
  // `FR-BOARD-011` (reorder), `FR-BOARD-015` (archive and restore) and
  // `FR-BOARD-016` (permanently delete an archived list).
  //
  // THE FIRST PRODUCT-CONTENT EVENTS IN THIS REGISTRY. Everything above is
  // identity, workspace or board — who exists, who may see what. A list is
  // the first thing a member puts work *into*, and these six are the first
  // entries a board's activity feed will show that are about the board's
  // contents rather than about the board.
  //
  // FOUR OF THE SIX WERE ALREADY IN `ck_activity_entries_type`, AND THIS IS
  // THE MOMENT THIS FILE'S HEADER DESCRIBES. The B07 block above says of
  // `0004`'s placeholders: "The placeholders are still placeholders —
  // nothing emits them — and a block that first emits one registers it here
  // then, not in advance." `list.created`, `list.renamed`, `list.archived`
  // and `list.unarchived` are four of those placeholders (snake_case in
  // `0004`, dotted by `0013`'s mechanical rename), unemitted by anything
  // until now. B09 was the first block to hit this case, once, with
  // `board.visibility_changed`; B11 hits it four times over and needs no DDL
  // for any of the four. Only `list.position_changed` and `list.deleted` are
  // new in any spelling, and `db/postgres/migrations/0016_list_activity.sql`
  // adds exactly those two — fifty-nine names to sixty-one.
  //
  // ⚠ ONE `list.*` NAME IN THE CONSTRAINT IS NOT REGISTERED HERE, AND ITS
  // ABSENCE IS DELIBERATE. `list.moved_between_boards` is `FR-BOARD-012`
  // (move a list to another board, V1), which B11 does not build. It stays
  // an unemitted placeholder and the block that builds the cross-board move
  // registers it then — the same rule that kept these four waiting. So the
  // counts on the two sides do not match and are not meant to: the live
  // constraint answers a grep for `list.` with SEVEN names, this registry
  // declares SIX, and the odd one out is that placeholder rather than a name
  // dropped by accident. (Before `0016` the same grep answered five, which is
  // the "the vocabulary is already complete" reading `0016`'s header exists to
  // defuse from the SQL side.)
  //
  // ⚠ **B21 IS THAT BLOCK, AND THE PARAGRAPH ABOVE IS CORRECTED IN PLACE
  // RATHER THAN DELETED, BECAUSE IT DID PRECISELY WHAT IT WAS WRITTEN TO DO.**
  // `BE-21.4` (`FR-TMPL-022`, move a list to another board with its cards, with
  // `FR-BOARD-012` specifying the same capability) emits
  // `list.moved_between_boards` — see that entry at the foot of this block —
  // so the name is registered here now, **by the block that first emits it**,
  // and the two sides have MET: the live constraint answers a grep for `list.`
  // with SEVEN names and this registry declares SEVEN. The sentence "it stays
  // an unemitted placeholder" is the only clause above that has expired; the
  // rule it rests on is the one that just fired.
  //
  // ⚠ AND NOTHING WAS NEEDED FROM THE DATABASE FOR IT. The name has been a
  // `0004` placeholder throughout (`list_moved_between_boards` there, dotted by
  // `0013`'s mechanical rename), so B21 registers it with a one-line edit to
  // this file and `db/postgres/migrations/` stays at nineteen files with the
  // `activity_entries` CHECK-change count at eight. That is B12's reason — "the
  // names already exist" — arriving for the third time, and B21 is the first
  // block since B16 to answer "does this block emit an event name of its own?"
  // with a *yes* at all.
  //
  // ⚠ AND THERE IS NO `list.restored`. `FR-BOARD-015`'s "Send to board"
  // emits `list.unarchived`, the exact inverse of `list.archived`, so the
  // pair reads as one lifecycle. A third spelling would leave two names for
  // one fact with `list.unarchived` still unemitted and looking overlooked.
  //
  // SCOPE. All six carry a real `boardId`, so everything the B08 block above
  // says about the fan-out applies unchanged: `publishEvents` filters on
  // `boardId !== null` and puts these on `boardChannel(boardId)` after the
  // transaction commits — which is what `FR-BOARD-011`'s "Dropping commits
  // the new order **for every member**" and `FR-BOARD-010`'s "Renames are
  // visible to all board members" will consume once `FR-COLLAB-025`/B18
  // lands the client half. Note *why* it is the board and not the list:
  // **`activity_entries` has no `list_id` column** — its scoping columns are
  // `card_id`, `board_id` and `actor_user_id` — so the list id travels in
  // `payload`, the same trade `0012` recorded for the workspace id. Each of
  // the six therefore satisfies `ck_activity_entries_scope`'s first
  // disjunct on its own and `0016` added no by-name exemption.
  //
  // FEED. Five `true` and one `false`, and the authority is not uniform
  // across the five — read each entry rather than assuming symmetry.
  // `list.renamed` is `true` because `FR-BOARD-010`'s own sentence says so;
  // the other four rest on `FR-COLLAB-020`, which describes the board feed
  // as "entries from all cards and lists on the board". **B11 is the first
  // block able to honour that clause**, because it is the first block with
  // lists.
  //
  // One consistency note, because a later reader will otherwise think this
  // file argues both sides of one sentence. `board.member_added`'s docstring
  // says this registry has "consistently read that sentence loosely — as
  // 'the board's activity feed shows board-level events too', not as a
  // literal card/list scope" — and here that same sentence is cited as
  // positive authority. Nothing flips: a **list** is named explicitly in
  // FR-COLLAB-020's own words, so it qualifies under the strict reading and
  // under the loose one alike. The loose reading was needed to admit
  // `board.created` and `board.renamed`, which the strict one would exclude;
  // it is not needed here, and citing the strict reading for lists does not
  // withdraw it from them.
  // ---------------------------------------------------------------------

  /**
   * A list was added to a board — `FR-BOARD-009`.
   *
   * `true`. `FR-BOARD-009` itself never says "activity", so this rests on
   * `FR-COLLAB-020` — the board feed shows "entries from all cards and lists
   * on the board" — and on the same argument `board.created` is registered
   * under: it is the first line of the list's own history and the entry that
   * explains where everything after it happened. A feed that shows cards
   * being added to a list it never showed being created tells half a story,
   * which is the objection B09 raised against surfacing one membership event
   * without its siblings, applied in the direction that admits rather than
   * excludes.
   *
   * A placeholder since `0004`, so no migration was needed for it.
   */
  'list.created': { surfacesInFeed: true },
  /**
   * A list was renamed — `FR-BOARD-010`.
   *
   * `true` **by the requirement rather than by judgement**, and it is the
   * only one of these six on that footing, so cite this entry rather than
   * the block comment when the question comes up. `FR-BOARD-010`'s behaviour
   * text: "Renames are visible to all board members and **recorded in board
   * activity**." That is `board.renamed`'s exact footing, in almost the same
   * words, and it does not depend on how `FR-COLLAB-020` is read.
   *
   * Its counterpart is the same as `board.renamed`'s and is a client-side
   * promise the server keeps by never being asked: AC2 says an in-progress
   * rename abandoned with Escape restores the original name, which means no
   * request is sent and therefore no entry is written.
   *
   * A placeholder since `0004`, so no migration was needed for it.
   *
   * ⚠ **Since B28, the payload carries `previousTitle` beside `title`** —
   * `apps/server/src/routes/lists.ts`'s `PATCH /lists/:id/title` handler,
   * additive JSONB, no migration. `FR-COLLAB-018` names *"renamed this card
   * (from &lt;old name&gt;)"* as the canonical sentence, and B28's grammar
   * renders that from `previousTitle` (the list-level counterpart). **Entries
   * written before this change do not carry the key** — the whole historical
   * corpus — so the grammar has a fallback branch for its absence rather than
   * assuming every `list.renamed` row looks alike; see
   * `packages/shared/src/activity.ts`'s header for how that fallback is
   * expressed as a neutral placeholder rather than a second template.
   */
  'list.renamed': { surfacesInFeed: true },
  /**
   * A list colour changed — `FR-BOARD-019`, B58.
   *
   * `true`, on `label.recoloured`'s footing: colour is shared board-visible
   * state. The board/list/card decoration block mints the name in `0037`.
   */
  'list.recoloured': { surfacesInFeed: true },
  /**
   * A list was archived — `FR-BOARD-015`.
   *
   * `true`, on `FR-COLLAB-020` (see the block comment) and because an
   * archived list is **recoverable**: `FR-EXP-010` makes it an entry in the
   * board's archived-items surface with a "Send to board" action, so unlike
   * a deletion this is a state a member arrives at wanting to know who put
   * it there. That is `board.closed`'s reasoning — a closed board is still
   * readable, so its feed is reachable and "who closed this, and when" is
   * the first question — applied to the list-level equivalent.
   *
   * ⚠ **This event says nothing about the list's cards, because archiving a
   * list must not write `cards.archived_at`.** `FR-BOARD-015`'s edge case
   * requires restore to tell cards archived *with* the list from cards
   * archived *individually beforehand*, and the only thing that
   * distinguishes them is that the second group has its own timestamp and
   * the first does not. So there is no accompanying `card.archived` storm
   * here, and a future block must not add one "for completeness" — it would
   * make the edge case unimplementable and no test would catch it until
   * somebody restored a list.
   *
   * A placeholder since `0004`, so no migration was needed for it.
   */
  'list.archived': { surfacesInFeed: true },
  /**
   * An archived list was restored to the board — `FR-BOARD-015`, "Send to
   * board".
   *
   * `true`, symmetrically with `list.archived`: the pair is one lifecycle
   * and a feed showing only half of it would read as though the list had
   * never come back. `board.reopened` is registered on the same footing.
   *
   * ⚠ **This is the restore event. There is no `list.restored`** — see the
   * block comment. The name is `0004`'s placeholder, kept because it is the
   * exact inverse of `list.archived`, and it needed no migration.
   *
   * The payload records the list id and the board, and nothing about cards.
   * D1 of the block contract answers `OQ-1` — a restored list returns to its
   * **prior** position — and the mechanism is that `lists.position` is a
   * fractional rank archiving never clears and no other list can collide
   * with, so restore computes no rank at all. There is consequently nothing
   * positional for this entry to record: the rank in the row before the
   * restore and after it is the same string.
   */
  'list.unarchived': { surfacesInFeed: true },
  /**
   * A list moved within its board's strip — `FR-BOARD-011`.
   *
   * **New in `0016_list_activity.sql`**, in this spelling and every other:
   * `0004` enumerated `card_position_changed` and no list equivalent. The
   * name mirrors `card.position_changed` deliberately, so that the two
   * ordering events on the two ordered tables read alike; it is not a reuse
   * of it, because a card's rank within a list and a list's rank within a
   * board are different facts about different tables. 21 characters, inside
   * `EVENT_TYPE_MAX_LENGTH`.
   *
   * `true`, on `FR-COLLAB-020` (see the block comment). The judgement worth
   * stating is the one *against* suppressing it as noise: `FR-BOARD-011` AC1
   * — "given four lists, when the user drags the fourth to the first
   * position, then the order becomes 4,1,2,3 **for all members**" — makes a
   * reorder a change to shared state that every member sees, not a personal
   * view setting, and SC-1 admits no unrecorded mutation regardless of how
   * ordinary the mutation is. A board whose columns were rearranged
   * overnight with nothing in the feed to say so is the complaint this flag
   * exists to prevent.
   *
   * The payload carries the previous and the new rank, for the reason
   * `board.visibility_changed`'s carries both levels: `activity_entries` is
   * append-only with no backfill, and B28 cannot render "moved left" against
   * "moved right" from a single value without a second query against a row
   * that has since moved again.
   */
  'list.position_changed': { surfacesInFeed: true },
  /**
   * An archived list and every card that belonged to it were permanently
   * deleted — `FR-BOARD-016`.
   *
   * **New in `0016_list_activity.sql`.** 12 characters. The name mirrors
   * `board.deleted`, which `0013` added for the same operation one level up.
   *
   * `false`, **on `board.deleted`'s exact footing** and for the same
   * mechanical reason rather than out of squeamishness: after this is
   * emitted the list is gone from the archive and from search
   * (`FR-BOARD-016` AC2), every read path carries `deleted_at IS NULL`, and
   * no route writes it back — so the surface this entry would appear on
   * cannot be opened for it. An entry in a feed nobody can reach is an audit
   * record, not a feed entry, which is why it is written at all (SC-1) and
   * why it is `false` here.
   *
   * ⚠ Note the difference from `board.deleted`, because it is the reason
   * this one is *not* simply invisible: the **board's** feed is still very
   * much reachable, and a member who opens it will see the list's earlier
   * entries with no entry explaining where the list went. That is a real
   * cost, and it is accepted rather than overlooked — `FR-BOARD-016`'s own
   * edge case ("deletion cannot be reversed by undo; the undo stack must not
   * offer to restore it") is the requirement's position on how visible this
   * operation should be afterwards, and a feed entry offering a name and a
   * timestamp for something irrecoverable invites exactly the restore
   * attempt it forbids. A block that revisits this should revisit
   * `board.deleted` in the same change.
   *
   * It is still **broadcast**, and that is the point — `surfacesInFeed` is
   * the §2.6 feed discriminator and nothing else reads it, so a `false` here
   * still reaches the board room. That is what lets a member currently
   * looking at the board see the list disappear rather than sit on a stale
   * strip, which is the same mechanism `FR-BOARD-008`'s edge case needs for
   * a deleted board.
   */
  'list.deleted': { surfacesInFeed: false },
  /**
   * A list moved to **another board**, taking its cards with it — B21,
   * `FR-TMPL-022` (`BE-21.4`), the same capability `FR-BOARD-012` specifies.
   *
   * A placeholder since `0004` (`list_moved_between_boards`), dotted by
   * `0013`, and unemitted by anything until now — so **no migration**. 25
   * characters, inside `EVENT_TYPE_MAX_LENGTH`.
   *
   * `true`, on `FR-COLLAB-020` and on the block comment's footing, with one
   * argument of its own: this is the **largest single change to a board's
   * contents any route in the product can make**. `list.position_changed` is
   * `true` for a list moving one slot within its own strip, and a list leaving
   * the board entirely with eight cards in it is that state change several
   * orders of magnitude larger. A feed that records the reorder and not the
   * departure is the "a card's history that simply stops" complaint one level
   * up.
   *
   * ---------------------------------------------------------------------
   * ⚠⚠ EMITTED **TWICE PER GESTURE, ONCE PER BOARD** — AND THE TWO ROWS DO NOT
   * SHARE A PAYLOAD
   * ---------------------------------------------------------------------
   * One row carries `board_id = <source>` and one `board_id = <destination>`.
   * That is not bookkeeping and it binds **today** rather than in a later
   * phase: `publishEvents` filters `boardId !== null` and publishes each event
   * to **exactly one** `boardChannel(boardId)`, B18 is shipped and built no
   * replay and no `?since=`, so a single row carrying the destination would
   * leave every socket with the **source** board open rendering a list that
   * had left it until a reload. `00-`'s principle 6 ("changes appear to other
   * members without a reload") is in force from Phase 1 via `FR-COLLAB-025`.
   *
   * They are also **two facts, not one fact twice**: "this list left board A"
   * and "this list arrived on board B" are different sentences for different
   * audiences, and neither board can render the other's.
   *
   * ⚠⚠ **THE PAYLOAD RULE, which is what makes that true rather than merely
   * asserted:** *no payload key names a row that the audience of that row's own
   * `board_id` could not already see.* `ws/board-socket.ts` forwards the
   * payload into the room **byte for byte**, with no filter anywhere, so a key
   * is delivered to that board's members as written. Stated as **audience**
   * rather than as "the same board", because the *list itself* legitimately
   * appears on both rows — board A's members watched it sit there until a
   * moment ago and board B's are about to see it arrive. What is not nameable
   * is the **other board and anything on it**:
   *
   *     departure row   payload: { workspaceId: <source>, listId,
   *                                previousPosition, cardsMoved }
   *     arrival row     payload: { workspaceId: <destination>, listId,
   *                                position, cardsMoved }
   *
   * ⚠ **There is no `payload.boardId` and no `payload.previousBoardId` on
   * either row.** The row's own `board_id` column says which board it is for,
   * and naming the other one would break the rule symmetrically in both
   * directions. `payload.workspaceId` is per-row and is **not** a violation: it
   * is the workspace of *this row's own board*, which every member of that
   * board can already see, and it genuinely differs between the two rows
   * because a **cross-workspace move is permitted** (nothing in `FR-TMPL-022`
   * or `FR-BOARD-012` scopes the destination to the source's workspace).
   * `cardsMoved` is a count rather than an id and discloses nothing
   * addressable.
   *
   * ⚠ **THE DISCRIMINATOR IS `'position' in payload` → arrival; otherwise
   * departure**, uniform with `card.moved_between_boards`. There is
   * deliberately **no `direction: 'in' | 'out'` key**, and this is the part of
   * the shape most likely to be "improved": it would be a second encoding of a
   * fact the row already carries, and the two could disagree — the exact
   * objection `DELETE /boards/:id/members/:userId` records against a
   * `self: true` flag ("`actor_user_id` and `payload.userId` being equal *is*
   * the distinction"). ⚠ **A future `generateNotifications` mapping for this
   * name MUST switch on it or it will notify twice**, once per row, for one
   * gesture. B21 adds no mapping at all — see `card.moved_between_boards`.
   *
   * ⚠ **ONE REGISTRY ENTRY, TWO SENTENCES.** `surfacesInFeed: true` makes both
   * rows surface, which is the intent since each surfaces on its own board's
   * feed — but B28 renders **two different sentences** from this one entry,
   * chosen by the discriminator above. Board A's feed can say only "⟨actor⟩
   * moved this list off the board" and board B's only "⟨actor⟩ moved this list
   * onto the board"; neither may name the other board.
   *
   * ---------------------------------------------------------------------
   * ⚠ NOTHING PER CARD
   * ---------------------------------------------------------------------
   * One row per board and **no `card.moved_between_boards` storm** for the N
   * cards that travelled. Two precedents in this registry are exactly this
   * shape — one gesture, one row, N children affected: `label.deleted` (one
   * row, no per-card `label.removed` storm) and `board.member_removed` (one
   * row for a sweep across N cards). `FR-TMPL-022` AC2's "reported **once**,
   * not once per card" is about the actor's report rather than about the spine,
   * but it points the same way, and B18's client reconciles by refetching
   * `GET /boards/:id/contents` — so **one** frame per board already updates the
   * whole board face including the arrived cards, and per-card frames would be
   * pure surplus.
   *
   * The cost, recorded honestly rather than discovered: a card that arrived via
   * a list move has **no per-card arrival entry**, so under B28's card-back
   * filter its stream is empty until something else happens to it. **B28
   * renders that from this row, via `payload.listId` joined to
   * `cards.list_id`.**
   *
   * ⚠⚠ **B28 considered that instruction and declined it — corrected in
   * place, not deleted, because the instruction was a recorded intention from
   * the block that created the situation and deserves an argued answer rather
   * than silent non-compliance.** The join above is **present-tense**: it
   * asks which list a card is in *now* and offers that as the answer to a
   * *past-tense* question, "did this card arrive as part of this move". A
   * card moved to a different list after the board move would be credited
   * with an arrival it was never part of; a card that was in the list at
   * move time but has since left it would still be missing one. Both
   * failures are silent — every row the synthesis produces is a plausible
   * sentence, not an error — and `FR-COLLAB-017` asks for a **history**, not
   * a derivation from current state, so a synthesis that can misattribute an
   * arrival is wrong on the requirement's own terms, not merely imprecise.
   * See `.b28-design.md` §4, "Two consequences…", for the fixture this
   * produces (an arrived-by-list-move card's feed is legitimately **empty**
   * until something else happens to it — build that as the assertion, not as
   * a bug to fix). The entry this comment originally asked for is a
   * **write-path** obligation, not a read-time one: a later block that wants
   * it should have B21 **emit** one `card.moved_between_boards`-shaped row
   * per travelling card, which no amount of joining at read time can
   * reconstruct correctly.
   *
   * SCOPE. Both rows carry `card_id: null` and a real `board_id`, so both
   * satisfy `ck_activity_entries_scope` on its **second disjunct alone** —
   * B11's six list events' shape exactly. The list id is in `payload` because
   * `activity_entries` has no `list_id` column, the trade `0012` recorded for
   * the workspace id and `0016` for the list.
   */
  'list.moved_between_boards': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Cards — B12, `FR-BOARD-020` (add a card to a list), `FR-BOARD-022`
  // (rename one without opening it), `FR-BOARD-024` (persist and read card
  // order) and `FR-BOARD-025` (drag a card within a list and between lists
  // on the same board).
  //
  // (a) ALL FOUR WERE ALREADY IN `ck_activity_entries_type`, SO **B12 SHIPS
  // NO MIGRATION** AND THE POSTGRES-ONLY `activity_entries` CHECK-CHANGE
  // COUNT STAYS AT SIX (`0011`–`0016`). This is the inverse of B11, which
  // needed `0016` for two of its six, and it is the fullest instance yet of
  // the case `0013`'s header anticipated: `card.created`, `card.renamed`,
  // `card.position_changed` and `card.moved_between_lists` are four of
  // `0004_collaboration_and_activity.sql`'s placeholders (snake_case there,
  // dotted by `0013`'s mechanical first-underscore-to-dot rename), unemitted
  // by anything until now. B09 hit this case once, B11 four times out of
  // six, B12 four times out of four.
  //
  // Verify rather than believe — the check is one command and this comment
  // is the kind of claim `CLAUDE.md` warns outlives its condition:
  //   docker compose exec -T postgres psql -U molt -d molt_v1 \
  //     -c "\d+ activity_entries"
  // Match the DOTTED spelling. The pre-0013 snake_case forms
  // (`card_created`, `card_position_changed`, …) still appear in 0004, 0011
  // and 0012 as historical constraint bodies, not as the live vocabulary.
  //
  // (b) ⚠ THESE ARE THE FIRST EVENTS IN THIS REGISTRY THAT CARRY A REAL
  // `cardId`, AND IT GOES IN THE **COLUMN**, NOT THE PAYLOAD.
  // `activity_entries` has a real `card_id` column — it is
  // `ck_activity_entries_scope`'s first disjunct alongside `board_id` — and
  // `eventInputSchema` below has always carried the field. Every B12 event
  // sets **both** `cardId` and `boardId`: `cardId` because `FR-COLLAB-020`
  // describes the board feed as "entries from all cards and lists on the
  // board, **each entry naming its card**", which is a join B28 cannot make
  // from a payload key without scanning JSONB; `boardId` because
  // `publishEvents` filters on `boardId !== null` and fans out to
  // `boardChannel(boardId)`, which is the mechanism behind `FR-BOARD-025`
  // AC4 ("given a card moved between lists, when another member has the
  // board open, then they see the move without reloading").
  //
  // **B11's payload-only `listId` is NOT a precedent to follow here**, and
  // the difference is mechanical rather than stylistic: `activity_entries`
  // has no `list_id` column, so a list event has nowhere to put one and
  // `0016` recorded that trade. A card event does have somewhere. Copying
  // B11's shape — `cardId` in `payload` — would leave the column null on
  // every card event in the product and quietly make FR-COLLAB-020's
  // "naming its card" clause unimplementable without a backfill.
  //
  // `listId` and `workspaceId` still travel in `payload`, for exactly the
  // reason above: no columns exist for them. A between-list move therefore
  // carries the previous and the new list id in its payload, on the same
  // "append-only with no backfill" argument `board.visibility_changed` and
  // `list.position_changed` record for their before/after values.
  //
  // (c) THE `card.*` NAMES IN THE CONSTRAINT THAT ARE **NOT** REGISTERED
  // HERE, WITH THEIR OWNERS. A grep of `card.` against the live constraint
  // returns SIXTEEN names — twelve at every pass through B21, thirteen after
  // B22 added one, fifteen after B27 added two, and sixteen after B31 added
  // one — and this registry declares THIRTEEN of them.
  // ⚠ **"TWELVE" IS CORRECTED IN PLACE AT B35, WHICH REGISTERED
  // `card.copied_from_card` WITHOUT TOUCHING THE CONSTRAINT AT ALL** — see
  // the fourth departure note at the foot of this section, which is where
  // the full arithmetic and the reason it moves only one number, not two,
  // are argued.
  // ⚠ **"THIRTEEN … NINE" stood here through B26 and
  // is corrected in place at B27, which added `card.relation_added` and
  // `card.relation_removed` (`FR-TMPL-028`, `0022`).** Both arithmetic halves
  // moved by two and the waiting list below did not move at all — B27's two
  // names were never on it, exactly as `card.deleted` was never on it, because
  // a name the constraint did not hold cannot be waiting in a queue of
  // placeholders.
  // ⚠ **AND "FIFTEEN … ELEVEN" IS CORRECTED IN PLACE AT B31, WHICH ADDED
  // `card.field_overridden` (`FR-EXP-031` AC4 / `FR-COLLAB-026` AC2,
  // `db/postgres/migrations/0024_card_field_override_activity.sql`).** Both
  // halves moved by one and the waiting list below still did not move: like
  // `card.deleted` and like B27's two, this name was in the constraint in NO
  // spelling before its own migration, so it was never a placeholder queued
  // here. **Three corrections to this arithmetic in three blocks that touched
  // the `card.` prefix, and every one of them moved BOTH halves by the same
  // amount** — which is the shape to expect, and the shape that says the rule
  // is being followed: a new `card.*` name arrives in the constraint and in
  // this registry in one change, and only a `0004` placeholder can ever make
  // the two halves move by different amounts. The other three ARE
  // placeholders, and the rule that keeps
  // them waiting is `0013`'s own — "a block that first emits one registers it
  // here then, not in advance":
  //     card.merged                       — B63 (card merge and single-card
  //                                         export)
  //
  // ⚠ **"declares four … the other eight" was true when B12 wrote it and is
  // corrected in place: `card.description_edited` left this list.** It was
  // listed here as `FR-CARD-003`/`BE-14.2`'s, and `BE-14.2` has since built the
  // route that emits it — see the B14 block at the foot of this registry. The
  // arithmetic moved by one in each direction (four registered becomes five,
  // eight waiting becomes seven) and the *rule* did not move at all: the name
  // was registered by the block that first emitted it, which is exactly what
  // this paragraph asked for. A reader who finds the old counts in a stale copy
  // of this comment should recount against `EVENT_TYPE_NAMES` rather than trust
  // either number.
  //
  // ⚠ **AND `card.moved_between_boards` HAS NOW LEFT IT TOO, on the identical
  // footing — recorded as a SECOND correction beside the one above rather than
  // folded into it, because the two are two instances of one rule and reading
  // them together is what shows the rule working rather than being restated.**
  // It was listed here as `FR-BOARD-026`/B21's, and B21 has built the route
  // that emits it: `POST /cards/:id/move-to-board` (`BE-21.1`, `FR-TMPL-018`,
  // also spec'd as `FR-CARD-029`) — see that entry at the foot of this block.
  // The arithmetic moves by one in each direction again (five registered
  // becomes six, seven waiting becomes six) and the rule does not move at all.
  // ⚠ Neither correction needed a migration: both names were `0004`
  // placeholders dotted by `0013`, so what each was waiting for was an
  // **emitter**, never DDL. `db/postgres/migrations/` is at nineteen files for
  // both.
  //
  // ⚠ **AND `card.archived` / `card.unarchived` HAVE NOW LEFT IT TOO — A
  // THIRD CORRECTION BESIDE THE TWO ABOVE, WITH A FOURTH NAME ARRIVING
  // BESIDE THEM THAT WAS NEVER ON THIS LIST TO LEAVE. READ BOTH HALVES
  // BEFORE TREATING THIS AS ONE MORE INSTANCE OF THE SAME CORRECTION.**
  // `card.archived` and `card.unarchived` were listed above as
  // `FR-BOARD-028`/`BE-22.1`'s, both `0004` placeholders dotted by `0013`,
  // and B22 has built the routes that emit them —
  // `POST /cards/:id/archive` and `POST /cards/:id/restore` — see the two
  // entries at the foot of this block. The arithmetic moves by TWO in each
  // direction this time (six registered becomes eight, six waiting becomes
  // four) and the rule does not move at all: this is B12's reason — "the
  // names already exist" — arriving for a FOURTH time (B14 was the second,
  // B21 the third).
  //
  // ⚠ **`card.deleted` is NOT a fifth instance of that reason, and it was
  // never on the waiting list above for the same arithmetic to apply to.**
  // `FR-EXP-011`'s permanent delete (also `BE-22.1`) is absent from `0004`
  // in every spelling — there is no `card_deleted` placeholder to have been
  // dotted — so it needed its own migration,
  // `db/postgres/migrations/0020_card_delete_activity.sql`, the first this
  // Cards section has ever required. That is the SAME mixed shape B09's,
  // B11's, B15's and B16's migrations produced elsewhere — two of three
  // names already there, one genuinely new — arriving on this noun for the
  // first time. On this file's own standing rule (see the correction in the
  // B15 block comment below, "partial is the normal case and no ordinal
  // should be claimed for it"), no ordinal is claimed for that shape here
  // either — only for the specific "names already exist" reason two of the
  // three share. The grep at the top of this comment moved from twelve
  // names to thirteen for exactly this reason, and the ninth name this
  // registry now declares arrived pre-registered rather than leaving a
  // queue it was never waiting in. See `card.deleted`'s own entry for the
  // argument in full.
  //
  // ⚠ The two migrations are not the same KIND of migration either.
  // `card.archived`/`card.unarchived` needed none — an emitter was all
  // either was waiting for, exactly like `card.moved_between_boards` above.
  // `card.deleted` needed `0020` alone, and `0020` widens
  // `ck_activity_entries_type` for this one name and touches nothing else —
  // no other constraint, no table, `0018`'s and `0017`'s shape rather than a
  // fresh one. `db/postgres/migrations/` moves from nineteen files to
  // TWENTY, and the Postgres-only `activity_entries` CHECK-change count
  // moves from eight to NINE — the first change to either number since
  // `0018_comment_activity.sql`.
  //
  // Two neighbours that a grep for "card" also returns and that are neither
  // `card.*` nor B12's: `member.added` / `member.removed` are *card*
  // membership (`FR-CARD-007`/`FR-CARD-008`, B14 — see the B10 block above,
  // which exists to stop them being confused with `board.member_*`, and which
  // matters more now that B14 has registered and emits them), and
  // ⚠ `checklist.item_converted_to_card` is **B62's** — `FR-CARD-021`,
  // `BE-62.2`, "convert a checklist item into a card", Parity-Later and
  // unbuilt. **This line read "is B16's" from B12 until B26, and that was
  // simply wrong** — corrected in place rather than deleted, on this file's
  // own practice, because the way it was wrong is worth one sentence: B16 is
  // comments and has never had anything to do with a checklist, so the claim
  // was not a stale ownership that later moved but a two-character slip that
  // survived several passes because nobody emitting a checklist event ever
  // read it. B26 is the block that finally did, and it verified the owner
  // against `implementation-plan.md`'s `BE-62.2` item line rather than
  // against this comment. The name is still a `0004` placeholder, still
  // unemitted and still deliberately UNREGISTERED below — see the B26 block
  // at the foot of this registry, whose migration reproduces it untouched.
  //
  // ⚠ **AND `card.copied_from_card` HAS NOW LEFT IT TOO — A FOURTH DEPARTURE
  // ON THE IDENTICAL FOOTING TO THE THREE ABOVE, ARRIVING FOR THE FIFTH TIME
  // B12's REASON HAS BEEN ASKED FOR (B14 was the second, B21 the third, B22
  // the fourth).** It was listed above as `FR-TMPL-013`/`BE-35.1`'s (the same
  // capability `FR-BOARD-027` and `FR-CARD-030` also spec), a `0004`
  // placeholder (`card_copied_from_card`) dotted by `0013`, and B35 has built
  // the shared copy engine that emits it — `apps/server/src/copy/copy-card.ts`,
  // called once per copied card from `POST /cards/:id/copy`,
  // `POST /lists/:id/copy` and `POST /boards/:id/copy` alike — so it left the
  // loop for the registry. The arithmetic moves by one in each direction,
  // exactly like B14's and B21's single departures rather than B22's double:
  // twelve registered becomes THIRTEEN, four waiting becomes THREE, and the
  // constraint total does not move at all — `sixteen` stays `sixteen`,
  // because this departure needed no migration, on the identical no-DDL
  // footing every departure in this section has needed. A reader who finds
  // "TWELVE … four" in a stale copy of this comment should recount against
  // `EVENT_TYPE_NAMES` rather than trust either number, on the standing
  // instruction the B14 correction above already gives.
  //
  // ⚠ `card.copied_from_card` is not a sixth instance of the *migration*
  // question either — it needed none, same as `card.moved_between_boards`
  // and `card.archived`/`card.unarchived` above and unlike `card.deleted`,
  // `card.relation_added`/`card.relation_removed` and
  // `card.field_overridden`. `db/postgres/migrations/` stays at
  // twenty-five files.
  //
  // ⚠ **AND `card.added_to_list` HAS NOW LEFT IT TOO — B43 files a boardless
  // Inbox card onto a real board/list, and this is the first block whose
  // subject is literally "a card that already exists is added to a list".** The
  // name was a `0004` placeholder dotted by `0013`, so this is the same no-DDL
  // departure shape as `card.copied_from_card`: the live constraint already
  // admitted it, and what was missing was a registered emitter. It is not
  // `card.created`, because the card was created earlier in the private Inbox;
  // reusing `card.created` would also hit the notification rule that expects a
  // board-scoped creation. It is not `card.created_by_email`, whose owner is
  // now B49 inbound board email.
  //
  // (d) WHY A REORDER AND A MOVE ARE TWO NAMES ON ONE ROUTE.
  // `POST /cards/:id/move` serves both gestures — `cardMoveBodySchema`'s
  // docstring says why one route rather than two — but the *feed* must
  // render them differently, and a reader cannot recover which happened from
  // a single name plus a payload without switching on `payload`, which is
  // the thing this registry exists to stop (see the profile block at the
  // top). "Ada moved this card to Doing" and "Ada reordered this card" are
  // different sentences: the first is a state change on the board somebody
  // may be waiting for, the second is housekeeping. The automation matcher
  // (`FR-AUTO-*`) makes the split load-bearing rather than cosmetic — "when
  // a card is moved into list X" is a rule people write, and it must not
  // fire on a drag inside the list it is already in.
  //
  // SCOPE. All four satisfy `ck_activity_entries_scope` twice over
  // (`card_id` and `board_id`), so no by-name exemption is needed — and
  // there is no migration in which to add one.
  // ---------------------------------------------------------------------

  /**
   * A card was added to a list — `FR-BOARD-020`.
   *
   * `true`. `FR-BOARD-020` itself never says "activity", so this rests on
   * `FR-COLLAB-020` — the board feed shows "entries from all cards and lists
   * on the board … each entry naming its card" — which names cards **first**
   * and therefore admits this event under the strict reading and the loose
   * one alike, exactly as `list.created` does. It is also the first line of
   * the card's own history and the entry that explains where everything
   * after it happened, which is the argument `board.created` and
   * `list.created` are both registered under.
   *
   * A placeholder since `0004`, so no migration was needed for it.
   */
  'card.created': { surfacesInFeed: true },
  /**
   * A card was created by sending mail to a board address — `FR-AUTO-030`,
   * absorbing `FR-TMPL-030` and `FR-NOTIF-035` for the backend path.
   *
   * It surfaces on the same footing as `card.created`: this is the first
   * board-visible placement of the card, and the entry is the durable fact that
   * the card arrived through email rather than the board composer. The row still
   * carries both `boardId` and `cardId` as columns so the board feed, card
   * activity, socket fan-out and search refresh follow the normal spine.
   *
   * A placeholder since `0004`, so no migration is needed for the type name.
   */
  'card.created_by_email': { surfacesInFeed: true },
  /**
   * A previously boardless Inbox card was filed onto a board list —
   * `FR-NOTIF-018`, B43.
   *
   * `true`, on the same footing as `card.created`: after filing this is the
   * card's first board-visible placement, and the destination board feed needs
   * a durable row explaining how it arrived. The row must carry both `boardId`
   * and `cardId` as columns so `publishEvents` reaches the destination board
   * room and the card's activity can join without scanning payload JSON.
   *
   * A placeholder since `0004`, so no migration is needed for the type name.
   * Inbox creation itself remains private and emits no event in B43.
   */
  'card.added_to_list': { surfacesInFeed: true },
  /**
   * A card was renamed — `FR-BOARD-022` (quick edit from the board face) and
   * later `FR-CARD-002` (the title on the card back, `BE-14.2`).
   *
   * `true`, on `FR-COLLAB-020`. Note that this is **not** the
   * by-the-requirement footing `board.renamed` and `list.renamed` stand on:
   * both of those have a behaviour sentence saying the rename is "recorded
   * in board activity", and `FR-BOARD-022` has no such sentence — its
   * criteria are about the quick-edit surface. It surfaces anyway because a
   * feed that showed a card being created and moved but not renamed would be
   * a feed that cannot answer "why is this card called that now", and
   * because the two levels above it already surface theirs.
   *
   * ⚠ **One name for both routes, deliberately** — and the prediction that
   * stood here is corrected in place rather than deleted, because the way it
   * was wrong is the more useful half of it. B12 wrote: "`BE-14.2` will patch
   * the same column from the card back, and it should emit this name rather
   * than a second `card.title_edited`: the fact is identical and the surface
   * the edit came from is not something the feed renders. A second spelling
   * would leave B28 switching on which route wrote the row."
   *
   * The reasoning held and its conclusion went one step further than predicted:
   * **B14 built no second route at all.** A second route patching the same
   * column, with the same event, the same validation and the same refusal of an
   * empty title, differs from `PATCH /cards/:id/title` only in its path — that
   * is duplication, not a field-scoped patch, and SC-4 asks for one route per
   * field, not one route per surface that edits the field. `FR-CARD-002`'s
   * whole difference from `FR-BOARD-022` is the surface the edit came from, and
   * the server cannot see it, must not be told it, and would have nothing to do
   * with it if it were — which is the sentence above, applied to the route
   * rather than to the name.
   *
   * So "one name for both routes" is now "one name, one route, two client
   * surfaces", and the guidance it was written to give still stands for anyone
   * tempted by a `card.title_edited`: there is one fact here, and this is its
   * name. `packages/shared/src/card-detail.ts`'s header carries the long form,
   * including what `FR-CARD-002` genuinely does own (all of it client-side,
   * `FE-14.2`).
   *
   * A placeholder since `0004`, so no migration was needed for it.
   *
   * ⚠ **Since B28, the payload carries `previousTitle` beside `title`** —
   * `apps/server/src/routes/cards.ts`'s `PATCH /cards/:id/title` handler
   * (the one route both `FR-BOARD-022` and `FR-CARD-002` share), additive
   * JSONB, no migration. `FR-COLLAB-018` names *"renamed this card (from
   * &lt;old name&gt;)"* as the canonical sentence **verbatim**, and this is
   * the entry it names — B28's grammar renders the sentence from
   * `previousTitle`. **Entries written before this change do not carry the
   * key** — the whole historical corpus — so the grammar has a fallback
   * branch for its absence rather than assuming every `card.renamed` row
   * looks alike; see `packages/shared/src/activity.ts`'s header for how that
   * fallback is expressed as a neutral placeholder rather than a second
   * template.
   */
  'card.renamed': { surfacesInFeed: true },
  /**
   * A card moved **within its own list** — `FR-BOARD-024`, `FR-BOARD-025`.
   *
   * A placeholder since `0004` (`card_position_changed`), dotted by `0013`.
   * ⚠ It is **not** the same fact as `list.position_changed`, which `0016`
   * added for B11 and whose docstring says so from the other side: a card's
   * rank within a list and a list's rank within a board are different facts
   * about different tables, and this registry is keyed by the full dotted
   * string, so they are two rows in one CHECK constraint. The names mirror
   * each other on purpose; neither is a reuse of the other.
   *
   * `true`, on `FR-COLLAB-020`, and the judgement worth stating is the one
   * *against* suppressing it as noise — the same one `list.position_changed`
   * records one level up. `FR-BOARD-024`'s whole user story is that "the
   * vertical order of a list carries meaning" and its AC1 makes the order
   * shared ("when a different member opens the board, then they see the same
   * order"), so a reorder is a change to shared state rather than a personal
   * view setting, and SC-1 admits no unrecorded mutation however ordinary.
   *
   * ⚠ **A drop in place emits nothing at all.** `FR-BOARD-025`'s edge case —
   * "Dropping a card back on its original position must be a no-op and must
   * not create an activity entry" — is the one place in this block where the
   * absence of an event is a requirement. The route detects it before
   * writing (`compareRanks`, the card's current rank against the named gap)
   * and returns the card unchanged. A handler that mints a fresh rank
   * between the same two neighbours satisfies every visible assertion and
   * fails this one silently.
   *
   * The payload carries the previous and the new rank, for the reason
   * `board.visibility_changed`'s carries both levels: `activity_entries` is
   * append-only with no backfill, and B28 cannot render "moved up" against
   * "moved down" from a single value without a second query against a row
   * that has since moved again.
   */
  'card.position_changed': { surfacesInFeed: true },
  /**
   * A card moved **to a different list on the same board** —
   * `FR-BOARD-025`.
   *
   * `true` **by the requirement rather than by judgement**, and it is the
   * only one of these four on that footing, so cite this entry rather than
   * the block comment when the question comes up. `FR-BOARD-025`'s behaviour
   * text: "Dropping commits the move for all members and **records it in
   * board activity**." That is `board.renamed`'s and `list.renamed`'s exact
   * footing, in almost the same words, and it does not depend on how
   * `FR-COLLAB-020` is read.
   *
   * Its counterpart is a client-side promise the server keeps by never being
   * asked: AC2 says a drag abandoned with Escape returns the card to its
   * origin and "no move is recorded", which means no request is sent.
   *
   * The payload carries the previous list id, the new list id and both
   * ranks. The list ids are in `payload` because `activity_entries` has no
   * `list_id` column — the same trade `0012` recorded for the workspace id
   * and `0016` for the list — and both are needed because B28 renders "moved
   * from Doing to Done" from one row.
   *
   * ⚠ **This is emphatically not `card.moved_between_boards`**, which is
   * also in the constraint and is also a `0004` placeholder.
   * `FR-BOARD-025`'s own sentence closes the cross-board drag —
   * dragging works "within a list, between lists on the same board — and
   * *not* between boards (see `FR-BOARD-026`)" — and `ck_cards_parent` keeps
   * `board_id` and `list_id` non-null together, so `cards.board_id` is set
   * at creation and never changes **in this block**.
   *
   * ⚠ **"…and stays unemitted … B21 registers that name when it first emits
   * one" is corrected in place: B21 has arrived and that name is now
   * registered**, immediately below. The half of this paragraph that matters
   * is unchanged and is the reason the correction is a correction rather than
   * a deletion — the two names are **still emphatically not each other**, and
   * they are now both live, which is the state this warning was written for.
   * `POST /cards/:id/move` (B12) still refuses a `listId` on another board
   * with a **404 by construction**, and a cross-board move is a different
   * route, a different body and a different event; `routes/cards.ts` says so
   * in capitals at the handler. The "in this block" scoping above is what
   * keeps the rest of the paragraph true: `cards.board_id` is written by
   * exactly two statements in the product, both in
   * `apps/server/src/routes/cross-board-move.ts`.
   *
   * A placeholder since `0004`, so no migration was needed for it.
   */
  'card.moved_between_lists': { surfacesInFeed: true },
  /**
   * A card moved **to a list on a different board** — B21, `FR-TMPL-018`
   * (`BE-21.1`), the same capability `FR-BOARD-026` and `FR-CARD-029` specify.
   *
   * A placeholder since `0004` (`card_moved_between_boards`), dotted by
   * `0013`, and unemitted by anything until now — so **no migration**, which
   * is B12's reason ("the names already exist") arriving for the third time.
   * 25 characters, inside `EVENT_TYPE_MAX_LENGTH`.
   *
   * `true`. `FR-BOARD-025`'s "records it in board activity" is authority for
   * the *same-board* move above and is not stretched to cover this one; this
   * rests on `FR-COLLAB-020` and on the plain observation that a card leaving
   * a board is a larger change to that board than a card moving one list
   * across it, which is already `true`. ⚠ It is also the entry that makes the
   * **source** board's feed coherent: every pre-move `activity_entries` row
   * stays on board A carrying a `card_id` that now lives elsewhere — B21
   * deletes nothing and repoints nothing, because `FR-TMPL-018` AC3 says the
   * pre-move history is *not shown* on the destination rather than *deleted*,
   * and SC-1 admits no unrecorded mutation being un-recorded later. Without
   * this row, board A's feed would show a card's history that simply stops.
   *
   * ---------------------------------------------------------------------
   * ⚠⚠ EMITTED **TWICE PER GESTURE, ONCE PER BOARD**, WITH TWO DIFFERENT
   * PAYLOADS — SEE `list.moved_between_boards` FOR THE FULL ARGUMENT, WHICH IS
   * THE SAME ONE
   * ---------------------------------------------------------------------
   * One row with `board_id = <source>`, one with `board_id = <destination>`,
   * because `publishEvents` puts each event on exactly one
   * `boardChannel(boardId)` and B18 built no replay: a single destination row
   * leaves every socket with the source board open rendering a card that has
   * left it. The payload rule — *no payload key names a row that the audience
   * of that row's own `board_id` could not already see* — applies here in its
   * strictest form, because unlike a list, the card's **lists** are
   * board-scoped and are the only interesting thing to name:
   *
   *     departure row   payload: { workspaceId: <source>, previousListId,
   *                                previousPosition }
   *     arrival row     payload: { workspaceId: <destination>, listId,
   *                                position }
   *
   * ⚠ So board A's feed can say only "⟨actor⟩ moved this card out of ⟨Doing⟩"
   * and board B's only "⟨actor⟩ moved this card into ⟨Backlog⟩". **Neither
   * names the other board**, and neither carries `payload.boardId` or
   * `payload.previousBoardId`. Giving both rows one payload — the shape a
   * reader reaches for first — hands board A's members a list id on a board
   * they may not be able to read, which `ws/board-socket.ts` forwards into the
   * room byte for byte, and which B28 would then try to render as a navigable
   * list name (`FR-COLLAB-018` AC2).
   *
   * ⚠ **DISCRIMINATOR: `'position' in payload` → arrival; otherwise
   * departure.** Uniform with `list.moved_between_boards`, and there is
   * deliberately **no `direction` key** — see that entry for the argument, and
   * for the instruction that a future `generateNotifications` mapping must
   * switch on it or notify twice.
   *
   * ⚠ **`cardId` and `boardId` ride as COLUMNS on both rows**, per this block's
   * own (b) argument: `cardId` because `FR-COLLAB-020` describes the feed as
   * "each entry naming its card", a join B28 cannot make from a JSONB key, and
   * `boardId` because `publishEvents` fans out on it. Both rows therefore
   * satisfy `ck_activity_entries_scope` **twice over**, on disjuncts 1 and 2 —
   * B12's card events' shape exactly, so `0004`'s constraint needed no by-name
   * exemption and must not be touched.
   *
   * ---------------------------------------------------------------------
   * ⚠ WHAT THE MOVE DOES **NOT** EMIT, AND WHY EACH ABSENCE IS A DECISION
   * ---------------------------------------------------------------------
   * The move reconciles the card's labels and members against the destination
   * board (`FR-TMPL-019`, `FR-TMPL-020`) and emits **no `label.added`, no
   * `label.removed`, no `member.added` and no `member.removed`** for any of it,
   * though all four are registered above and would parse. Reconciliation is one
   * gesture's *consequence*, not a series of user gestures: a `label.added` row
   * would tell the card's stream that the actor applied a label they never
   * picked. `label.deleted`'s "no per-card `label.removed` storm" is the
   * precedent.
   *
   * ⚠ **For `member.removed` it is not only tidiness.** That name **is** mapped
   * in the server's `NOTIFICATION_FROM_EVENT` to `removed_from_card` with a
   * `payloadTarget` audience, so emitting it per stripped member would send
   * each of them "you were removed from a card" — for a card on a board they
   * cannot see — N notifications for one gesture, generated inside
   * `withTransaction` by a module the route file never mentions.
   * `FR-TMPL-020` AC4 defers notification behaviour to
   * `09-notifications-and-personal-surfaces.md` in as many words, and the whole
   * outcome rides in the **response** instead (`reconciliationSchema` in
   * `packages/shared/src/cross-board-move.ts`), addressed to the actor, which
   * is what `FR-TMPL-019` and `FR-TMPL-020` both actually ask for.
   *
   * ⚠ **`label.created` IS emitted**, once per label the reconciliation creates
   * on the destination board, with `card_id: null` and the destination's
   * `board_id` — B15's shape for that name. That is not B15's seeded-labels
   * case, which emits nothing "because six `label.created` rows at the head of
   * every board's feed would attribute to the creator a vocabulary they did not
   * choose": here the actor **did** choose it, by moving a card carrying that
   * label, and a board-vocabulary change with an identifiable actor is exactly
   * what SC-1 exists for.
   *
   * ⚠ **No card numbers in the payload.** The card **is** renumbered on arrival
   * (`OQ-2`, `allocateCardNumber`), and the number it held on the source board
   * is deliberately unrecorded: `FR-EXP-015` makes the number and slug after
   * the short link cosmetic and ignored on lookup, so an old permalink still
   * resolves and nothing unrecoverable is lost. The *allocation* is
   * load-bearing; the old *value* is read by nothing.
   */
  'card.moved_between_boards': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // ⚠⚠ B22 ARRIVES HERE, INSIDE THE CARDS SECTION RATHER THAN AS A SECTION
  // OF ITS OWN — `FR-BOARD-028` (archive and restore a card, `BE-22.1`) and
  // `FR-EXP-011` (permanently delete an archived card, also `BE-22.1`).
  // Three names, on B21's precedent immediately above rather than B15's or
  // B16's: a `card.*` name is grouped by NOUN here, not by the block that
  // first emits it, and B21 already put `card.moved_between_boards` in this
  // section despite shipping its own route file
  // (`apps/server/src/routes/cross-board-move.ts`) that touches neither
  // `cards.ts` nor this comment's neighbourhood. `card-archive.ts` is B22's
  // route file, on the identical rule, and it is not the same file as
  // `board-archive.ts`, which answers the read (`GET /boards/:id/archive`)
  // rather than emitting any of the three names below.
  //
  // A MIXED DDL STORY, WHICH THIS SECTION HAS NOT HAD BEFORE. `card.archived`
  // and `card.unarchived` are `0004` placeholders (`card_archived`,
  // `card_unarchived`), dotted by `0013`, unemitted by anything until now —
  // B12's reason, "the names already exist", arriving for a fourth time (see
  // the correction in (c) above). `card.deleted` is not: `FR-EXP-011`'s
  // permanent delete has no `0004` placeholder in any spelling and needed its
  // own migration, `db/postgres/migrations/0020_card_delete_activity.sql`,
  // the first this section has ever required. Two of three already existed;
  // one did not — the same *partial* mixture `0017` and `0018` produced for
  // labels and comments, arriving on this noun for the first time (and, per
  // the correction above, claiming no ordinal for that shape itself).
  // `CLAUDE.md`'s standing rule survives unchanged regardless: grep each name
  // individually against the live constraint, in the dotted spelling, and
  // count the hits against the names rather than against the block.
  //
  // SCOPE, AND WHY EVERY ROW CARRIES A `card_id` COLUMN. All three follow
  // B12's rule inherited whole: `activity_entries` has a real `card_id`
  // column, `FR-COLLAB-020` describes the board feed as "entries from all
  // cards and lists on the board, each entry naming its card", and `boardId`
  // rides beside it because `publishEvents` filters on `boardId !== null`
  // before fanning out to `boardChannel(boardId)` — D5 of the block
  // contract, and the mechanism `FR-CARD-028`'s archived banner and
  // `FR-EXP-011`'s permanent removal both need to reach an open board
  // without a reload. No by-name exemption to `ck_activity_entries_scope`
  // was needed for any of the three; all three satisfy its first disjunct
  // twice over, on `card.moved_between_boards`'s footing exactly.
  //
  // OBSERVERS. D2 of the block contract admits observers on all three
  // routes — `requireCardWriter`, unchanged — because `FR-PERM-016`'s
  // content-write exclusions (which name archiving explicitly) are `V1` and
  // `BE-38.1`'s bundle, exactly as `member.self_joined`'s entry above
  // records for `FR-CARD-007`. Adding a refusal in any of the three routes
  // below would be a defect, not a feature; nothing about that changes what
  // is registered here, which is unconditional on who the actor is.
  // ---------------------------------------------------------------------

  /**
   * A card was archived — `FR-BOARD-028`, `POST /cards/:id/archive`.
   *
   * A `0004` placeholder (`card_archived`), dotted by `0013`, unemitted by
   * anything until now — no migration needed for this one. See the block
   * comment above for the mixed-DDL story its two siblings below complete.
   *
   * `true`, on `FR-COLLAB-020` and on `list.archived`'s footing exactly: an
   * archived card is **recoverable** (`FR-EXP-010`'s archived-items surface,
   * with a restore action), so unlike a deletion this is a state a member
   * arrives at wanting to know who put it there.
   *
   * ⚠ **This is the CARD's own archive, and it is not what `list.archived`
   * describes.** `POST /lists/:id/archive` (B11) writes one column on the
   * list and never touches `cards.archived_at` — `list.archived`'s own
   * docstring records why at length, and D8/D9 of this block's contract
   * inherit that invariant rather than re-deciding it: a card merely hidden
   * by an archived parent list has no row of its own in this stream, because
   * nobody archived it. `FR-EXP-010`'s edge case is what makes such a card
   * findable anyway, and the distinction rides on the wire as
   * `restorableInPlace` — see `packages/shared/src/board-archive.ts`.
   *
   * ⚠ **A no-op emits nothing (D7).** Archiving an already-archived card is
   * legal and idempotent — two clients racing the same archive control — and
   * the route decides it with `UPDATE … WHERE archived_at IS NULL RETURNING
   * id`, never SELECT-then-write, which races into a 500 on nothing in
   * particular. An unconditional `emit()` after that `UPDATE` would pass
   * every visible assertion (200, `archived: true`) while filling the feed
   * with phantom archives — `FR-BOARD-025`'s drop-in-place edge case in a
   * fourth costume.
   *
   * The payload carries the card's `title`, its `listId` and the workspace
   * id — no columns exist for the latter two, `0012`'s and `0016`'s trade —
   * matching the sibling payload convention every B12 event above already
   * follows for the same three facts.
   */
  'card.archived': { surfacesInFeed: true },
  /**
   * An archived card was restored — `FR-EXP-010`, `FR-CARD-028`,
   * `FR-TMPL-031`, `POST /cards/:id/restore`.
   *
   * A `0004` placeholder (`card_unarchived`), dotted by `0013`, unemitted by
   * anything until now — no migration needed. See the block comment above.
   *
   * `true`, symmetrically with `card.archived`: the pair is one lifecycle,
   * and a feed showing only the departure would read as though the card had
   * never come back. `list.unarchived`'s docstring makes the identical
   * argument one container up.
   *
   * ⚠ **`card.unarchived`, never `card.restored` — D3 of the block contract,
   * and `list.unarchived`'s precedent followed rather than re-derived.** The
   * constraint has held `card.unarchived` since `0004` and holds no
   * `card.restored` in any spelling; minting a second name for the identical
   * fact would cost a migration for nothing and would disagree with
   * `list.unarchived`, which already emits for the same gesture one
   * container up.
   *
   * ⚠ **ONE NAME, TWO BRANCHES, AND THE PAYLOAD IS WHAT TELLS THEM APART —
   * D1/`OQ-3` LANDING ON THE WIRE.** `FR-EXP-010`'s behaviour sentence:
   * "Restoring a card returns it to its original list if that list still
   * exists and is not archived; otherwise the user is asked to choose a
   * destination list." Branch A (restore in place) computes **no rank at
   * all** — D8, `archived_at = NULL` and nothing else, because
   * `lastRankInList` counts archived cards and the card's own rank was never
   * cleared by archiving it. Branch C (a destination list is supplied)
   * mints one via `lastRankInList(dest, null)`. There is no Branch B row: a
   * caller who receives `FR-EXP-010` AC3's `409` (no live, unarchived list
   * to restore into, and no destination named) has not restored anything —
   * the refusal is not a mutation, on `FR-PERM-032`'s last-admin refusal's
   * exact footing above, and nothing is written or emitted for it.
   *
   * ⚠ **A no-op emits nothing (D7).** Restoring a card that is not archived
   * is legal and idempotent on `card.archived`'s identical footing, decided
   * the same way: `UPDATE … WHERE archived_at IS NOT NULL RETURNING id`.
   *
   * The payload carries the destination `listId`, `previousListId` **only
   * when Branch C lands the card somewhere other than where it left from**,
   * and the workspace id — the same "did it come back where it left from"
   * question `card.moved_between_lists` answers with two list ids, asked
   * here of a restore instead of a drag. No `title`: nothing about it
   * changed, on `card.description_edited`'s and `list.unarchived`'s
   * restraint against carrying facts a restore does not touch.
   */
  'card.unarchived': { surfacesInFeed: true },
  /**
   * A card was permanently deleted — `FR-EXP-011`, `DELETE /cards/:id`.
   *
   * ⚠⚠ **NEW in `db/postgres/migrations/0020_card_delete_activity.sql`, in
   * this spelling and every other — `0004` enumerated no `card_deleted`
   * placeholder, unlike `card.archived` and `card.unarchived` immediately
   * above.** 12 characters, the same length as `board.deleted` and
   * `list.deleted`, whose operation this mirrors one and two containers up.
   * This is the first migration the Cards (B12) section of this registry
   * has ever needed: `card.created`, `card.renamed`, `card.position_changed`
   * and `card.moved_between_lists` needed none (all four `0004`
   * placeholders), neither did `card.description_edited` (B14) or
   * `card.moved_between_boards` (B21), and neither did `card.archived` /
   * `card.unarchived` immediately above. See the block comment for why this
   * is a *mixed* case rather than a *partial* one repeated.
   *
   * ⚠⚠ **`false`, ON `list.deleted`'s AND `board.deleted`'s FOOTING EXACTLY,
   * AND FOR THE IDENTICAL MECHANICAL REASON — READ THIS BEFORE "HARMONISING"
   * IT WITH `label.deleted`'s `true`.** D4 of the block contract:
   * `DELETE /cards/:id` is a **soft** delete, `cards.deleted_at` set and no
   * row removed, because `fk_activity_entries_card_id` is `ON DELETE
   * CASCADE` and a hard delete would take the card's entire activity
   * history with it — this very row would delete itself on commit. But the
   * row survives while the feed it would appear in does not: every read
   * path in this server already carries `isNull(cards.deletedAt)`
   * (`isLiveCard()`), so the card is gone from the board face, the archive
   * listing and search the instant this commits, and no route restores it
   * — `FR-EXP-011`'s "not recoverable by anyone" holding **behaviourally**
   * rather than by a missing endpoint alone. An entry in a feed nobody can
   * open is an audit record, not a feed entry — `board.deleted`'s and
   * `list.deleted`'s exact argument. `label.deleted`'s `true` does not
   * reach here for the reason its own docstring gives from the other side:
   * a board's feed stays reachable after one label vanishes from it; a
   * card's own history, and the board's view of that card, do not survive
   * this row.
   *
   * It is written anyway — SC-1 admits no unrecorded mutation, and "who
   * deleted this, and when" is the audit question that outlives the row.
   *
   * ⚠ **Still BROADCAST, which is the point.** `surfacesInFeed` is the §2.6
   * feed discriminator and nothing else reads it, so `false` here still
   * reaches `boardChannel(boardId)` — the same mechanism `comment.deleted`,
   * `board.deleted` and `list.deleted` all rely on to remove something live
   * from a board a member currently has open, per `FR-COLLAB-025`'s inline-
   * notice edge case.
   *
   * ⚠⚠ **THE GATE IS THE CARD'S OWN `archived_at`, NOT ITS LIST'S — argued
   * in full at the route and recorded here because it decides whether this
   * row can ever exist at all.** `FR-EXP-011` AC1 refuses the delete with a
   * `409` unless the card is itself archived; a card merely hidden by an
   * archived parent list has not been archived by anyone and cannot be
   * deleted without first being archived in its own right — one step, then
   * a second, separate step, `00-`'s "nothing is destroyed in one step"
   * applied literally. So every `card.deleted` row this product ever writes
   * is preceded, on the same card, by a `card.archived` row that has not
   * since been undone by a `card.unarchived` — a fact `B28` can rely on
   * without a second query.
   *
   * The payload carries the deleted card's `title`, its `listId` and the
   * workspace id — a name and a location, not the card's content, on
   * `card.description_edited`'s and `comment.deleted`'s restraint. No
   * `authorUserId`: D2 admits observers on this route exactly as
   * `requireCardWriter` does everywhere else, so the actor column already
   * says who, and unlike `comment.deleted` this is not a moderation action
   * against another member's content — there is no second author to
   * distinguish it from.
   */
  'card.deleted': { surfacesInFeed: false },

  // ---------------------------------------------------------------------
  // ⚠⚠ B31 ARRIVES HERE, INSIDE THE CARDS SECTION AND WITH EXACTLY ONE NAME
  // — `FR-EXP-031` AC4 (a queued edit lands on top of another member's, "the
  // later edit wins and the overridden version is retrievable from the card's
  // activity") and `FR-COLLAB-026` AC2 ("my draft is preserved and I am told
  // the description changed"), `BE-31.1`.
  //
  // B22's precedent for arriving inside this section rather than opening one
  // of its own, and B22's reason: a `card.*` name is grouped by NOUN here,
  // not by the block that first emits it.
  //
  // PLACEMENT, WHICH IS NOT THE PLACEMENT `0024` GIVES THE SAME NAME, AND THE
  // DIFFERENCE IS DELIBERATE. The migration puts `'card.field_overridden'`
  // immediately after `'card.description_edited'` in the CHECK list, because
  // that predicate is read top to bottom as one list and the three names a
  // reader must understand together should be adjacent there. Here it sits at
  // the END of the `card.*` run instead — after `card.deleted`, before B14's
  // card-back section — because `card.description_edited` in THIS file is the
  // first of B14's five entries (a description and four `member.*` names,
  // covered as a set by the block comment below), and inserting a B31 entry
  // between the block comment and the four names it describes would split a
  // set that reads as one. It is not at the foot of the registry beside B27's
  // `card.relation_added` / `card.relation_removed` either: those two sit
  // under an ATTACHMENT block header because `FR-TMPL-028`'s prose calls a
  // related card an attachment, and a title/description concurrency notice
  // filed there is a name nobody looking for it would find. ⚠ Neither
  // ordering is load-bearing — this registry is keyed by the full dotted
  // string and nothing iterates it positionally — so the only cost of getting
  // it wrong is a reader's time, which is the whole reason it is argued.
  //
  // A MIGRATION OF ITS OWN, WHICH THIS SECTION HAS NEEDED ONCE BEFORE.
  // `card.field_overridden` was in `ck_activity_entries_type` in NO spelling
  // — `overrid` and `conflict` and `version` each returned ZERO against the
  // live constraint, measured one candidate at a time — so it needed
  // `0024_card_field_override_activity.sql`, the second migration this Cards
  // section has ever required after `card.deleted`'s `0020`. It is `0020`'s
  // shape exactly: one CHECK constraint widened by one name, no table, no
  // index, no other constraint touched.
  //
  // ⚠⚠ AND THE NAME THAT ALMOST TOOK ITS PLACE IS ALREADY A MEMBER OF THAT
  // CONSTRAINT. `custom.field_value_changed` is live (a `0004` placeholder,
  // dotted by `0013`), it is the ONLY member whose name contains the word
  // "field", and it reads exactly like "a field on a card changed". It is
  // **B56's user-defined custom fields**, it is deliberately UNREGISTERED
  // below on `checklist.item_converted_to_card`'s footing, and reusing it
  // would have been the one mistake in this file's history that produces NO
  // runtime error at all: the INSERT would succeed, the frame would broadcast,
  // and the row would surface in B28's feed under a sentence B56 has yet to
  // write. `0024`'s header calls that the eighth collision shape — the decoy
  // is a genuine member whose name describes your subject better than your own
  // name does — and it is the reason this block greps the NOUN as well as the
  // prefix.
  //
  // ⚠ THE FEED FLAG HAS A CONSEQUENCE NOTHING ELSE IN THIS FILE PREDICTS.
  // `surfacesInFeed: true` puts a name in `FeedEventName`
  // (`packages/shared/src/activity.ts`), and `ACTIVITY_TEMPLATES` there and
  // `ACTIVITY_PHRASERS` in `apps/server/src/activity/phrase.ts` are TOTAL
  // mapped types over that union. So this one entry mechanically requires a
  // translatable sentence and a phraser in the same change; both are compile
  // errors until they exist, which is exactly what those mapped types were
  // built to buy (see B28's own headers, which argue against a `switch` with a
  // `default` on the ground that a 57th name would silently render whatever
  // the default said). Budget for it: a `true` here is three files, not one.
  //
  // SCOPE. It carries `cardId` AND `boardId` on the COLUMNS, B12's rule
  // inherited whole, so it satisfies `ck_activity_entries_scope` twice over
  // and needs no by-name exemption. `boardId` is what carries it to the open
  // card back without a reload — which in this block is not an abstract
  // mechanism but the requirement itself: `BE-31.2`'s card room reads the
  // SAME `boardChannel(boardId)` this event is published to, filtered per
  // subscriber on the frame's `cardId`, so a null `boardId` here would leave
  // the member whose edit was overridden looking at stale text with nothing
  // red anywhere.
  // ---------------------------------------------------------------------

  /**
   * Somebody's write landed on top of a change to the same card field that
   * they had not seen — `FR-EXP-031` AC4, `FR-COLLAB-026` AC2, `BE-31.1`.
   * Emitted by `PATCH /cards/:id/title` and
   * `PATCH /cards/:id/description` when the request carried an
   * `if-field-version` header whose ULID is not that field's current version.
   *
   * ⚠ **NEW in `db/postgres/migrations/0024_card_field_override_activity.sql`,
   * in this spelling and every other** — 21 characters, inside
   * `EVENT_TYPE_MAX_LENGTH`, and the same length as `card.relation_removed`,
   * with which it now ties as the longest `card.` name.
   *
   * ⚠ **NEVER WRITTEN ALONE.** It accompanies `card.renamed` or
   * `card.description_edited` in the SAME `withTransaction` call, on the same
   * card, in the same `emit` buffer — so the field change and the notice
   * commit together or not at all, and `publishEvents` broadcasts both after
   * the commit. There is no route that produces this name by itself, and a
   * consumer that finds one without a sibling field-change entry on the same
   * card is looking at corruption rather than at a case to handle.
   *
   * ⚠ **The write is APPLIED, always. There is no 409 anywhere on this
   * path.** `FR-EXP-031` says the product "resolves last-write-wins per field
   * and surfaces a notice naming the card and what was overridden, rather than
   * silently discarding either side", and AC4 says the later edit **wins**. A
   * 409 discards one side. This entry is therefore a *notice*, not a
   * rejection: it records that a resolution happened, and the resolution
   * always went the same way. Three of the four rows of `BE-31.1`'s decision
   * table emit nothing at all — no header (byte-identical to what B12 and B14
   * shipped), a header matching the current version (nobody intervened), and a
   * header on a field with no version at all (nothing recorded to override).
   *
   * `true`, and unlike most of this registry's `true`s it is not a judgement:
   * `FR-EXP-031` AC4 requires the overridden version to be retrievable **from
   * the card's activity**, which is the user-visible feed `B28` renders from
   * `FEED_EVENT_TYPES` (`GET /cards/:id/activity`). `false` would still
   * broadcast the frame and would still write the row — `surfacesInFeed` is
   * the §2.6 feed discriminator and nothing else reads it — so it would
   * satisfy the live half of `FR-COLLAB-026` AC2 and fail the durable half of
   * `FR-EXP-031` AC4 outright, with a green fan-out test either way. This is
   * the inverse of `card.deleted` above, whose `false` rests on the feed it
   * would appear in being unreachable; the card whose field was overridden is
   * very much still there, and its activity is where the requirement points
   * the overridden member.
   *
   * ⚠ **`true` here is NECESSARY for AC4 and is not SUFFICIENT, and the trap is
   * worth naming at the flag rather than one file over.** It puts the *entry* on
   * the feed; the entry's sentence is "overrode an unseen change to the
   * {field}", which names no text. What carries the overridden value to a client
   * is `activityEntrySchema.fieldOverride` (`./activity`), projected by
   * `apps/server/src/routes/activity.ts`. Set this `true`, write the payload,
   * and project neither, and AC4 is unmet with a green fan-out test, a green
   * feed test, and the row sitting in `activity_entries` where only SQL can
   * reach it — the state this block passed through before its integration pass
   * closed it.
   *
   * ⚠⚠ **THE PAYLOAD CARRIES THE OVERRIDDEN TEXT, AND THAT IS A NAMED
   * DEVIATION FROM B14's RULE RATHER THAN AN OVERSIGHT.**
   * `card.description_edited` above records "the previous and new **lengths**,
   * never the text", `PATCH /cards/:id/description`'s own route description
   * says so, and B31 changes neither. The exception is this one name and its
   * authority is `FR-EXP-031` AC4: a member told "your colleague's
   * 400-character description was replaced" has not had the overridden version
   * made *retrievable*, so a length cannot satisfy the criterion. Three
   * narrowings keep the exception from becoming B14's rule in reverse — the
   * row exists **only on a genuine conflict**, so the text lands on the rare
   * path and never on the routine edit; it is bounded by the field's own limit
   * (16,384 characters for a description, and no new bound is invented); and
   * B21's payload rule holds unchanged, because the overridden value is the
   * card's own former text, readable by every member of the board this row's
   * `board_id` names. `null` means the field was empty.
   *
   * Payload: `{ field, expectedVersion, overriddenVersion,
   * overriddenActorUserId, overriddenAt, overriddenValue, workspaceId }` —
   * `packages/shared/src/card-field.ts`'s `cardFieldOverridePayloadSchema`.
   * `field` is one of `CONCURRENCY_CONTROLLED_CARD_FIELDS` (`'title'` or
   * `'description'`) and is what makes ONE name sufficient where two would
   * have cost a migration per field: `cover.set`'s precedent (one name, three
   * variants keyed by the payload) and `board.permission_changed`'s (a
   * required `setting` key B38 extends rather than minting a fourth name).
   * The two version ids are `activity_entries.id` values — this table's own
   * primary keys, in JSONB, with no foreign key behind them and none wanted on
   * an append-only table where neither can dangle. `overriddenActorUserId` is
   * `null` for an automation actor, and has no FK either, so it survives the
   * account it names being deleted: the audit record must not lose the fact
   * that somebody's edit was displaced because they later left.
   *
   * ⚠ **`custom.field_value_changed` IS A DIFFERENT SUBJECT ENTIRELY**, and
   * the two share nothing but the word "field". That name is B56's
   * user-defined custom fields — a field DEFINITION with a value per card, a
   * `0004` placeholder that no block emits and this registry deliberately does
   * not declare. This name is a concurrency notice about the card's own title
   * or description. See the block comment above and `0024`'s header, which
   * treat the confusion as the collision shape that migration exists to
   * document.
   */
  'card.field_overridden': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // ⚠⚠ B35 ARRIVES HERE, AT THE END OF THE `card.*` RUN, ON
  // `card.field_overridden`'s PRECEDENT IMMEDIATELY ABOVE RATHER THAN A
  // SECTION OF ITS OWN — copying a card (`FR-TMPL-013`, also spec'd as
  // `FR-BOARD-027` and `FR-CARD-030`, `BE-35.1`) is grouped by NOUN here, the
  // same rule B22 and B31 both state above: a `card.*` name sits with its
  // siblings, not with the block that first emits it. The shared engine
  // (`apps/server/src/copy/copy-card.ts`) is called from three different
  // route files — `routes/card-copy.ts`, `routes/list-copy.ts`,
  // `routes/board-copy.ts` — none of them named `cards.ts`, on
  // `cross-board-move.ts`'s and `card-archive.ts`'s identical precedent
  // above of a route file that touches neither `cards.ts` nor this
  // comment's neighbourhood.
  //
  // Not placed beside B27's `card.relation_added`/`card.relation_removed` at
  // the foot of the registry either, for the same reason B31's placement note
  // above gives: those two sit under an ATTACHMENT block header because
  // `FR-TMPL-028`'s own prose calls a related card an attachment, which has
  // nothing to do with copying one. Neither ordering is load-bearing — this
  // registry is keyed by the full dotted string, not iterated positionally.
  //
  // ONE NAME FOR THREE ROUTES, EACH EMITTING IT ONCE PER CARD THE ROUTE
  // COPIES: a card copy emits it once; a list copy emits one per card in the
  // list; a board copy emits one per card on the board. `FR-COLLAB-017` lists
  // two spellings for the one fact "a card was created by copying another",
  // and this registry keeps the one already dotted in the live constraint
  // since `0004` (`card_copied_from_card` → `card.copied_from_card`) rather
  // than minting a second name for it — `card.renamed`'s "one name for
  // several surfaces" rule, generalised here to "one name, one gesture,
  // however many times the gesture repeats inside one request."
  // ---------------------------------------------------------------------

  /**
   * A card was created by copying another card — `FR-TMPL-013` (`BE-35.1`,
   * `POST /cards/:id/copy`), the same capability `FR-BOARD-027` and
   * `FR-CARD-030` also specify. Also emitted once per copied card by
   * `POST /lists/:id/copy` (`BE-35.2`) and `POST /boards/:id/copy`
   * (`BE-35.3`), which both call the same engine
   * (`apps/server/src/copy/copy-card.ts`) rather than looping a per-card
   * route of their own — see the block comment above for why one name
   * serves all three.
   *
   * A `0004` placeholder (`card_copied_from_card`), dotted by `0013`, and
   * unemitted by anything until now — so **no migration**, B12's reason
   * ("the names already exist") arriving for a FIFTH time. See the
   * correction at the foot of the block comment above, which updates the
   * running "registered / waiting" arithmetic in place.
   *
   * `true`. This is the copy's OWN creation entry — `card.created`'s
   * argument applies whole: it is the first line of the copy's history and
   * the entry that explains where it came from, which a member opening the
   * copy's activity needs on the same footing `card.created` gives every
   * other card. **No `card.created` accompanies it** — one creation, one
   * entry, `card.renamed`'s "one name, one route" reasoning turned from a
   * name onto a fact.
   *
   * ⚠⚠ **THE FEED FLAG HAS THE SAME CONSEQUENCE IT HAD FOR
   * `card.field_overridden` — A `true` HERE IS THREE FILES, NOT ONE, AND
   * THIS FILE IS ONLY THE FIRST.** `surfacesInFeed: true` puts this name in
   * `FeedEventName` (`./activity`), and `ACTIVITY_TEMPLATES` there,
   * `ACTIVITY_PHRASERS` in `apps/server/src/activity/phrase.ts`, and each
   * file's own test fixture (`activity.test.ts`'s `EXPECTED_SLOTS`,
   * `FR-COLLAB-018.spec.ts`'s `PAYLOADS`) are all TOTAL mapped types over
   * that union — so registering this entry is a compile error in those four
   * places until each supplies this key, which is by design (see B31's own
   * paragraph on this for `card.field_overridden`, above). **This block's
   * `events.ts`/`events.test.ts` registration does not close those four; a
   * later change must.** `CARD_FIELD_BY_EVENT_TYPE`
   * (`./card-field`) is a fifth place, not a compile error but a failing
   * test: this is a card's first placement exactly as `card.created` is
   * (see that entry's own mapping to `'placement'`), so the entry this name
   * wants there is `'card.copied_from_card': 'placement'`, not a second
   * exception beside `card.field_overridden` — the two are not the same
   * shape, and `card.field_overridden` is excluded there for a reason (a
   * notice, not a change) that does not apply to a card's own creation.
   *
   * ⚠⚠ **THIS IS NOT `card.added_to_list`, WHICH B43 HAS NOW REGISTERED FOR
   * FILING AN EXISTING INBOX CARD ONTO A BOARD LIST.** "A copied card is added
   * to a list" remains a plausible gloss on that name and the wrong owner for
   * this block: copy creates a new card and records its source; filing moves an
   * existing boardless card into its first board list.
   *
   * ⚠ **`cardId` and `boardId` ride as COLUMNS, on B12's rule inherited
   * whole — and `boardId` is the DESTINATION board, never the source.**
   * `cardId` is the newly created (copied) card's id, never the source
   * card's; `boardId` is the board the copy lands on. `publishEvents` fans
   * out on that column alone, so a row stamped with the source board would
   * satisfy `ck_activity_entries_scope` and reach no open socket — copy is
   * the one operation in this registry with two boards in play at once, and
   * this is the named silent failure `CLAUDE.md` warns of for exactly that
   * shape.
   *
   * ⚠ **No sibling row on the source.** Unlike `card.moved_between_boards`,
   * which departs one board and must keep that board's own feed coherent
   * about a card that left it, a copy leaves the source card completely
   * untouched — nothing left, nothing that needs explaining there. One row,
   * on the copy alone.
   *
   * ⚠ **Not mapped in `auto-watch.ts` or `generate.ts`, deliberately.** The
   * copier does not auto-watch the cards it creates and nobody is notified —
   * a 200-card board copy stays one quiet gesture, `createBoard`'s
   * six-unannounced-labels precedent generalised from a label to a card:
   * "a handler that emitted them would attribute to the creator a vocabulary
   * they did not choose" applies here to authorship of a *card*, not a label.
   *
   * ⚠⚠ **Already a member of `CARD_SEARCH_TRIGGER_EVENT_TYPES`**
   * (`apps/server/src/search/refresh-index.ts`), pre-registered there by B33
   * for exactly this block. `refreshSearchIndex` reindexes per emitted
   * event's card id, so this row is what makes a copied card searchable at
   * all — omit it on any one of the three copy routes and every card that
   * route copies is silently unsearchable, nothing red anywhere.
   *
   * Payload carries at least `{ sourceCardId, sourceBoardId,
   * sourceCardTitle }`. The title is denormalised into the payload on
   * `card.archived`'s precedent: the source may be renamed or deleted after
   * the copy exists, and the copy's own history should not depend on a join
   * to a row that may no longer say what it said at copy time.
   */
  'card.copied_from_card': { surfacesInFeed: true },
  /**
   * Two same-board cards were mechanically merged into a new consolidated card
   * — `FR-CARD-035`, B63.
   *
   * This is the final `0004` card placeholder to leave the queue above: the
   * live constraint already admits `card.merged`, and B63 is the first block
   * that emits it, so registration needs no migration. The row belongs to the
   * consolidated card: `cardId` is the new card, `boardId` is the board that
   * receives it, and both original cards ride in the payload as source
   * snapshots so activity phrasing and undo do not depend on live source rows.
   */
  'card.merged': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // ⚠⚠ CARD TEMPLATES — B37, `FR-TMPL-008` (mark a card as a template),
  // `FR-TMPL-012` (turn one back into a normal card) and `FR-TMPL-009` /
  // `FR-TMPL-010` (create a card from one), extended by B65 `FR-TMPL-011`
  // (hide or unhide one from everyday board flow).
  //
  // PLACED HERE, IMMEDIATELY AFTER `card.copied_from_card` AND STILL INSIDE
  // THE `card.*` RUN, on B35's own precedent one entry up: a `card.*` name is
  // grouped by NOUN in this registry, never by the block that first emits it
  // (B22 stated the rule, B31 restated it, B35 followed it, and this block is
  // the fourth). The adjacency is also the point rather than an accident —
  // the argument below is entirely about why the third of these names is NOT
  // the name directly above it, and a reader who has to page between two
  // sections to compare them will not compare them.
  //
  // NAMING: `<facet>_<participle>`, NOT a bare participle, on `0026`'s own
  // rule for the board-level pair and on the same split this registry already
  // draws for `card.` — bare participles for whole-card lifecycle
  // (`card.archived`, `card.unarchived`, `card.deleted`) and
  // `<facet>_<participle>` for a facet of the row (`card.position_changed`,
  // `card.field_overridden`). `cards.is_template` is a facet of the row and
  // not a lifecycle state — a template card is live, listed, readable and
  // editable like any other (see `FR-TMPL-008`'s own "Template cards occupy a
  // position in a list and are counted like other cards") — so the second
  // form wins. The toggle's pair is spelt SYMMETRICALLY,
  // `template_marked` / `template_unmarked`, on `list.archived` /
  // `list.unarchived`'s precedent and `board.template_marked` /
  // `board.template_unmarked`'s: a `marked`/`removed` pair would read as two
  // operations on two different things when it is one flag flipped twice.
  //
  // ─────────────────────────────────────────────────────────────────────
  // ⚠⚠ WHY `card.created_from_template` IS A NEW NAME AND NOT A PAYLOAD KEY
  // ON `card.copied_from_card`. B36 DID THE OPPOSITE ONE CONTAINER UP.
  // ─────────────────────────────────────────────────────────────────────
  // The mirror of B36 is the obvious move and it is the wrong one here.
  // `board.created` simply GAINED `createdFromTemplateBoardId` beside B35's
  // `copiedFromBoardId` (see that entry's own docstring), with no new name
  // and no migration line; the analogue would be
  // `createdFromTemplateCardId` on `card.copied_from_card` and a block that
  // ships no DDL at all. It was considered and rejected, for two reasons
  // that do not apply to boards.
  //
  //   1. ⚠⚠ THE KEY WOULD NEVER VARY FROM ITS OWN NEIGHBOUR. On
  //      `board.created` the two provenance keys are MUTUALLY EXCLUSIVE — an
  //      entry carries `copiedFromBoardId` OR `createdFromTemplateBoardId`,
  //      never both, and an ordinary create carries neither. That is exactly
  //      what makes each of them informative, and it is what B36's note above
  //      means by "a given row carries AT MOST ONE of the two".
  //      `card.copied_from_card`'s payload contract REQUIRES `sourceCardId`
  //      (see its docstring), so a `createdFromTemplateCardId` sitting beside
  //      it would be permanently EQUAL to it: a stored value that never
  //      varies, on every row the route could ever write. ⚠ **That is
  //      verbatim the objection B36 itself raised when it refused to put
  //      `previousIsTemplate` on `board.template_marked`** — "a stored value
  //      that never varies is dead weight on every row this pair can ever
  //      produce". A block may not cite that argument and violate it one file
  //      over.
  //
  //   2. THE REUSED NAME HAS TO BE TRUE OF BOTH PATHS, AND THIS ONE IS NOT.
  //      `board.created`'s sentence — "created this board" — is true whether
  //      the board was made from scratch, copied or instantiated, which is
  //      *why* a payload key could carry the whole distinction there.
  //      `card.copied_from_card` renders as "copied this card from {card}"
  //      (`packages/shared/src/activity.ts`), and `FR-TMPL-009`'s own words
  //      are *create a card from a template*, with the template left
  //      unchanged and the new card "a normal, independent card". Reusing the
  //      name would put a false sentence in the feed, and it would make
  //      "which cards came from this template" a payload-key scan where every
  //      other question of that shape in this product is a query on `type`.
  //
  // ⚠ MECHANICALLY THE SWAP WAS PERMITTED, and that is worth stating so the
  // rejection reads as a decision rather than as a compiler's. `EVENT_TYPES`
  // binds no payload shape to a name at the type level — `eventInputSchema`'s
  // `payload` is `z.record(z.string(), z.unknown())`, and the three keys
  // below are a DOCSTRING CONVENTION enforced only at the write site — so
  // nothing would have stopped `createdFromTemplateCardId` from being written
  // beside `sourceCardId` on every instantiated card. It was rejected on the
  // two arguments above, not because a type refused it.
  //
  // ⚠ THE PRICE OF THAT RULING, PAID IN ONE PLACE AND SILENT IF UNPAID:
  // `apps/server/src/search/refresh-index.ts` holds
  // `CARD_SEARCH_TRIGGER_EVENT_TYPES`, and `card.copied_from_card` is a
  // member. B36 got card indexing FOR FREE precisely because its instantiated
  // cards emit that name; a BRAND NEW name is not in that set, so
  // `card.created_from_template` is added to it in the same change. Omit it
  // and a card created from a template is INVISIBLE TO SEARCH until somebody
  // edits it — working over a reload, nothing red anywhere, no type error,
  // because that set is deliberately `ReadonlySet<string>` and would swallow
  // a typo just as quietly.
  //
  // ─────────────────────────────────────────────────────────────────────
  // THE PAYLOADS
  // ─────────────────────────────────────────────────────────────────────
  //   card.template_marked        { workspaceId, listId, title }
  //   card.template_unmarked      { workspaceId, listId, title }
  //   card.created_from_template  { workspaceId, listId, title,
  //                                 templateCardId, templateCardTitle }
  //
  // `{ workspaceId, listId, title }` is the shape every card-scoped event has
  // carried since B14, and neither key is decoration: `activity_entries` has
  // actor, board and card foreign keys and NO `workspace_id` and NO `list_id`
  // column (the trades `0012` and `0016` recorded), so a consumer holding
  // only the entry could not otherwise say where it happened.
  //
  // ⚠ NO `previousIsTemplate` ON THE PAIR, for B36's reason exactly: both
  // routes are IDEMPOTENT with NO event on the no-op (a conditional
  // `UPDATE … WHERE is_template = <the opposite>` that matches zero rows
  // writes nothing and emits nothing), so the value before a MARK row is
  // written is always `false` and before an UNMARK row always `true`.
  //
  // ⚠ `templateCardTitle` IS DENORMALISED DELIBERATELY, on the same
  // `card.archived` precedent `card.copied_from_card`'s `sourceCardTitle`
  // cites: the template may be renamed, un-templated or deleted after the
  // card exists — `FR-TMPL-009` makes the created card independent from the
  // moment it exists — and the created card's own history must not depend on
  // a join to a row that may no longer say what it said.
  //
  // SCOPE. All three carry `cardId` AND `boardId` on the COLUMNS, B12's rule
  // inherited whole, so all three satisfy `ck_activity_entries_scope` twice
  // over and `0027` adds no by-name exemption to it. ⚠ On
  // `card.created_from_template` the `cardId` column is the NEW card and
  // `payload.templateCardId` is the template, never the reverse — the
  // asymmetry `card.copied_from_card` states for `sourceCardId`, restated
  // here because it is the one that would be silently backwards.
  //
  // ⚠ ALL THREE `true`. A card template is not a hidden facet: `FR-TMPL-008`
  // puts a badge on the board face and `FR-TMPL-012` takes it off again, so
  // "who made this a template, and when" is a change to shared board content
  // that other members can observe and dispute — `board.template_marked`'s
  // footing one container up, and `card.created`'s for the third name. Note
  // what a `true` costs, because this registry has said it twice already and
  // both times a later reader had to discover it: a `true` here is FOUR
  // files, not one — `ACTIVITY_TEMPLATES` (`./activity`), `ACTIVITY_PHRASERS`
  // (`apps/server/src/activity/phrase.ts`), `activity.test.ts`'s
  // `EXPECTED_SLOTS` and `FR-COLLAB-018.spec.ts`'s `PAYLOADS` are all TOTAL
  // mapped types over `FeedEventName`, and each is a compile error until it
  // has this key. `CARD_FIELD_BY_EVENT_TYPE` (`./card-field`) is a fifth
  // place and is not a compile error but a failing test — all three names
  // are card-scoped by that map's own prefix rule, so all three are mapped
  // there.
  // ---------------------------------------------------------------------

  /**
   * A member flagged a card as a template — `FR-TMPL-008`,
   * `POST /cards/:id/template`.
   *
   * **New in `db/postgres/migrations/0027_card_template_activity.sql`, in
   * this spelling and every other** — measured one name at a time, dotted,
   * against the LIVE `ck_activity_entries_type` before that file was
   * written. 20 characters, inside `EVENT_TYPE_MAX_LENGTH`.
   *
   * `{ workspaceId, listId, title }`, the block comment's smallest shape, and
   * no `previousIsTemplate` — the route is idempotent with no event on the
   * no-op, so the value before this row is written is always `false`.
   *
   * `true`, on the block comment's footing. Idempotent at the write layer
   * too: a `POST` on a card that is already a template matches zero rows in
   * the conditional `UPDATE`, writes nothing and emits nothing, so this row
   * exists exactly once per card that is ever templated — never once per
   * retried click of a "Make template" action. `FR-TMPL-008`'s "must not
   * offer Make template again" is a statement about the MENU, not a 409.
   *
   * ⚠ **Nothing is forbidden on a template card and this event advertises
   * rather than restricts.** `FR-TMPL-008`'s "start and due dates and the
   * completion state are hidden on a template card" is a statement about
   * which SURFACE renders them — B36 settled the identical question for
   * template boards in as many words — so no date, completion or comment
   * route changes because this row exists, and a consumer must not infer a
   * refusal from it.
   */
  'card.template_marked': { surfacesInFeed: true },
  /**
   * A member removed a card's template flag — `FR-TMPL-012`,
   * `DELETE /cards/:id/template`.
   *
   * **New in `db/postgres/migrations/0027_card_template_activity.sql`.** 22
   * characters. `card.template_marked`'s precedent exactly, in both the
   * payload and the idempotence: a `DELETE` on a card that is not a template
   * matches zero rows, writes nothing and emits nothing.
   *
   * `true`, symmetrically with the entry above: the pair is one lifecycle
   * and a feed showing only half of it would read as though the card had
   * always been ordinary. `FR-TMPL-012`'s own behaviour text — the badge
   * disappears, the dates become editable again and the card stops appearing
   * in the picker — is a change other members can see on the board
   * face without opening anything, which is the same standing
   * `board.template_unmarked` and `list.unarchived` are registered on.
   */
  'card.template_unmarked': { surfacesInFeed: true },
  /**
   * A member hid a template card from its list — `FR-TMPL-011`,
   * `POST /cards/:id/template/hidden-from-list`.
   *
   * New in `db/postgres/migrations/0041_hidden_template_cards.sql`. 30
   * characters, inside `EVENT_TYPE_MAX_LENGTH`. Payload:
   * `{ workspaceId, listId, title }`, the same smallest card-scoped shape as
   * `card.template_marked`.
   *
   * `true`, on the template flag pair's footing: a card leaving the shared
   * board face is a change other board members can observe and dispute. The
   * row also drives live board-room fan-out; without a board-scoped event,
   * hiding would work only after reload.
   */
  'card.template_hidden_from_list': { surfacesInFeed: true },
  /**
   * A member made a hidden template card visible in its list again —
   * `FR-TMPL-011`, `DELETE /cards/:id/template/hidden-from-list`.
   *
   * New in `db/postgres/migrations/0041_hidden_template_cards.sql`. 32
   * characters, exactly the event-type column ceiling and therefore measured
   * explicitly. Same payload and scope as the hide event: `boardId` and
   * `cardId` on the columns, `{ workspaceId, listId, title }` in JSON.
   */
  'card.template_unhidden_from_list': { surfacesInFeed: true },
  /**
   * A card was created from a template card — `FR-TMPL-009` and
   * `FR-TMPL-010`, `POST /cards/:id/create-from-template`. Also the entry
   * `FR-CARD-031`'s card-back action produces, which is the same capability
   * reached from a second surface rather than a second implementation.
   *
   * **New in `db/postgres/migrations/0027_card_template_activity.sql`, in
   * this spelling and every other.** 26 characters, inside
   * `EVENT_TYPE_MAX_LENGTH` and four short of `checklist.item_converted_to_
   * card`'s standing ceiling of 32, so this file moves nothing.
   *
   * ⚠⚠ **NOT `card.copied_from_card` WITH AN EXTRA PAYLOAD KEY** — the
   * block comment above carries the two arguments in full, and this is the
   * entry a reader will arrive at holding B36's opposite precedent. The
   * short form: the key would be permanently equal to `sourceCardId`, which
   * is the objection B36 itself raised against `previousIsTemplate`; and
   * "copied this card from {card}" is a false sentence for an instantiation
   * that `FR-TMPL-009` calls creating a card.
   *
   * `true`. This is the created card's OWN creation entry — `card.created`'s
   * and `card.copied_from_card`'s argument applies whole: it is the first
   * line of that card's history and the entry that explains where it came
   * from. **No `card.created` accompanies it**, one creation one entry, and
   * no second row is written on the template: a template is left completely
   * untouched by being instantiated (`FR-TMPL-009`: "editing the template
   * afterwards does not update cards already created from it", and nothing
   * flows the other way either), so unlike `card.moved_between_boards` there
   * is no second audience with anything to be told.
   *
   * Payload: `{ workspaceId, listId, title, templateCardId,
   * templateCardTitle }`. `templateCardId` is the TEMPLATE and the `card_id`
   * column is the NEW card; `templateCardTitle` is the denormalised snapshot
   * the feed falls back to when the template has since been renamed, deleted
   * or is on a board this reader cannot see (`FR-COLLAB-018` AC3's shape,
   * reached through `hydrate.ts`'s ordinary viewer-scoped lookup).
   *
   * ⚠⚠ **It is added to `CARD_SEARCH_TRIGGER_EVENT_TYPES`
   * (`apps/server/src/search/refresh-index.ts`) in the same change, and that
   * is the single highest-risk consequence of not reusing
   * `card.copied_from_card`.** That set is what makes a newly created card
   * findable at all; a name absent from it leaves the card unsearchable
   * until its next edit, with a 201, a correct response body and nothing red
   * anywhere.
   *
   * ⚠⚠ **UNMAPPED IN `generate.ts` AND `auto-watch.ts` TODAY, AND THAT IS A
   * RECORDED GAP RATHER THAN A DECISION — the one open question this
   * registration leaves behind, written here because this entry is where a
   * consumer's obligations for a name are enumerated.** `card.created` IS
   * mapped in `apps/server/src/notifications/generate.ts`
   * (`watched_card_created_on_board`, forking to
   * `watched_card_created_in_list` for a list watcher, `FR-COLLAB-023`), and
   * this name is not — so a member watching a list hears about a card typed
   * into the composer and **not** about one created from a template through
   * the *same composer* (`FR-TMPL-009`: "a 'Create from template' entry in
   * the add-card composer at the bottom of any list"). That reads like an
   * oversight and is not one; it is simply undecided here.
   *
   * The argument FOR mapping it is `FR-COLLAB-023`'s own text — a card
   * appeared in the list somebody subscribed to, and the requirement draws no
   * distinction between the ways one can arrive. The argument that does NOT
   * carry over is B35's: `card.copied_from_card` is unmapped because one
   * board copy emits it once per card and would notify every watcher 200
   * times for one gesture, and this route creates exactly one card per
   * request at a member's keystroke. ⚠ Whoever closes it should know the
   * price up front, because it is the reason this block did not: a
   * twenty-second key moves three assertions in
   * `apps/server/test/unit/FR-NOTIF-001.spec.ts` — the map's key and rule
   * counts, `EXPECTED_WATCH`, and "card.created is the ONLY rule whose type
   * depends on the recipient", which stops being true the moment a second
   * rule carries `notificationByScope`. `auto-watch.ts` needs nothing either
   * way: it maps neither `card.created` nor this name, so creating a card
   * from a template subscribes its creator exactly as much as `POST /cards`
   * does, which is not at all.
   *
   * ⚠ **The flag pair above is a different case and is settled, not open:**
   * `card.template_marked` and `card.template_unmarked` notify nobody and
   * subscribe nobody. No requirement in this family names a notification,
   * `FR-NOTIF-*` has no type for one, and a member tidying their own list
   * would otherwise put a line in every board watcher's inbox.
   *
   * ⚠ **`payload.templateCardId` is a key `ACTIVITY_PAYLOAD_ID_KEYS`
   * (`apps/server/src/activity/phrase.ts`) must know about**, or the
   * `{card}` slot in this name's sentence falls to its placeholder and the
   * feed says "created this card from a card" for ever. That map is keyed by
   * payload KEY NAME rather than by event type, so the key had to be a new
   * one there too — `sourceCardId` is `card.copied_from_card`'s and reusing
   * the spelling would have been the same conflation this entry exists to
   * refuse.
   */
  'card.created_from_template': { surfacesInFeed: true },
  /**
   * A link card was created from a pasted URL — B64, `FR-TMPL-027`.
   *
   * New in `db/postgres/migrations/0040_mirror_and_link_card_activity.sql`. The row is
   * the link card's own creation entry, so it carries `boardId` and `cardId`
   * just like `card.created`; the payload carries the destination `listId`,
   * `workspaceId`, stored title and target URL. It is deliberately a distinct
   * event from `card.created` so activity/search consumers can tell a normal
   * composer title from a URL paste without inspecting payload shape.
   */
  'card.link_card_created': { surfacesInFeed: true },
  /**
   * A link card was converted into an ordinary card — B64, `FR-TMPL-027`.
   *
   * New in `db/postgres/migrations/0040_mirror_and_link_card_activity.sql`. Conversion is
   * a visible card-state change and keeps the same `cards` row, short link,
   * number, list and position; the payload snapshots the target URL and the
   * title installed by the conversion.
   */
  'card.link_card_converted': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // The card back — B14, `FR-CARD-003` (write a rich-text description),
  // `FR-CARD-007` (join or leave a card) and `FR-CARD-008` (assign other
  // members to a card).
  //
  // `FR-CARD-001` (open and close the card back) and `FR-CARD-002` (edit the
  // card title) are also B14's and register nothing. The first is a **read** —
  // `GET /cards/:id` writes no row, opens no transaction and emits nothing, on
  // `GET /boards/:id/contents`'s exact footing. The second patches a column
  // B12 already patches: `PATCH /cards/:id/title` emits `card.renamed`, B14
  // built no second route, and `card.renamed`'s docstring above records the
  // correction to the prediction B12 left there.
  //
  // (a) ALL FIVE WERE ALREADY IN `ck_activity_entries_type`, SO **B14 SHIPS NO
  // MIGRATION** AND THE POSTGRES-ONLY `activity_entries` CHECK-CHANGE COUNT
  // STAYS AT SIX (`0011`–`0016`) FOR A NINTH SHIPPED BLOCK. Verified in
  // `0013_board_activity.sql` and again in `0016_list_activity.sql`, which is
  // the constraint's latest restatement: `card.description_edited`,
  // `member.added`, `member.removed`, `member.self_joined` and
  // `member.self_left` are all `0004_collaboration_and_activity.sql`
  // placeholders (snake_case there, dotted by `0013`'s mechanical
  // first-underscore-to-dot rename), unemitted by anything until now. B09 hit
  // this case once out of two names, B11 four times out of six, B12 four out
  // of four, and B14 five out of five.
  //
  // ⚠ **B14's reason for shipping no DDL is B12's reason, NOT B13's, and the
  // two must not be read as one precedent.** B12 needed none because the names
  // it emits already existed; B13 needed none because it **emits nothing at
  // all**. B14 is squarely the first case — it emits five events and adds no
  // name — and a later block reasoning by analogy from the wrong one will
  // either skip a migration it needs (if it reads B13's case as "reads and
  // writes alike need no DDL") or write one it does not (if it reads B12's as
  // "only reads escape"). `CLAUDE.md` records the same pair from the other
  // side, under "eight blocks, six CHECK changes".
  //
  // ⚠ **THE "LATER BLOCK" ARRIVED AND IT IS NEITHER CASE — corrected in
  // place, because this paragraph asked the question and B15 is the answer.**
  // B15 (labels) emits SIX names of which TWO already existed and FOUR did
  // not, so it needed `0017_label_activity.sql` for half its vocabulary:
  // a *partial* case, which is B09's shape rather than B12's, B13's or
  // B14's. Reasoning by analogy from any one of the three gets it wrong in
  // one direction or the other, exactly as this paragraph predicted. The
  // rule that survives all four: grep each name individually against the
  // live constraint. See the B15 block at the foot of this registry.
  //
  // Verify rather than believe — one command, and this comment is exactly the
  // kind of claim `CLAUDE.md` warns outlives its condition:
  //   docker compose exec -T postgres psql -U molt -d molt_v1 \
  //     -c "\d+ activity_entries"
  // Match the DOTTED spelling; the pre-0013 snake_case forms
  // (`card_description_edited`, `member_self_joined`, …) still appear in 0004,
  // 0011 and 0012 as historical constraint bodies, not as the live vocabulary.
  //
  // (b) ⚠ THE FOUR `member.*` NAMES ARE **CARD** MEMBERSHIP AND ARE NOT
  // `board.member_added` / `board.member_removed`. The B10 block above exists
  // to stop that confusion and was written before either side was live; it is
  // now live on both sides, which is the state it warned about. This registry
  // is keyed by the full dotted string, so `member.added` and
  // `board.member_added` are two distinct rows in one CHECK constraint, and a
  // call site that reaches for the short name to record a *board* membership
  // change writes a plausible row that B28 will render as somebody joining a
  // *card*. Nothing fails, in either direction.
  //
  // (c) TWO ROUTES, FOUR GESTURES, AND THE EVENT IS CHOSEN BY COMPARING THE
  // TARGET AGAINST THE ACTOR.
  //     POST   /cards/:id/members          userId === actor  → member.self_joined
  //     POST   /cards/:id/members          userId !== actor  → member.added
  //     DELETE /cards/:id/members/:userId  userId === actor  → member.self_left
  //     DELETE /cards/:id/members/:userId  userId !== actor  → member.removed
  //
  // Four names for two routes, which is the inverse of the shape `0004` might
  // suggest and is deliberate. It is `card.position_changed` versus
  // `card.moved_between_lists` again — one route, two names, because the FEED
  // must render them differently and a reader cannot recover which happened
  // from one name plus a payload without switching on `payload`, which is the
  // thing this registry exists to stop. "Ada joined this card" and "Ada added
  // Bea to this card" are different sentences: the first is a person taking
  // work, the second is a person being given it, and `FR-CARD-007` and
  // `FR-CARD-008` are two requirements for exactly that reason. The
  // notification consumer makes the split load-bearing rather than cosmetic —
  // being added by somebody else is a thing you are told about, and joining a
  // card yourself is not.
  //
  // The route does NOT need a separate `POST /cards/:id/join`; that would be
  // this route with the id filled in from the session, two paths writing one
  // row, and the "am I adding myself?" comparison would still have to exist on
  // the other one.
  //
  // (d) ⚠ **A NO-OP EMITS NOTHING**, and it must be detected BEFORE the write.
  // Adding somebody already on the card, or removing somebody who is not on
  // it, is a legal and idempotent request — `FR-CARD-008` AC3 makes the
  // popover a toggle, and two clients toggling at once produce exactly this —
  // but it is not a mutation, and SC-1 records mutations. `INSERT … ON
  // CONFLICT DO NOTHING` followed by an unconditional `emit()` passes every
  // visible assertion (200, correct member list, no duplicate row) while
  // filling the card's activity with phantom joins. This is `FR-BOARD-025`'s
  // drop-in-place edge case in a second costume, and this repository treats
  // that lesson as load-bearing: the route reads the membership inside the
  // transaction and writes nothing at all when it already matches.
  //
  // ⚠ It does **not** extend to the title and the description. No requirement
  // asks for `PATCH /cards/:id/description` to suppress an event when the text
  // is unchanged, `FR-CARD-003`'s Save is an explicit gesture rather than a
  // toggle, and "same string" is not the same claim as "same membership" —
  // last-write-wins (the block note, and `B31` for the collision handling)
  // means a save that changes nothing still settles who wrote last.
  //
  // (e) SCOPE. All five carry `cardId` **and** `boardId` on the COLUMNS, which
  // is B12's rule inherited whole and not re-decided: `activity_entries` has a
  // real `card_id` column (`ck_activity_entries_scope`'s first disjunct
  // alongside `board_id`), `FR-COLLAB-020`'s "each entry naming its card" is a
  // join B28 cannot make from a JSONB key without a scan, and `publishEvents`
  // filters on `boardId !== null` before fanning out to
  // `boardChannel(boardId)` — which is what `FR-CARD-008` AC2 needs ("their
  // avatar appears **immediately for all viewers of the board**"). The list id
  // travels in `payload` for the mechanical reason `0016` recorded: there is no
  // `list_id` column. So all five satisfy the scope constraint twice over and
  // no by-name exemption exists or is needed — there is no migration in which
  // to add one.
  //
  // (f) FEED. **All five `true`, and the authority is not uniform across them
  // — read each entry rather than inferring symmetry.** The pair that rests on
  // a requirement's own sentence is `member.self_joined` / `member.self_left`
  // (`FR-CARD-007` AC4, verbatim); the other three are judgements with their
  // arguments in their docstrings.
  //
  // ⚠ THE CONTRAST A READER WILL TRIP ON, STATED HERE SO IT IS NOT
  // "HARMONISED": B10's `board.member_added` and `board.member_removed` are
  // **`false`**, and these card-membership events are **`true`**. That is not
  // an inconsistency, and the difference is not the word "member" — it is the
  // authority behind each. `FR-PERM-018` and `FR-PERM-029` name no feed
  // anywhere in their criteria or edge cases (B10's entries work through that
  // at length, including why `FR-PERM-025` AC4's "any setting" does not reach a
  // membership change), whereas `FR-CARD-007` AC4 says in as many words that
  // joining and leaving "appear in the card's activity". A board membership is
  // also a *permission* fact, whose natural surface is the member list rather
  // than the feed; a card membership is *content*, sitting beside the labels
  // and the due date on the thing the feed is about. A block that wants to
  // revisit either side should revisit that side's three together — B10's
  // entries already say so of theirs.
  // ---------------------------------------------------------------------

  /**
   * A card's description was written, changed or cleared — `FR-CARD-003`,
   * `PATCH /cards/:id/description`.
   *
   * `true`, and it is a judgement rather than a requirement: `FR-CARD-003`'s
   * acceptance criteria are about the editor (markdown conversion, the draft,
   * Cancel, the cap, the card-front indicator) and none of them names the
   * activity feed. It surfaces on `FR-COLLAB-020` — the board feed shows
   * "entries from all cards and lists on the board, each entry naming its
   * card", a clause that names cards explicitly and therefore admits this
   * under the strict reading and the loose one alike — and on the practical
   * argument `card.renamed` is registered under: a feed that showed a card
   * being created, renamed and moved but not *rewritten* could not answer "who
   * changed what this card says", which is the question the description
   * exists to hold the answer to.
   *
   * SC-1 would require the row regardless of the flag. This is a change to
   * **shared** card content that every board member reads, and Phase 1 is
   * explicitly last-write-wins within the field (`FR-COLLAB-026`'s collision
   * handling is `B31`'s, a known and accepted gap), which makes "who wrote
   * last, and when" the only record of a lost edit there is.
   *
   * ⚠ **The payload must not carry the description itself, and this is the one
   * caution worth spending a paragraph on.** `activity_entries.payload` is
   * JSONB on an append-only table nothing prunes: a 16 KB description written
   * forty times is 640 KB of history for one card, and a *public* board
   * (`FR-PERM-010`) makes that history readable by anyone with the link, which
   * is a disclosure of text a member may since have deleted. The previous and
   * new **lengths**, or a boolean "cleared", are what `B28` needs to render
   * "updated the description" — the same restraint `board.visibility_changed`
   * and `list.position_changed` exercise when they carry before/after values
   * that are two short strings rather than two documents.
   *
   * ⚠ **There is no `card.description_cleared`, and clearing emits this
   * name.** `cards.description` is nullable and the route collapses an
   * empty-after-trim submission to `NULL` (see `cardDescriptionPatchSchema` in
   * `./card-detail`), so "cleared" is a value of this event rather than an
   * event of its own — the same call `list.unarchived` records against a third
   * spelling for restore.
   *
   * ⚠ **Nothing here records a draft.** `card_description_drafts` is a real
   * table (`0003_card_detail.sql`) that B14 does not write, and a draft is not
   * a mutation of shared state — it is per-viewer text `FR-CARD-003` AC2 wants
   * to survive a card close. If a later block persists drafts server-side it
   * must **not** emit into this stream: a keystroke-rate event on an
   * append-only table is a different animal from an explicit Save.
   *
   * A placeholder since `0004`, so no migration was needed for it.
   */
  'card.description_edited': { surfacesInFeed: true },
  /**
   * Somebody put **another person** on a card — `FR-CARD-008`,
   * `POST /cards/:id/members` where the target is not the caller.
   *
   * ⚠ **Card membership, not board membership.** `board.member_added` is
   * B10's, is a different row in the same CHECK constraint, and is `false`
   * where this is `true` — see (b) and (f) in the block comment above for both
   * halves of that.
   *
   * `true`, and a judgement: `FR-CARD-008`'s criteria are the popover's search
   * filter, the avatar appearing immediately for all viewers, the toggle-off,
   * and a non-board-member not being selectable — no criterion and no edge case
   * says "activity". It surfaces on two grounds. `FR-COLLAB-020` is the first,
   * as for every card event above it. The second is symmetry with
   * `member.self_joined` below, which surfaces **by `FR-CARD-007` AC4's own
   * sentence** — and unlike B09's half-story objection, which argued *against*
   * surfacing one membership event while its siblings were unbuilt, all four
   * card-membership events land in the same block and the same commit, so the
   * feed either shows the whole of card membership or none of it. Showing the
   * self pair and hiding the assign pair would be the half story: a card whose
   * avatars appeared without explanation while joins and leaves were narrated.
   *
   * The payload carries the target's user id. The **actor** does not go in the
   * payload — `actorUserId` is a column, and `FR-CARD-008`'s feed sentence is
   * "X added Y", which needs both and gets them from two different places.
   *
   * ⚠ Adding somebody already on the card writes nothing at all — see (d).
   *
   * A placeholder since `0004`, so no migration was needed for it.
   */
  'member.added': { surfacesInFeed: true },
  /**
   * Somebody took **another person** off a card — `FR-CARD-008`,
   * `DELETE /cards/:id/members/:userId` where the target is not the caller.
   *
   * `true`, on `member.added`'s footing exactly and not repeated: the pair is
   * one gesture in two directions (`FR-CARD-008` AC3 makes the popover a
   * toggle, so the same click that added a member removes them), and a feed
   * showing only the additions would read as though nobody ever came off a
   * card.
   *
   * ⚠ **Not `board.member_removed`**, which is B10's and is `false`.
   *
   * ⚠ **This is not `FR-PERM-029`'s cascade.** Removing somebody from a
   * *board* unassigns them from that board's cards ("card unassignment across
   * five cards" is one of `FR-PERM-029`'s criteria), and the block that builds
   * that cascade will have to decide whether it emits one of these per card, a
   * single `board.member_removed`, or both. B14 does not build it and does not
   * decide it; what B14 fixes is the meaning of this name, so that whoever
   * builds the cascade knows what they would be claiming by emitting it.
   *
   * ⚠ Removing somebody who is not on the card writes nothing at all — see (d).
   *
   * A placeholder since `0004`, so no migration was needed for it.
   */
  'member.removed': { surfacesInFeed: true },
  /**
   * Somebody joined a card themselves — `FR-CARD-007`'s one-click Join,
   * `POST /cards/:id/members` where the target **is** the caller.
   *
   * `true` **by the requirement rather than by judgement**, and it is one of
   * only two entries in this block on that footing, so cite this rather than
   * the block comment when the question comes up. `FR-CARD-007` AC4: "Given a
   * user joins and then leaves, when the activity section is expanded, then
   * **both events appear in the card's activity**." That names the surface and
   * both directions in one sentence — a stronger footing than `board.renamed`'s
   * "recorded in board activity", because it also fixes that the *pair* must be
   * visible, which is why `member.self_left` cannot be quietly suppressed as
   * noise later.
   *
   * Note the surface AC4 names: the **card's** activity, not the board's.
   * `activity_entries.card_id` is what makes that query possible without a
   * JSONB scan — see (e) — and B28 owns the route that runs it. This registry's
   * `surfacesInFeed` is one boolean for both surfaces; if a later block needs
   * an entry that appears on a card and not on its board, that is a second
   * discriminator and a migration, not a re-reading of this flag.
   *
   * `FR-CARD-007`'s other half — "Joining a card subscribes the user to that
   * card's notifications by default" — is `09-`'s and B29's, and it is a
   * **consumer** of this event rather than a second event: the subscription is
   * derived from membership, and `FR-CARD-007` is explicit that leaving "does
   * not automatically unsubscribe a user who subscribed explicitly", which is a
   * rule about a table this block does not write.
   *
   * ⚠ **Observers.** `FR-CARD-007`'s edge case says "Observers and read-only
   * members cannot join cards", and **B14 does not enforce it** — the route
   * uses `requireCardWriter`, which admits an observer exactly as
   * `requireBoardWriter` does. That is `FR-PERM-016`, V1, owned by `BE-38.1`,
   * whose exclusions are a bundle that must land together; `CLAUDE.md` states
   * it plainly and says "Do not 'fix' one to match the other". So this event
   * can currently be emitted by an observer, and that is a recorded gap rather
   * than a defect in this block.
   *
   * A placeholder since `0004`, so no migration was needed for it.
   */
  'member.self_joined': { surfacesInFeed: true },
  /**
   * Somebody left a card themselves — `FR-CARD-007`'s Leave,
   * `DELETE /cards/:id/members/:userId` where the target **is** the caller.
   *
   * `true` **by the requirement**, on `member.self_joined`'s exact footing:
   * `FR-CARD-007` AC4 requires *both* events to appear when a user joins and
   * then leaves, so this one is named by the same sentence and cannot be
   * dropped without failing it. The pair is one lifecycle, which is
   * `list.archived` / `list.unarchived`'s argument arriving at the same answer
   * from the other direction — here the requirement settles what symmetry only
   * suggested there.
   *
   * ⚠ **There is no `member.left`, and no `member.self_removed`.** `0004` named
   * this one, and one spelling per fact is the rule `list.unarchived` records
   * against a third name for restore.
   *
   * ⚠ Leaving a card the caller was not on writes nothing at all — see (d).
   *
   * A placeholder since `0004`, so no migration was needed for it.
   */
  'member.self_left': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Votes — BE-68.8, `FR-AUTO-003` ("a card is voted on").
  //
  // Until this block, a vote wrote no SC-1 activity entry: the trigger could
  // be authored and stored but never fired, which is why `card_voted_on` sat
  // in `AUTOMATION_UNSUPPORTED_TRIGGER_KINDS` with `no_activity_event`. This
  // name is the entry the vote route emits (Slice B), and the candidate the
  // automation matcher derives (Slice C).
  //
  // NOT a `0004` placeholder — no vote name exists in the constraint in any
  // spelling (live check in `0058_card_voted_on_activity.sql`'s header) — so
  // admission and registration land together, B36/B37's shape.
  // ---------------------------------------------------------------------

  /**
   * Somebody voted on a card — `FR-AUTO-003`, `PUT /cards/:id/vote`.
   *
   * 13 characters, inside `EVENT_TYPE_MAX_LENGTH`.
   *
   * Columns carry the fact: `board_id` (so `publishEvents` fans it out to the
   * board room) and `card_id` (so the card feed can query it without a JSONB
   * scan), `actorUserId` for "X voted". The payload carries no text — the
   * standing rule (deletion is permanent; public-board history is
   * link-readable) — and no tally: the count is derived state the route reads
   * from `card_votes`, not a fact this entry owns. Repeat-vote and unvote
   * semantics are Slice B's decision; this registration constrains neither.
   *
   * `true`, on `member.added` / `label.added`'s footing: a card-scoped member
   * gesture on `FR-COLLAB-020`'s loose reading (the board's feed shows
   * board-level events too, so it shows card-level ones), symmetric with the
   * membership pair above. Repeat votes by one actor collapse by byte-identity
   * under `collapse.ts`'s generic rule — no per-type list exists to update.
   */
  'card.voted_on': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // PR-list approvals (owner-directed, no FR- requirement; admitted by
  // `0071_card_approvals.sql`, five names, and `0072_card_approvals_reset.sql`,
  // a sixth; none in the constraint in any spelling beforehand - checked per
  // name, dotted, against the live CHECK).
  //
  // All six carry `board_id` on the COLUMN (`publishEvents` fans out on it
  // alone); the five card events also carry `card_id`. ⚠ THE REASON TEXT IS
  // NEVER IN ANY PAYLOAD, on `comment.added`'s rule: user content is read from
  // the card (`card_approvals.reason`), not from the event.
  // ---------------------------------------------------------------------

  /**
   * A member approved a card in the PR list - created an `approved` verdict,
   * or changed an existing verdict to `approved`, or changed the reason of an
   * existing `approved` one. An identical repeat PUT emits nothing.
   * Payload: `{ workspaceId, listId, verdict, approvedCount, rejectedCount,
   * requiredApprovals }`. `true`, on `card.voted_on`'s footing.
   */
  'card.approved': { surfacesInFeed: true },

  /** The same, for a `rejected` verdict. Same payload. */
  'card.rejected': { surfacesInFeed: true },

  /**
   * A member withdrew their own verdict (a DELETE that actually removed a
   * row). Payload: `{ workspaceId, listId, approvedCount, rejectedCount,
   * requiredApprovals }`.
   */
  'card.approval_withdrawn': { surfacesInFeed: true },

  /**
   * The card's approvals reached the board's required number - emitted ONCE
   * per false-to-true transition of "met", in the transaction that caused it,
   * by the actor whose action caused it (an approving member, or the admin who
   * lowered the requirement). Payload: `{ workspaceId, listId, approvedCount,
   * rejectedCount, requiredApprovals }`.
   */
  'card.approvals_met': { surfacesInFeed: true },

  /**
   * A card writer cleared EVERY member's verdict on one card at once, because
   * the card needs more work and must be revalidated (`POST
   * /cards/:id/approvals/reset`; admitted by `0072_card_approvals_reset.sql`).
   * Emitted once, and only when at least one row was removed; a repeat reset
   * emits nothing. Payload: `{ workspaceId, listId, removedApproved,
   * removedRejected, requiredApprovals }` - the counts of the rows the DELETE
   * actually returned. Approving again across N records a fresh `card.approvals_met`.
   */
  'card.approvals_reset': { surfacesInFeed: true },

  /**
   * The board's PR list designation changed. `board_id` on the column, no
   * `card_id`. Payload: `{ workspaceId, listId, isPrList, previousPrListId }`.
   * `true`, on `list.recoloured`'s footing: shared board-visible state.
   */
  'list.pr_list_changed': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Labels — B15, `FR-CARD-009` (apply a label to a card and take it off
  // again, BE-15.2) and `FR-CARD-010` (manage the board's label set:
  // create, rename, recolour, delete — BE-15.1).
  //
  // (a) ⚠ **TWO OF THESE SIX WERE ALREADY IN `ck_activity_entries_type`
  // AND FOUR WERE NOT, SO B15 IS THE FIRST *PARTIAL* CASE SINCE B09** —
  // and `CLAUDE.md`'s standing warning that "a tenth block must not reason
  // by analogy from whichever of the three it happened to read first" is
  // about exactly this block. The three cases it enumerates are B12's
  // ("every name I emit already exists" — no DDL), B13's ("I emit nothing
  // at all" — no DDL) and B14's (B12's again). **None of them is B15's.**
  // `label.added` and `label.removed` are `0004` placeholders, dotted by
  // `0013`; `label.created`, `label.renamed`, `label.recoloured` and
  // `label.deleted` exist in the constraint in no spelling and are added by
  // `db/postgres/migrations/0017_label_activity.sql` — sixty-one names to
  // sixty-five, and the Postgres-only CHECK-change count from six to seven.
  //
  // The rule that survives all four cases, and the only one worth
  // carrying: **grep each name individually against the LIVE constraint.**
  // "The names already exist" is one query and is false for any name a
  // block invents; "there are no names" is a property of the block itself.
  // Assume the first wrongly and a write hits `ck_activity_entries_type` at
  // runtime; assume the second wrongly and you write a migration adding a
  // member the constraint already has.
  //
  // ⚠ **"THE FIRST *PARTIAL* CASE SINCE B09" IS FALSE AND IS CORRECTED HERE
  // RATHER THAN DELETED, BECAUSE THE WAY IT IS WRONG IS THE USEFUL PART.**
  // It overlooked **B11**, which needed `0016` for two of its six names, and
  // it overlooked **B08**, whose `board.deleted` was the one name `0004`'s
  // placeholder set did not contain (see that entry's own docstring; `0013`
  // added it). Counted against the entries in this registry rather than
  // against any comment, the partial blocks are **B08, B09, B11, B15 and
  // B16** — which is MOST of the blocks that needed DDL at all. The unusual
  // shapes are the other three: all-new (B06, B07, B10), all-existing (B12,
  // B14) and none-at-all (B13, a read).
  //
  // So **partial is the normal case and no ordinal should be claimed for
  // it** — this paragraph's own error is the evidence, and it is the fourth
  // arithmetic correction this file carries. What does not change is the
  // rule above: grep each name INDIVIDUALLY. B16 (comments) is the block
  // that made the miscount visible, and it also breaks the *prefix grep*
  // twice over in a way none of its predecessors did — its two `comment.`
  // hits are BOTH genuinely B16's and both correct (so "are all these hits
  // mine?" answers yes and is the wrong question), and the prefix cannot see
  // `board.permission_changed`, which is B16's fourth event. Read the B16
  // block at the foot of this registry before reasoning by analogy from
  // this one.
  //
  //   docker compose exec -T postgres psql -U molt -d molt_v1 -c \
  //     "SELECT m[1] FROM pg_constraint c,
  //        regexp_matches(pg_get_constraintdef(c.oid), '''([a-z._]+)''', 'g') AS m
  //      WHERE c.conrelid='activity_entries'::regclass
  //        AND c.conname='ck_activity_entries_type' AND m[1] LIKE 'label%'"
  //
  // Match the DOTTED spelling; `label_added` / `label_removed` still appear
  // in 0004, 0011 and 0012 as historical constraint bodies.
  //
  // (b) ⚠ **`label.added` AND `label.removed` ARE CARD-SCOPED AND ARE NOT
  // BOARD-LEVEL NAMES. THERE IS NO `board.label_added`.** This is `0015`'s
  // `member.added` / `board.member_added` collision in a fourth costume,
  // and it is the one a future block is most likely to walk into: a block
  // that wants "a label was added to the board" must NOT reach for
  // `label.added`, which means "an existing label was put on a card". The
  // board-level creation event is `label.created`. Two requirements share
  // this noun — `FR-CARD-009` owns the card side, `FR-CARD-010` the set —
  // and this registry is keyed by the full dotted string, so a mistyped
  // `label.added` at a set-management call site writes a plausible row that
  // B28 will render as somebody labelling a *card*, with nothing failing.
  // `0017`'s header carries the same warning from the SQL side and names
  // what makes this shape different from `0015`'s and `0016`'s.
  //
  // (c) SCOPE, AND IT IS NOT UNIFORM ACROSS THE SIX.
  //     label.added / label.removed      → cardId AND boardId, both COLUMNS
  //     the other four                   → boardId on the column, cardId NULL
  //
  // The two card-scoped names follow B12's rule inherited whole:
  // `activity_entries` has a real `card_id` column, `FR-COLLAB-020`
  // describes the board feed as "entries from all cards and lists on the
  // board, **each entry naming its card**", and that is a join B28 cannot
  // make from a JSONB key without a scan. The four board-level names carry
  // `cardId: null` because a label belongs to a board and to no card —
  // `label.deleted` in particular touches N cards through
  // `fk_card_labels_label_id`'s ON DELETE CASCADE, and naming any one of
  // them would be a lie. All six satisfy `ck_activity_entries_scope`
  // because its **second disjunct is `board_id IS NOT NULL` standing
  // alone**, conjoined with nothing about `card_id`, so `0017` adds no
  // by-name exemption and does not touch that constraint at all.
  //
  // ⚠ **The label id ALWAYS travels in `payload`.** `activity_entries` has
  // no `label_id` column and no foreign key to `labels` — its scoping
  // columns are `card_id`, `board_id` and `actor_user_id`, verified against
  // the live catalogue — which is the same trade `0012` recorded for the
  // workspace id and `0016` for the list id. A pleasant side effect worth
  // knowing: because there is no FK, deleting a label touches not one
  // activity entry, which is why D6's hard delete costs no history and why
  // `labels` needs no `deleted_at` (there is no `isLiveLabel()` anywhere in
  // the server, and `apps/server/src/labels/permissions.ts` says so).
  //
  // `workspaceId` is in every payload for the same mechanical reason, and
  // `listId` rides on the two card-scoped names on B14's block rule —
  // `FR-CARD-007` AC4's card activity section reads one card at a time and
  // would otherwise need a lookup per entry.
  //
  // (d) PAYLOAD SHAPE: **`board.renamed`'s precedent, not
  // `card.description_edited`'s** — the text itself, not its length.
  // `card.description_edited` deliberately omits the description because
  // `cards.description` is unbounded `text`, a card back holds up to 16 KB,
  // `activity_entries.payload` is JSONB on an append-only table nothing
  // prunes, and on a **public** board that history is readable by anyone
  // with the link. None of that reaches a `varchar(255)` label name: forty
  // renames is at most 10 KB of history for a whole board, and a label name
  // is a shared vocabulary term rather than authored prose. So names and
  // colours travel as text, before and after, on
  // `board.visibility_changed`'s and `list.position_changed`'s shape.
  //
  // (e) FEED. **All six `true`, and NOT ONE OF THEM BY A REQUIREMENT'S OWN
  // SENTENCE.** Verified before writing: neither `FR-CARD-009` nor
  // `FR-CARD-010` contains the string "activity" or "feed" anywhere — not
  // in behaviour, not in an acceptance criterion, not in an edge case. So
  // unlike `board.renamed`, `list.renamed`, `card.moved_between_lists`,
  // `member.self_joined` and `member.self_left`, every entry below is a
  // judgement and carries its own argument. **Do not cite this block
  // comment for any of the six; cite the entry.**
  //
  // (f) `label.*` NAMES IN THE CONSTRAINT THAT ARE NOT REGISTERED HERE:
  // **none.** A grep of `label.` against the live constraint returns six
  // and this registry declares six, which is the first exact match in this
  // file — `list.` returns seven against six, `card.` twelve against five.
  // Said out loud so a reader does not go looking for a placeholder that
  // is not there. The name a later block will want and must add itself is
  // `label.position_changed`: `labels.position` is a real column, B15 only
  // ever appends, and `FR-CARD-010` describes no reorder gesture, so
  // `0013`'s rule keeps it unwritten until something emits it.
  // ---------------------------------------------------------------------

  /**
   * A label was added to a board's set — `FR-CARD-010`,
   * `POST /boards/:id/labels`.
   *
   * **New in `0017_label_activity.sql`**, in this spelling and every other:
   * `0004` enumerated `label_added` and `label_removed` — the *card* side —
   * and no set-management equivalent. 13 characters. The name mirrors
   * `board.created` and `list.created`, so the three levels of "this thing
   * came into existence" read alike; it is not a reuse of either, because
   * this registry is keyed by the full dotted string.
   *
   * ⚠ **This is emphatically not `label.added`**, which is four lines up in
   * the CARD block, means "an existing label was put on a card", and is
   * `FR-CARD-009`'s. See (b) — the pair is the sharpest name collision in
   * this file since `member.added` / `board.member_added`.
   *
   * `true`, and a judgement. It rests on the **loose** reading of
   * `FR-COLLAB-020` that `board.member_added`'s docstring says this registry
   * "has consistently read … as 'the board's activity feed shows
   * board-level events too'" — a label is named nowhere in that sentence's
   * card/list wording — plus `FR-PERM-025` AC4: "given a board admin changes
   * any **setting**, when the board activity feed is read, then the change is
   * recorded". `board.member_added` worked through why "any setting" does
   * **not** reach a *membership* change; it does reach the board's label
   * vocabulary, and the requirement's own title is the evidence —
   * `FR-CARD-010` is "Manage the board's label **set**", edited from a
   * popover in the board menu beside the board's other settings.
   *
   * B09's half-story objection points the *other* way here and is worth
   * stating because it is the same argument reaching the opposite answer:
   * all four board-level label events land in the same block and the same
   * commit, so the feed shows the whole of label-set management or none of
   * it. There is no state in which a label appears to be renamed without the
   * feed ever having shown it created.
   *
   * The payload carries the label id, the name and the colour, on
   * `board.created`'s footing: `label.renamed` records the *previous* name
   * and the chain has to start somewhere.
   *
   * ⚠ **The six labels `POST /boards` seeds do NOT emit this.** They are
   * part of what `board.created` already describes, exactly as the creator's
   * admin `board_members` row is, and six entries at the head of every
   * board's feed would attribute to the creator a vocabulary they did not
   * choose — for ever, on an append-only table with no delete path. A
   * handler that emits them passes 201, the right six rows and every shape
   * assertion. See `DEFAULT_BOARD_LABEL_COLOURS` in `./label`.
   */
  'label.created': { surfacesInFeed: true },
  /**
   * A label in the board's set was renamed — `FR-CARD-010`,
   * `PATCH /labels/:id/name`.
   *
   * **New in `0017_label_activity.sql`.** 13 characters. Mirrors
   * `board.renamed`, `list.renamed` and `card.renamed`, the same one-field
   * edit three levels up.
   *
   * `true`, on `label.created`'s footing exactly and not repeated. ⚠ Note
   * that this is **not** the by-the-requirement footing `board.renamed` and
   * `list.renamed` stand on: both of those have a behaviour sentence saying
   * the rename is "recorded in board activity", and `FR-CARD-010` has no
   * such sentence anywhere. The proximity of the names is the trap — a
   * reader who assumes this entry inherits `board.renamed`'s authority will
   * cite a sentence that does not exist.
   *
   * The payload carries `previousName` and `name`, **as text**, for the
   * reason `board.visibility_changed`'s carries both levels:
   * `activity_entries` is append-only with no backfill, and B28 cannot
   * render "renamed Blocked to Waiting" from a single value. This is
   * `board.renamed`'s precedent rather than `card.description_edited`'s
   * length-only restraint — see (d); `labels.name` is `varchar(255)`, so the
   * disclosure and volume arguments that keep a description out of a payload
   * do not reach it.
   *
   * ⚠ **Clearing a name to `null` emits this name, not a third one.**
   * `labels.name` is nullable and `PATCH /labels/:id/name` accepts `null`
   * (`labelNamePatchSchema` in `./label`), so "unnamed" is a *value* of this
   * event rather than an event of its own — the call
   * `card.description_edited` records against `card.description_cleared` and
   * `list.unarchived` against `list.restored`.
   *
   * ⚠ Renaming a label to the name it already has still writes a row, and
   * that is deliberate: `trg_labels_set_updated_at` fires
   * `WHEN (OLD.* IS DISTINCT FROM NEW.*)` so the column does not move, but
   * the no-op suppression B14 built is for a **toggle** whose two clients
   * race, not for an explicit Save. `card.description_edited`'s docstring
   * draws the same line for the same reason.
   */
  'label.renamed': { surfacesInFeed: true },
  /**
   * A label in the board's set changed colour — `FR-CARD-010`,
   * `PATCH /labels/:id/colour`.
   *
   * **New in `0017_label_activity.sql`.** 16 characters, the longest of the
   * four and still comfortably inside `EVENT_TYPE_MAX_LENGTH`.
   *
   * ⚠ **The name is `FR-CARD-010`'s own verb**, not a coinage: its behaviour
   * text is "create, rename, **recolour** and delete".
   * `label.colour_changed` was the `*_changed` alternative
   * (`board.visibility_changed`, `card.position_changed`,
   * `list.position_changed`) and was not chosen — the requirement's word is
   * available, and `renamed`/`recoloured` reads as one family for the two
   * one-field edits on one row where `renamed`/`colour_changed` reads as
   * two. British spelling throughout, matching `labels.colour` and
   * `LABEL_COLOURS` in `./label`.
   *
   * `true`, on `label.created`'s footing. The judgement worth stating is the
   * one *against* suppressing it as cosmetic noise: a board's label colours
   * are the vocabulary every card front is read through, and
   * `FR-CARD-010`'s own edge case ("two labels on the same board may share a
   * colour with different names") means a recolour can make two labels
   * visually identical across every card on the board at once. That is a
   * change to shared state, not a personal view setting —
   * `list.position_changed`'s argument arriving at the same answer.
   *
   * The payload carries `previousColour` and `colour`. Both are palette
   * members, so both are at most 13 characters; the before/after argument is
   * `label.renamed`'s.
   */
  'label.recoloured': { surfacesInFeed: true },
  /**
   * A label was permanently deleted from the board's set, and with it every
   * `card_labels` row that referenced it — `FR-CARD-010`,
   * `DELETE /labels/:id`.
   *
   * **New in `0017_label_activity.sql`.** 13 characters. Mirrors
   * `board.deleted` and `list.deleted`, the same operation on the two
   * containers above it.
   *
   * ⚠⚠ **`true`, AND IT CANNOT BORROW `list.deleted`'s OR `board.deleted`'s
   * `false`. READ THIS BEFORE "HARMONISING" THE THREE.** Their argument is
   * mechanical rather than squeamish: after either is emitted the row is
   * gone from every read path, no route writes `deleted_at` back, and **the
   * feed the entry would appear in is unreachable for ever** — an entry in a
   * feed nobody can open is an audit record, not a feed entry. A deleted
   * label's board feed is very much reachable: nothing about the board
   * changed, and every member will open it tomorrow.
   *
   * So the argument has to be its own, and it lands the other way. Deleting
   * a label removes it from every card on the board through
   * `fk_card_labels_label_id`'s ON DELETE CASCADE, which emits **nothing per
   * card** (see below). If the deletion itself were also invisible, a member
   * returning to find twelve cards missing a label would have no record
   * anywhere of what happened — the feed would show the label being created
   * and renamed and then simply stop. `FR-BOARD-016`'s "a feed entry
   * offering a name and a timestamp for something irrecoverable invites
   * exactly the restore attempt it forbids" was `list.deleted`'s reason for
   * `false`, and `FR-CARD-010` has no undo-stack sentence to invoke it; there
   * the whole subtree left the feed's reach, here nothing leaves but the
   * label.
   *
   * ⚠ **This is a HARD delete, and it is the only one in the product.**
   * `labels` has no `deleted_at`, `FR-CARD-010` says deletion "removes it
   * from every card on that board and cannot be undone", and the reason
   * `board.deleted` and `list.deleted` are soft deletes does not apply:
   * `fk_activity_entries_board_id` and `fk_activity_entries_card_id` are ON
   * DELETE CASCADE, so a hard delete there would take the activity history
   * SC-1 exists to preserve — whereas `activity_entries` has **no
   * `label_id` column and no foreign key to `labels` at all**, so removing
   * the row touches not one entry. This one included: it survives its own
   * subject.
   *
   * The payload carries the label id, its `name` and its `colour`. It is the
   * only place that identity survives the commit, which is why it carries
   * more than `board.deleted` and `list.deleted` do — those can lean on a
   * soft-deleted row still being there to join to, and this cannot.
   *
   * ⚠ **ONE ROW, AND NO PER-CARD `label.removed` STORM.** The removal from
   * every card happens in the database and the application never sees the
   * rows. A handler that reads the affected cards first and emits one
   * `label.removed` each passes every visible assertion — 204, the cards no
   * longer carry it — while writing N rows describing a removal nobody
   * performed, which would then render in B28 as N members individually
   * unlabelling cards. `member.removed`'s docstring faced the mirror
   * question for `FR-PERM-029`'s board-member cascade and declined to decide
   * it; here it is forced, and this event's payload is the whole record.
   *
   * ⚠ **There is no `label.restored` and there never will be.** The deletion
   * is irreversible by requirement, so unlike `list.archived` /
   * `list.unarchived` there is no lifecycle for a second name to complete.
   */
  'label.deleted': { surfacesInFeed: true },
  /**
   * An existing label was put on a card — `FR-CARD-009`,
   * `POST /cards/:id/labels`.
   *
   * ⚠ **A `0004` placeholder since the schema was written (`label_added`,
   * dotted by `0013`'s mechanical rename), unemitted by anything until B15
   * — so it needed no DDL.** That is why `0017` adds four names rather than
   * six, and why a block that reads only `FR-CARD-009` would find its whole
   * vocabulary already admitted and ship no migration at all. See (a).
   *
   * ⚠ **Card-scoped, and NOT a board-level name.** This is the application of
   * a label that already exists in the board's set; `label.created` is what
   * puts one *in* the set. See (b) — there is no `board.label_added`, and a
   * block that wants one must not reach for this.
   *
   * `true`, and a judgement rather than a requirement — `FR-CARD-009` names
   * no feed anywhere. It qualifies under the **strict** reading of
   * `FR-COLLAB-020` as well as the loose one, because it carries a real
   * `card_id` and that sentence names cards explicitly ("entries from all
   * cards and lists on the board, each entry naming its card") —
   * `card.description_edited`'s exact footing. The supporting argument: a
   * label is the mechanism that keeps a board legible at 200 cards, so
   * applying one is a change to shared card content every member sees, not a
   * personal view setting.
   *
   * The payload carries the label id (no column exists for it), the card's
   * list id and the workspace id. The **target label's name and colour are
   * not copied in** — unlike `label.created`, this event's subject is a row
   * that still exists and B28 can join to it, and a copy would be a stale
   * one the first time the label was renamed.
   *
   * ⚠ **Applying a label the card already carries writes nothing at all.**
   * `FR-CARD-009` AC1's checkbox is a toggle two clients can fire at once,
   * so the route decides it with `INSERT … ON CONFLICT DO NOTHING RETURNING`
   * — one statement — rather than SELECT-then-write, whose losing side is a
   * 500 on `card_labels_pkey` for an operation the requirement calls a
   * no-op. An unconditional `emit()` after a correct `ON CONFLICT DO
   * NOTHING` passes 200, the right label list and no duplicate row while
   * filling the card's activity with phantom applications. This is B14's
   * lesson (see (d) in the block above) in a third costume, and B12's
   * drop-in-place edge case in a fourth.
   */
  'label.added': { surfacesInFeed: true },
  /**
   * A label was taken off a card — `FR-CARD-009`,
   * `DELETE /cards/:id/labels/:labelId`.
   *
   * A `0004` placeholder (`label_removed`), dotted by `0013`, unemitted
   * until B15 — no DDL needed.
   *
   * `true`, on `label.added`'s footing exactly and not repeated: AC1's
   * checkbox is one toggle in two directions, and a feed showing only
   * applications would read as though no card ever lost a label —
   * `member.added` / `member.removed`'s argument arriving at the same
   * answer.
   *
   * ⚠ **This is NOT what `DELETE /labels/:id` emits.** Deleting a label from
   * the board's *set* emits one `label.deleted` and no per-card event of any
   * kind; the cascade is a database fact the application never observes. See
   * `label.deleted`. A block that later builds a bulk unlabel gesture would
   * emit this one per card, because there the application really does
   * perform each removal.
   *
   * ⚠ Removing a label the card does not carry writes nothing at all —
   * `DELETE … RETURNING` decides it in one statement. See `label.added`.
   */
  'label.removed': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Comments — B16, `FR-COLLAB-001` (post a comment and read the stream,
  // BE-16.1), `FR-COLLAB-002` (edit your own, BE-16.2), `FR-COLLAB-003`
  // (delete one, BE-16.2) and `FR-COLLAB-014` (the board's commenting
  // permission, BE-16.3). `FR-COLLAB-006` — ordering and paging — has no
  // `BE-` item and is absorbed into BE-16.1; it emits nothing.
  //
  // (a) **THREE OF THESE FOUR WERE ALREADY IN `ck_activity_entries_type`
  // AND ONE WAS NOT.** `comment.added`, `comment.deleted` and
  // `board.permission_changed` are `0004` placeholders, dotted by `0013`,
  // unemitted by anything until now; `comment.edited` existed in no spelling
  // and is added by `db/postgres/migrations/0018_comment_activity.sql` —
  // sixty-five names to sixty-six, and the Postgres-only CHECK-change count
  // from seven to eight.
  //
  // ⚠ **NO ORDINAL IS CLAIMED FOR THAT, AND THE REASON IS THE POINT.** A
  // draft of this block called B16 "the third partial case, after B09 and
  // B15", inheriting the B15 block's "the first partial case since B09" —
  // and BOTH are wrong, because a partial split is what MOST DDL-bearing
  // blocks in this registry have had. Counted against the entries above
  // rather than against any comment: **B08** (four `board.*` placeholders,
  // `board.deleted` new in `0013` — its own docstring says so), **B09** (one
  // of two), **B11** (four of six), **B15** (two of six) and **B16** (three
  // of four) are all partial. The genuinely unusual shapes are the other
  // three: all-new (B06, B07, B10, whose migrations added every name they
  // emit), all-existing (B12, B14) and none-at-all (B13, a read).
  //
  // So "which precedent is this?" was never the useful question, and the
  // ordinal was worth less than the miscount cost. **This file already
  // carries three corrections of its own arithmetic; do not add a fourth by
  // numbering these.** The question that survives every shape is below.
  //
  // ⚠ **AND THIS IS THE FIRST CASE WHERE THE GREP THAT USUALLY CATCHES IT
  // ANSWERS CORRECTLY AND STILL MISLEADS.** Against `0017`'s applied state a
  // grep of `comment.` returned exactly two names — and unlike B15's two
  // (which were a *different requirement's*), B12's four (which were all of
  // them) or B11's five (one of which was somebody else's), **both of these
  // are genuinely B16's, both are correct, and both are reused exactly as
  // they stand.** Every check on those two hits comes back clean. That is
  // the exact fingerprint of the B12 case — "every name I emit already
  // exists, ship no migration" — which was TRUE for B12 and TRUE again for
  // B14, so the heuristic that has been right twice fires here and is wrong.
  //
  // What it cannot see: `0004` MODELLED A COMMENT AS IMMUTABLE. It gave the
  // noun a birth and a death and no edit, while giving every other noun in
  // this constraint its edit verb — `card.renamed`,
  // `card.description_edited`, `board.renamed`, `list.renamed`,
  // `label.renamed`, `label.recoloured`. ⚠ Sharper still, and the half worth
  // carrying: **`comments.edited_at timestamptz(3) NULL` is line 35 of
  // `0004`'s own CREATE TABLE**, so the TABLE and the CONSTRAINT have
  // disagreed since the day they were written — a reader who checks the
  // schema concludes editing was modelled from the start and a reader who
  // greps the constraint concludes it was not. `0018` is where they stop.
  //
  // The rule that survives every shape above, and the only one worth
  // carrying: **grep each name INDIVIDUALLY against the LIVE constraint.**
  // ⚠ And note the second failure a prefix grep has here that it did not
  // have before: `comment.` cannot see `board.permission_changed`, which is
  // also B16's. A prefix is not a block's vocabulary.
  //
  //   docker compose exec -T postgres psql -U molt -d molt_v1 -c \
  //     "SELECT m[1] FROM pg_constraint c,
  //        regexp_matches(pg_get_constraintdef(c.oid), '''([a-z._]+)''', 'g') AS m
  //      WHERE c.conrelid='activity_entries'::regclass
  //        AND c.conname='ck_activity_entries_type' AND m[1] LIKE 'comment%'"
  //
  // Match the DOTTED spelling; `comment_added` / `comment_deleted` still
  // appear in 0004, 0011 and 0012 as historical constraint bodies.
  //
  // (b) SCOPE, AND IT IS NOT UNIFORM ACROSS THE FOUR.
  //     comment.added / comment.edited / comment.deleted
  //                                    → cardId AND boardId, both COLUMNS
  //     board.permission_changed       → boardId on the column, cardId NULL
  //
  // The three comment events follow B12's rule inherited whole:
  // `activity_entries` has a real `card_id` column, `FR-COLLAB-020`
  // describes the board feed as "entries from all cards and lists on the
  // board, **each entry naming its card**", and that is a join B28 cannot
  // make from a JSONB key without a scan. `boardId` is what
  // `publishEvents` filters on before fanning out to
  // `boardChannel(boardId)`, which is the mechanism `FR-COLLAB-026` AC1
  // needs ("given I have a card open, when another member posts a comment,
  // then it appears at the top of the stream without a reload").
  // `board.permission_changed` carries `cardId: null` because a board
  // setting belongs to no card and naming one would be a lie.
  //
  // ⚠ **The COMMENT id always travels in `payload`.** `activity_entries` has
  // **no `comment_id` column** and no foreign key to `comments` — its
  // scoping columns are `card_id`, `board_id` and `actor_user_id`, verified
  // against the live catalogue — the same trade `0012` recorded for the
  // workspace id, `0016` for the list id and `0017` for the label id.
  //
  // ⚠ For labels that absence was a convenience (a hard delete touched no
  // activity entry). **Here it is a documented cost**, and it is (e) below.
  //
  // (c) PAYLOAD SHAPE: **`card.description_edited`'s precedent, NOT
  // `label.renamed`'s** — lengths, never the text. The argument is
  // identical to the description's and it is stronger here.
  // `activity_entries.payload` is JSONB on an append-only table nothing
  // prunes; a 16 KB comment edited forty times is 640 KB of history for one
  // card; and on a **public** board (`FR-PERM-010`) that history is readable
  // by anyone with the link — which for a comment is worse than for a
  // description, because `FR-COLLAB-003` promises deletion is *permanent*
  // and a body preserved in an audit payload would make that promise false.
  // `label.renamed` carries its text because `labels.name` is
  // `varchar(255)` and a label name is a shared vocabulary term rather than
  // authored prose; neither is true of `comments.body`, which is unbounded
  // `text`. A block that genuinely needs diffs builds a revision table.
  //
  // (d) ⚠ **THE PAYLOAD ASYMMETRY MAPS EXACTLY ONTO WHO MAY PERFORM EACH
  // OPERATION, WHICH IS WHAT MAKES IT CHECKABLE.** `comment.added` and
  // `comment.edited` carry **no** `authorUserId`, because the actor IS the
  // author — only the author may edit (`FR-COLLAB-002` AC3 refuses even a
  // board admin) and B16 writes no email-reply comments.
  // `comment.deleted` **must** carry it, because a board admin may be
  // deleting somebody else's comment and the row is **gone** the instant the
  // transaction commits: without it, "Ada deleted Bea's comment" is
  // unrecoverable from the audit trail for ever. Adding `authorUserId` to
  // all three "for symmetry" would be harmless noise; dropping it from
  // `comment.deleted` is irreversible data loss.
  //
  // (e) ⚠ **THE ORPHAN THIS BLOCK KNOWINGLY CREATES, AND THE TWO
  // OBLIGATIONS IT WRITES FORWARD ONTO `BE-28.1`.** `FR-COLLAB-003`'s
  // deletion is a **hard** delete with no tombstone — `comments` has no
  // `deleted_at` and none is to be invented — so `DELETE FROM comments`
  // removes the row while the `comment.added` and `comment.edited` entries
  // survive, holding a `payload.commentId` that resolves to nothing. Nothing
  // cascades, because no foreign key points at `comments` from here. **That
  // is correct and is documented rather than fixed**: SC-1 makes this table
  // an append-only audit spine, and the Phase 1 comment STREAM reads the
  // `comments` table, so a deleted comment does leave the stream (satisfying
  // `FR-COLLAB-003` AC1). The FEED that would render the orphan is
  // `FR-COLLAB-017` / **`BE-28.1`**, which inherits two obligations — written
  // forward the way B11 wrote its card-read conjunct onto B12 and B13:
  //
  //   1. **Drop orphaned `comment.added` / `comment.edited` entries** —
  //      resolve each against the `comments` table and omit the ones whose
  //      `payload.commentId` no longer exists. Rendering one is a tombstone
  //      by another route, which is the precise thing FR-COLLAB-003 forbids.
  //   2. **Do not double-render a comment the Phase 1 stream already
  //      serves.** `FR-COLLAB-017` mixes comments and activity in one
  //      card-back stream; B16 serves comments from `comments` and B28 will
  //      serve activity from `activity_entries`, which contains a
  //      `comment.added` row for every one of them. B28 owns the merge.
  //
  // Also named for **`B17`**: `notifications.comment_id` is `ON DELETE SET
  // NULL` (measured), so a notification survives its comment's deletion with
  // a null reference — exactly the state `FR-NOTIF-006` AC4 describes, one
  // noun over.
  //
  // (f) ⚠ **`board.permission_changed` IS DELIBERATELY GENERIC AND THE
  // `setting` KEY IS WHAT KEEPS IT USABLE.** `FR-PERM-027` (member
  // add/remove permission), `FR-PERM-028` (the self-join toggle) and
  // `FR-PERM-025`'s "any setting" will all emit this same name from `B38`,
  // and without a discriminator in the payload their entries would be
  // indistinguishable from B16's. The alternative —
  // `board.commenting_permission_changed` — was rejected twice over: the
  // generic name **already exists** in the constraint, so minting a specific
  // one costs a migration for nothing, and one name per setting would give
  // the constraint five near-identical members for one fact family. **B16 is
  // the first block to emit it, so B16 fixes the payload contract:
  // `setting` is REQUIRED on every `board.permission_changed` payload**
  // (`'commenting'` here), and `B38` extends the union of its values rather
  // than inventing a name.
  //
  // ⚠ **B38 HAS LANDED AND DID EXACTLY THAT — this paragraph is confirmed in
  // place rather than retired.** `FR-PERM-027` and `FR-PERM-028` emit this
  // same name with `setting: 'member_add_remove'` and
  // `setting: 'workspace_self_join'`; the union is three members, declared
  // ONCE as `BOARD_PERMISSION_SETTINGS` in
  // `packages/shared/src/board-permissions.ts`, with B16's emit site edited to
  // import it rather than restate its own literal. `FR-PERM-026` added
  // nothing — it is `FR-COLLAB-014` reconciled, the same route and the same
  // five values, so B38 wrote no code for it. `ck_activity_entries_type`
  // gained **no** name for any of the three, which was the whole point of the
  // trade this paragraph records.
  //
  // (g) FEED. **Two `true` and two `false`, and no two of the four rest on
  // the same authority — read each entry rather than inferring symmetry.**
  // One is a requirement's positive sentence (`board.permission_changed`),
  // one is a requirement's sentence in the **negative** (`comment.deleted`),
  // one is `FR-COLLAB-017`/`FR-COLLAB-020`'s enumeration
  // (`comment.added`) and one is a judgement with a positive-authority prop
  // (`comment.edited`). This is the first block in this registry where the
  // four authorities are four different kinds.
  //
  // (h) `comment.*` NAMES IN THE CONSTRAINT THAT ARE NOT REGISTERED HERE:
  // **none.** Post-`0018` a grep of `comment.` returns three and this
  // registry declares three — the **second** exact match in this file after
  // `label.`'s six (B15 recorded that as the first; `list.` returns seven
  // against six, `card.` twelve against five). Said out loud so nobody goes
  // looking for a placeholder that is not there. ⚠ Do not read the exact
  // match as "the comment vocabulary is closed": `board.permission_changed`
  // is B16's and is not a `comment.` name, and the names a later block will
  // want and must add itself include a reaction event (`comment_reactions`
  // is a real table, `FR-COLLAB-013`) and whatever `FR-COLLAB-031`'s inbound
  // email path needs. `0013`'s rule keeps them unwritten until something
  // emits them.
  // ---------------------------------------------------------------------

  /**
   * A comment was posted on a card — `FR-COLLAB-001`,
   * `POST /cards/:id/comments`.
   *
   * A `0004` placeholder (`comment_added`), dotted by `0013`, unemitted
   * until B16 — no DDL needed for this one.
   *
   * `true`, and close to requirement-level: `FR-COLLAB-020` describes the
   * board feed as filtering between **All** and **Comments**, which is only
   * meaningful if comments are in it, and `FR-COLLAB-017` describes the card
   * back as "one reverse-chronological stream mixing comments and
   * system-generated activity entries". It also qualifies under the
   * **strict** reading of `FR-COLLAB-020` as well as the loose one, because
   * it carries a real `card_id` and that sentence names cards explicitly.
   *
   * The payload carries the **comment id** (no column exists for it), the
   * card's list id, the workspace id and `bodyLength` — **never the body**.
   * See (c) in the block comment; the disclosure argument is sharper for a
   * comment than for a description because deletion is meant to be
   * permanent. No `authorUserId`: the actor is the author — see (d).
   *
   * ⚠ **This row OUTLIVES the comment it describes.** `FR-COLLAB-003`'s
   * delete is hard and nothing cascades to `activity_entries`, so
   * `payload.commentId` dangles the moment the comment goes. That is
   * deliberate and `BE-28.1` owns the render-time fix — see (e). Do **not**
   * "tidy" it by adding a `comment_id` column with `ON DELETE CASCADE`: that
   * destroys the audit trail SC-1 exists to keep, which is the same reason
   * `fk_activity_entries_board_id`'s cascade forces `board.deleted` to be a
   * soft delete.
   *
   * ⚠ **An empty or whitespace-only comment writes nothing at all.**
   * `FR-COLLAB-001` AC2 is explicit — "submission is rejected and **no
   * activity entry is created**" — and the refusal happens at the boundary
   * (`commentBodySchema` trims before `.min(1)`), so the handler is never
   * reached. A schema that dropped the `.trim()` would turn that into a 500
   * from `ck_comments_body_not_blank` instead, with no entry either way but
   * the wrong status code.
   *
   * ⚠ **Posting writes no `watches` row.** `FR-COLLAB-024`'s edge case makes
   * auto-watch-on-comment the rejected product decision; `FR-COLLAB-001` AC3
   * prompts instead, and that prompt is `FE-16.2`'s over a route
   * `FR-COLLAB-022` has not built. Adding one here is invisible until `B17`
   * ships and every commenter is subscribed to every subsequent comment.
   */
  'comment.added': { surfacesInFeed: true },
  /**
   * A comment's body was rewritten by its author — `FR-COLLAB-002`,
   * `PATCH /comments/:id/body`.
   *
   * **New in `0018_comment_activity.sql`**, in this spelling and every
   * other: `0004` enumerated `comment_added` and `comment_deleted` and no
   * edit verb, while creating `comments.edited_at` in the same file. 14
   * characters. The name is modelled on `card.description_edited` — the same
   * operation on the same kind of column — and drops the field segment
   * because a comment IS its body, so `comment.body_edited` would carry a
   * discriminator with nothing to discriminate against. `comment.updated`
   * was the alternative and was not chosen: `updated` is the vocabulary of
   * the trigger-owned `updated_at` columns, and `comments` has neither the
   * column nor a trigger.
   *
   * ⚠ **`false`, and it is a judgement, so here is the argument in full —
   * this is the entry a later block is most likely to "harmonise" with
   * `card.description_edited: true`.** No requirement asks for it to
   * surface; `FR-COLLAB-017`'s enumeration of recorded events does not
   * include a comment edit; and `FR-COLLAB-002`'s entire surfacing mechanism
   * is the **"(edited)" marker on the comment itself**, which comes from
   * `comments.edited_at` and not from a feed. `FR-COLLAB-017` AC4 is
   * positive authority for the split — "given an activity entry exists, when
   * any member tries to edit or delete it, then no such control is offered —
   * **only comments are mutable**" — which establishes that comment
   * mutability is a property handled *on the comment*, beside it rather than
   * as a feed entry of its own. A line per edit would also double-report one
   * fact and would flood a busy conversation with entries carrying no
   * content, since the text is deliberately not in the payload.
   *
   * ⚠ **The contrast with `card.description_edited: true` is not an
   * inconsistency**: a description edit has **no in-place marker and no
   * other surface**, so the feed is the only place it can appear; a comment
   * edit has one. Change one and you must change the other's argument, not
   * just its flag.
   *
   * Recorded regardless (SC-1), and the reason is specific rather than
   * generic here: this is the **only** record that an edit happened at a
   * given time by a given actor. `comments.edited_at` remembers only the
   * last one.
   *
   * The payload carries the comment id, `previousLength` and `length` —
   * before and after, on `board.visibility_changed`'s and
   * `list.position_changed`'s shape, and lengths rather than text on
   * `card.description_edited`'s. No `authorUserId`: only the author can
   * reach this route, so the actor column already says who — see (d).
   *
   * ⚠ **An edit to identical text still writes a row and still moves
   * `edited_at`.** That looks inconsistent beside B14's and B15's no-op
   * suppressions and is B14's stated boundary: those suppress a **toggle**
   * two clients can race, and this is an explicit **Save** under
   * last-write-wins, which settles who wrote last even when the text did
   * not change. `card.description_edited`'s docstring draws the same line.
   *
   * ⚠ **`comments` has NO trigger and the handler must write `edited_at`
   * itself.** That is the inverse of the rule every other timestamp in this
   * product follows, and the failure is silent: omit it and the route
   * answers 200 with the new body while "(edited)" never appears anywhere.
   * `commentSchema.editedAt` in `./comment` carries the long form.
   */
  'comment.edited': { surfacesInFeed: false },
  /**
   * A comment was permanently deleted — `FR-COLLAB-003`,
   * `DELETE /comments/:id`.
   *
   * A `0004` placeholder (`comment_deleted`), dotted by `0013`, unemitted
   * until B16 — no DDL needed.
   *
   * ⚠⚠ **`false`, ON REQUIREMENT-LEVEL AUTHORITY IN THE NEGATIVE, WHICH IS A
   * FOOTING NO OTHER ENTRY IN THIS REGISTRY STANDS ON.** `FR-COLLAB-003`'s
   * behaviour sentence: "A deleted comment is removed from the card, board,
   * and workspace activity feeds; **it does not leave a tombstone**." A feed
   * line reading "Ada deleted a comment" *is* a tombstone — it is the
   * precise thing that sentence forbids, and the requirement forbids it in
   * the feeds by name. So this is not `board.deleted`'s or `list.deleted`'s
   * "the feed is unreachable afterwards" argument, which would fail here:
   * the board's feed is very much reachable, and `label.deleted` is `true`
   * for exactly that reason one noun over. Do **not** harmonise the three.
   *
   * It is written anyway — SC-1 admits no unrecorded mutation, and "who
   * removed this, and when" is the audit question moderation exists to
   * answer, which matters more here than anywhere else in the product
   * because the row itself is gone.
   *
   * ⚠ **And it is still BROADCAST, which is the point.** `surfacesInFeed` is
   * the §2.6 feed discriminator and nothing else reads it, so a `false`
   * still reaches `boardChannel(boardId)` — which is how `B18` removes the
   * comment live from a card back somebody has open. `FR-COLLAB-025`'s edge
   * case ("if another member archives or deletes the card the viewer has
   * open, the card back shows an inline notice") applied one level down.
   * `board.deleted` and `list.deleted` rely on the identical mechanism.
   *
   * ⚠ **This is the ONE comment event whose payload carries
   * `authorUserId`,** and the asymmetry is not an oversight — see (d). A
   * board admin may delete a **non-admin's** comment (`FR-COLLAB-003`, and
   * `OQ-5` answered NO for a peer admin's), so actor and author can differ,
   * and the row is gone the instant the transaction commits. Without it,
   * "Ada deleted Bea's comment" is unrecoverable for ever. The payload also
   * carries the comment id and `bodyLength` — never the body, and here the
   * restraint is at its sharpest: preserving the text of a comment whose
   * deletion the requirement calls *permanent* would make that promise
   * false on any public board.
   *
   * ⚠ **`comment_attachments` and `comment_reactions` follow the row through
   * `ON DELETE CASCADE` and emit NOTHING.** That is what makes
   * `FR-COLLAB-003` AC4 ("when the comment is deleted, then the
   * comment-scoped attachment is removed with it") a property of the
   * **schema** rather than of a handler — an argument *for* the hard delete
   * rather than a cost of it. A handler that read the attachments first and
   * emitted one `attachment.removed` each would pass every visible
   * assertion while writing rows describing removals nobody performed;
   * `label.deleted`'s docstring faced the same temptation.
   *
   * ⚠ **There is no `comment.restored` and there never will be.** Deletion
   * is irreversible by requirement and `comments` has no `deleted_at`, so
   * unlike `list.archived` / `list.unarchived` there is no lifecycle for a
   * second name to complete.
   */
  'comment.deleted': { surfacesInFeed: false },
  /**
   * A board-level permission setting was changed — `FR-COLLAB-014` via
   * `PATCH /boards/:id/commenting-permission`, and later every other board
   * setting `B38` builds.
   *
   * A `0004` placeholder (`board_permission_changed`), dotted by `0013`,
   * unemitted by anything until B16 — no DDL needed.
   *
   * ⚠ **B16 is the first block to emit it, so B16 fixes its payload
   * contract: `setting` is REQUIRED, and B16's value is `'commenting'`.**
   * See (f) in the block comment for why a generic name plus a
   * discriminator beats `board.commenting_permission_changed`, and why
   * `B38` extends the union of `setting` values rather than minting a name.
   * A payload without `setting` is indistinguishable from B38's future
   * entries and there is no migration to fix it in.
   *
   * ---------------------------------------------------------------------
   * ⚠⚠ **B38 HAS LANDED AND THE UNION IS NOW THREE, NAMED HERE IN FULL:**
   * ---------------------------------------------------------------------
   * ```
   * { setting: 'commenting',          previous, next }  // one of 5 (B16)
   * { setting: 'member_add_remove',   previous, next }  // 'admins' | 'all_members'
   * { setting: 'workspace_self_join', previous, next }  // boolean
   * ```
   *
   * `FR-COLLAB-014` / `FR-PERM-026` for the first, `FR-PERM-027` for the
   * second, `FR-PERM-028` for the third; `PATCH /boards/:id/commenting-
   * permission`, `PATCH /boards/:id/member-permission` and
   * `PATCH /boards/:id/self-join` respectively. B38 minted **no** event name
   * for either of its two settings, exactly as this docstring predicted — the
   * generic name plus the discriminator is doing the whole job, and the
   * constraint gained nothing for them.
   *
   * ⚠ **The union has a CODE REPRESENTATION now, and there must never be a
   * second.** Through B37 it existed only in prose: `'commenting'` was a bare
   * string literal at B16's emit site and nothing anywhere enumerated the
   * values. B38 minted `BOARD_PERMISSION_SETTINGS` in
   * `packages/shared/src/board-permissions.ts` — a `const … as const` plus a
   * `z.enum`, on `COMMENTING_PERMISSIONS`' pattern — and **edited B16's emit
   * site to use it** rather than leaving three literals across two route
   * files. That file's docstring argues why the tuple lives there and not
   * here: this registry deliberately binds no payload shape to any name, and
   * one exception invites a second.
   *
   * ⚠ **`'visibility'` is NOT a member of that union.** A visibility change
   * has a name of its own — `board.visibility_changed`, above — so it emits
   * that and never this. Two valid spellings of one fact in an append-only
   * feed is exactly what the discriminator exists to prevent, not something it
   * licenses.
   *
   * ⚠ **`ck_activity_entries_type` does not police the union.** The CHECK
   * constrains `type`, which is this one name for all three; `setting` rides
   * in a JSONB payload the database does not read. So a fourth setting needs
   * **no migration** — and, symmetrically, a misspelt fourth literal is caught
   * by nothing but a test, and writes a row that renders as a setting change
   * nobody can name.
   *
   * `true` **by the requirement rather than by judgement**, the same footing
   * `board.visibility_changed` stands on. `FR-PERM-025` AC4: "given a board
   * admin changes **any setting**, when the board activity feed is read,
   * then the change is recorded" — and that requirement's own text
   * enumerates commenting permission as one of the settings it means, which
   * is what makes this citation direct rather than the by-analogy reading
   * `board.member_added`'s docstring rejects for a *membership*.
   *
   * The payload carries `previous` and `next` beside `setting`, for
   * `board.visibility_changed`'s reason: `activity_entries` is append-only
   * with no backfill, and `B28` cannot render "widened" against "narrowed"
   * from one value without a second query against a row that has since
   * changed again.
   *
   * ⚠ **Board-scoped with `cardId: null`.** A board setting belongs to no
   * card and naming one would be a lie; it passes `ck_activity_entries_scope`
   * on the second disjunct, `board_id IS NOT NULL` standing alone — the same
   * standing B15's four board-level label names rely on.
   *
   * ⚠ **Setting the permission a board already has emits NOTHING.**
   * `PATCH /boards/:id/visibility`'s documented behaviour, followed rather
   * than re-decided ("setting the level a board already has is an idempotent
   * no-op: no activity entry, no timestamp bump"). This is B14's *toggle*
   * side of the boundary — two clients can fire a settings patch at once —
   * and it is NOT in tension with `comment.edited` emitting on identical
   * text, which is the *explicit Save* side. Emitting here on a no-op fills
   * an append-only feed with phantom setting changes and passes every
   * visible assertion.
   *
   * ⚠ **A board later made private while holding `any_user` emits nothing
   * either, and no cascade writes this event.** `PATCH /boards/:id/visibility`
   * is not edited by B16: a cascade would make one gesture emit two events,
   * the second attributed to an actor who never touched the setting, which
   * is a lie in an append-only feed. The stored value survives and the
   * commenting guard fails closed on its own.
   */
  'board.permission_changed': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Dates — B24 stage 1, `FR-CARD-013` (`BE-24.1`, an optional start date and
  // an optional due date) and `FR-CARD-014` (`BE-24.2`, the due-date reminder
  // offset — a property of the CARD, settled against the per-board reading of
  // `FR-NOTIF-003` in `README.md` §C.2).
  //
  // (a) ALL FIVE WERE ALREADY IN `ck_activity_entries_type`, SO **THIS STAGE
  // SHIPS NO MIGRATION**. `due.date_set`, `due.date_changed`, `due.date_removed`,
  // `start.date_set` and `start.date_removed` are five of `0004`'s placeholders
  // (snake_case there, dotted by `0013`'s mechanical rename), unemitted by
  // anything until now — B12's reason ("the names already exist") arriving for
  // the fourth time (B14 second, B21 third). `db/postgres/migrations/` stays at
  // **twenty** files and the Postgres-only `activity_entries` CHECK-change count
  // stays at **nine**.
  //
  // ⚠ **Stage 2 (`BE-24.4`) adds two more names on the identical footing —
  // `due.date_completed` and `due.date_uncompleted`, both `0004` placeholders
  // too — and neither number above moves.** See the correction a few
  // paragraphs down, at the point stage 1 predicted these two would arrive
  // later; this is that later.
  //
  // ⚠ **`start.date_changed` is checked and is ABSENT from the live constraint
  // in every spelling — do not add it here on the strength of `due.date_changed`
  // existing beside it.** The asymmetry is `FR-CARD-013`'s: nothing distinguishes
  // *setting* a start date from *changing* one the way `FR-CARD-014` AC4
  // distinguishes a due-date change from a due-date set (the reminder-offset
  // recompute only the due date has), so `start.date_set` alone covers
  // NULL→value and value→value — `card.renamed`'s one-verb-for-every-value
  // pattern, not `due.*`'s three-verb one. Registering a name the constraint
  // does not admit is not a compile error; it is a runtime `23514` on the first
  // start-date edit.
  //
  // ⚠ **"`card.completed` / `card.unavailable` are not this block's, and
  // neither is `due.date_completed` / `due.date_uncompleted`" was stage 1's
  // sentence and it is corrected in place rather than deleted, because
  // stage 2 is where the second half stops being true.** `card.completed`
  // was never a real name — no such placeholder exists in any spelling, and
  // it is named here only as the mint stage 1 declined. `due.date_completed`
  // / `due.date_uncompleted` WERE `0004` placeholders waiting on an emitter,
  // and `BE-24.4` (`FR-CARD-004`, `PATCH /cards/:id/completed`) is now that
  // emitter — see the two entries at the foot of this block. `0013`'s rule
  // fires exactly as it did for `card.archived`/`card.unarchived` at B22: a
  // block that first emits a placeholder registers it **then**, and stage 2
  // is "then" for these two. So this block now emits **seven** names, not
  // five, and every one of the seven was already in the live constraint —
  // `db/postgres/migrations/` stays at **twenty** files and the
  // Postgres-only `activity_entries` CHECK-change count stays at **nine**
  // for this stage too. B12's reason ("the names already exist") arriving a
  // fifth time (B14 second, B21 third, stage 1 fourth, stage 2 fifth) — and,
  // on the closed four-example list `CLAUDE.md` keeps, a recurring reason
  // adds no bullet.
  //
  // ⚠ **`due.reminder_changed` is deliberately not minted.** No requirement
  // asks for a reminder-only event name — `FR-COLLAB-017`'s enumeration has no
  // reminder bullet — and `PATCH /cards/:id/due-date-reminder` rides on
  // `due.date_changed` instead, with `dueAt` unchanged in the payload. See (c).
  //
  // (b) SCOPE. All five carry `cardId` **and** `boardId` on the COLUMNS,
  // `card.created`'s rule inherited whole: `activity_entries` has a real
  // `card_id` column, `FR-COLLAB-020`'s "each entry naming its card" is a join
  // rather than a JSONB scan, and `publishEvents` filters on `boardId !== null`
  // before fanning out to `boardChannel(boardId)`.
  //
  // (c) THE PAYLOAD SHAPE IS SHARED ACROSS ALL THREE `due.*` NAMES, ON PURPOSE.
  // `due.date_set`, `due.date_changed` and `due.date_removed` all carry
  // `{ previousDueAt, dueAt, previousReminderMinutesBefore,
  // reminderMinutesBefore }` — not only the pair the event's own name
  // describes — because `PATCH /cards/:id/due-date-reminder` also emits
  // `due.date_changed` with `dueAt` unchanged, and a feed renderer (`B28`) that
  // read only the event *type* would print "changed the due date" for a
  // reminder-only edit. The renderer chooses its sentence by comparing the four
  // fields, never by the type alone. `start.date_set` / `start.date_removed`
  // carry the simpler `{ previousStartAt, startAt }`, since a start date has no
  // second fact riding beside it.
  //
  // (d) FEED. All five `true`, on `FR-COLLAB-020` (each entry names a card) and
  // on the practical argument every card-content event above is registered
  // under: a feed that showed a card created, renamed and moved but silent on
  // when it is due would not answer "why is this card suddenly at the top of
  // Overdue".
  // ---------------------------------------------------------------------

  /**
   * A card's due date was set for the first time — `FR-CARD-013`,
   * `PATCH /cards/:id/due-date` on a NULL→value transition.
   *
   * `true`, on `FR-COLLAB-020`. A placeholder since `0004`
   * (`card_due_date_set`), dotted by `0013`, so no migration was needed for
   * it.
   *
   * The payload carries `dueAt` and `reminderMinutesBefore` **after** the
   * write — `previousDueAt` and `previousReminderMinutesBefore` are both
   * `null` on this transition and are still sent, so a consumer never has to
   * special-case "the first row" the way `board.visibility_changed`'s
   * docstring names the visibility chain's missing anchor entry. Unlike that
   * entry, this one needs no anchor: `null` IS the anchor value, spelt the
   * same way it is spelt everywhere else in this product.
   *
   * ⚠ **`reminderMinutesBefore` on this row is `DUE_REMINDER_DEFAULT_MINUTES_BEFORE`
   * (1,440) unless the card already carried an offset from a prior due date
   * that was since cleared** — which cannot happen through this block's own
   * routes (clearing the due date clears the offset too, `cardDueDatePatchSchema`'s
   * docstring), so in practice this row's `reminderMinutesBefore` is always
   * 1,440. `FR-CARD-014` AC1 is the requirement this default satisfies.
   */
  'due.date_set': { surfacesInFeed: true },
  /**
   * A card's due date changed value, **or** its reminder offset changed while
   * the date itself did not — `FR-CARD-013` (`PATCH /cards/:id/due-date` on a
   * value→value transition) and `FR-CARD-014` (`PATCH
   * /cards/:id/due-date-reminder`, any transition).
   *
   * `true`, on `FR-COLLAB-020`. A placeholder since `0004`
   * (`card_due_date_changed`), dotted by `0013`, so no migration was needed
   * for it.
   *
   * ⚠ **Two routes emit this one name, and a reader must not assume `dueAt`
   * moved.** `previousDueAt === dueAt` on a row this event produced means the
   * reminder-only route wrote it; `previousReminderMinutesBefore ===
   * reminderMinutesBefore` with `previousDueAt !== dueAt` means the due-date
   * route wrote it and the offset was carried across unchanged (`FR-CARD-014`
   * AC4: "the reminder offset is preserved and recomputed relative to the new
   * due date"). Both fields can move together if a client happens to change
   * one right after the other, but never in the same request — this block
   * builds no combined body (see `./card-dates.ts`'s header).
   */
  'due.date_changed': { surfacesInFeed: true },
  /**
   * A card's due date was cleared — `FR-CARD-013` AC4's Remove gesture,
   * `PATCH /cards/:id/due-date` on a value→NULL transition.
   *
   * `true`, on `FR-COLLAB-020`. A placeholder since `0004`
   * (`card_due_date_removed`), dotted by `0013`, so no migration was needed
   * for it.
   *
   * `dueAt` and `reminderMinutesBefore` are both `null` on this row —
   * clearing the due date always clears the offset with it, so there is no
   * "removed the date but kept the reminder" state for this event to
   * distinguish. `previousReminderMinutesBefore` still carries whatever the
   * card held a moment before, so a consumer can tell "a bare due date was
   * cleared" from "a due date with an active reminder was cleared" without a
   * second query.
   */
  'due.date_removed': { surfacesInFeed: true },
  /**
   * A card's start date was set or changed — `FR-CARD-013`,
   * `PATCH /cards/:id/start-date` on any transition that leaves a non-null
   * value in place, whether the field was previously empty or already held a
   * different instant.
   *
   * `true`, on `FR-COLLAB-020`. A placeholder since `0004`
   * (`card_start_date_set`), dotted by `0013`, so no migration was needed for
   * it.
   *
   * ⚠ **There is no `start.date_changed`.** See the block comment: nothing in
   * `FR-CARD-013` distinguishes the first start date from a later change to
   * it, so one name covers both — `card.renamed`'s pattern, not
   * `due.date_set`/`due.date_changed`'s. The payload carries
   * `previousStartAt` (which may itself be non-null) and `startAt`, so a
   * consumer wanting the distinction can still make it from the row.
   */
  'start.date_set': { surfacesInFeed: true },
  /**
   * A card's start date was cleared — `FR-CARD-013` AC4's Remove gesture,
   * `PATCH /cards/:id/start-date` on a value→NULL transition.
   *
   * `true`, on `FR-COLLAB-020`. A placeholder since `0004`
   * (`card_start_date_removed`), dotted by `0013`, so no migration was needed
   * for it.
   */
  'start.date_removed': { surfacesInFeed: true },
  /**
   * A card was marked complete — `FR-CARD-004`, `PATCH /cards/:id/completed`
   * with `completed: true`. **Stage 2, `BE-24.4`.**
   *
   * `true`, **by the requirement rather than by judgement**, `board.renamed`'s
   * and `list.renamed`'s footing: `FR-CARD-004` AC4 is explicit — "given a
   * card is marked complete, when the activity section is expanded, then the
   * completion is recorded as an activity entry."
   *
   * A placeholder since `0004` (`due_date_completed`), dotted by `0013`, so
   * no migration was needed for it — the fifth instance of B12's "the names
   * already exist" reason on this file's own count (B14 second, B21 third,
   * stage 1 fourth).
   *
   * ⚠ **Emitted UNCONDITIONALLY, including on a card with NO due date.**
   * `FR-CARD-004`'s own behaviour text: "Completion is independent of due
   * dates — a card with no due date can be marked complete." There is no
   * `due.*`-family gate on this row, and `ck_activity_entries_scope` never
   * sees the difference — `card_id IS NOT NULL` is satisfied identically
   * either way.
   *
   * ⚠ **The payload is `{ dueAt }`, the card's due date AT THE MOMENT OF
   * COMPLETION, and carries no `previousCompleted` / `completed` pair.**
   * Unlike `due.date_changed` — which needs `previousDueAt`/`dueAt` because
   * one name covers two materially different transitions — completion has
   * two names, one per direction (`card.renamed`'s pattern: `list.archived`/
   * `list.unarchived` is the closer precedent, a toggle with no "previous"
   * field on either row because the type itself says which way the toggle
   * went). `dueAt` rides beside it so `B28` can choose between "marked the
   * due date complete" and "marked this card complete" by branching on a
   * FIELD — whether the card carries a due date — never on the event type,
   * which is the same house rule `due.date_changed`'s docstring states for
   * telling its own two writers apart.
   *
   * ⚠ **A no-op emits nothing** — re-marking an already-complete card
   * complete is legal and idempotent (two clients racing the same
   * checkbox), and `../routes/card-dates.ts`'s `PATCH /cards/:id/completed`
   * decides the no-op by `UPDATE … WHERE completed <> $next RETURNING`,
   * never by `SELECT`-then-write, on `member.added`'s `ON CONFLICT DO
   * NOTHING RETURNING` precedent one costume over: a handler that emitted
   * unconditionally would pass every visible assertion while filling an
   * append-only feed with phantom completions.
   */
  'due.date_completed': { surfacesInFeed: true },
  /**
   * A card's completion was removed — `FR-CARD-004`,
   * `PATCH /cards/:id/completed` with `completed: false`. **Stage 2,
   * `BE-24.4`.**
   *
   * `true`, symmetrically with `due.date_completed` above: the pair is one
   * toggle and a feed showing only half of it would read as though a
   * completed card can never become un-complete. `list.unarchived`'s and
   * `board.reopened`'s footing exactly.
   *
   * A placeholder since `0004` (`due_date_uncompleted`), dotted by `0013`,
   * so no migration was needed for it either. Same payload shape as its
   * pair — `{ dueAt }`, read at the moment of the toggle — and the same
   * no-op rule: an already-incomplete card asked to un-complete writes and
   * emits nothing.
   */
  'due.date_uncompleted': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Covers — B25 stage 2, `BE-25.2`, `FR-CARD-025`. `PUT`/
  // `DELETE /cards/:id/cover` (`apps/server/src/routes/card-cover.ts`).
  //
  // Asked separately, on the "fifteen, eight, nineteen" / "sixteen, eight,
  // nineteen" framework the two DDL paragraphs above use:
  //
  // Q1 — does this stage emit a name of its own? YES, TWO: `cover.set` and
  // `cover.removed`. B12's reason arriving again (B14 second, B21 third,
  // B24 stage 1 fourth, B24 stage 2 fifth): both are `0004` placeholders,
  // checked individually in the dotted spelling against live `molt_v1`
  // (`pg_get_constraintdef` on `ck_activity_entries_type`) rather than
  // assumed from this comment's own claim. `db/postgres/migrations/` does
  // not move.
  //
  // Q2 — was a third name checked and found absent, the way `start.date_
  // changed` was for B24? YES: `cover.resized` and `cover.changed`, the two
  // names a size-only or text-treatment-only edit might plausibly have
  // wanted, are in the live constraint under NEITHER spelling. That is why
  // a resize rides `cover.set` rather than minting a third name — see the
  // registration below.
  //
  // Q3 — does this stage need a structure the database does not already
  // have? NO. Both writes are PK-keyed UPDATEs on `cards`; the SELECT …
  // FOR UPDATE pre-image (`apps/server/src/routes/card-cover.ts`) is keyed
  // on `cards_pkey`; `idx_cards_cover_attachment_id` already exists and
  // this stage does not even need it, since the write never joins
  // `attachments` for its own decision (it queries `attachments` once, to
  // assert `card_id`, which is served by `fk_attachments_card_id` /
  // `idx_attachments_card_id_created_at`). No migration, on any axis.
  //
  // Registered here — a SEPARATE act from the constraint admitting the
  // name, on B21's recorded lesson: the constraint has held both names
  // since `0004`; this registry withheld them until the block that first
  // emits one claimed it.
  // ---------------------------------------------------------------------

  /**
   * A card's cover was set or replaced — `FR-CARD-025`, `PUT
   * /cards/:id/cover`. Emitted on any transition that leaves a cover in
   * place and changes something, **including a size-only or
   * text-treatment-only edit**: there is no `cover.resized` in the
   * constraint under any spelling (checked, see above), so a resize rides
   * this name rather than minting one.
   *
   * `true`, on `label.recoloured`'s footing: a card's cover is part of the
   * board's visual vocabulary (`00-`'s third cross-cutting principle) and a
   * feed silent on it would not explain why a card suddenly reads
   * differently at a glance.
   *
   * Payload: `{ source, colour, photoRef, attachmentId, size,
   * textTreatment, previousSource, previousSize }`. The first six describe
   * the cover as it now stands — the three source-specific fields travel
   * together and unpopulated ones are `null`, which is the row's own shape
   * (`apps/server/src/cards/cover.ts`'s `CoverColumns`) rather than a
   * variant-specific payload, because a feed renderer reading history does
   * not get to `switch` on a `source` it has to reparse from three
   * optional keys. `previousSource` (nullable — `null` on a card's first
   * cover) is what lets a consumer tell "set" from "changed" without a
   * second event name, and `previousSize` is what lets it tell a resize
   * from a source/colour replacement — neither is recoverable from the row
   * once the write has landed. `boardId`/`cardId` are on the COLUMNS, not
   * the payload, on `card.created`'s rule inherited whole
   * (`publishEvents` fans out on the `boardId` column).
   *
   * ⚠ **No `workspaceId` and no `listId` in the payload, unlike B14's five
   * member/description events.** `card-dates.ts`'s five `due.*`/`start.*`
   * events are the precedent that decides it, not B14's: both omit them
   * too, and for the identical reason — `activity_entries` has a column
   * for neither, and a card's own board/list column pair is already
   * reachable through `cardId` for any consumer that needs them, which is
   * B14's own escape hatch stated on the *card* side rather than repeated
   * in every payload since. Citing B14 here instead would read as an
   * oversight against its "every one of its five events carries the
   * card's list" rule; it is not one — B14's rule was never universal, and
   * B24's stage 1 already broke from it on the same grounds.
   */
  'cover.set': { surfacesInFeed: true },
  /**
   * A card's cover was removed — `FR-CARD-025`, `DELETE /cards/:id/cover`.
   *
   * ⚠ **"`DELETE /cards/:id/cover`" was the whole of this line through B26 and
   * is corrected in place: there is a SECOND emitter, and it is in another
   * block.** `DELETE /attachments/:id` (B27, `BE-27.1`) emits this name too,
   * whenever the attachment being deleted is the card's cover —
   * `FR-CARD-024` AC4, "given an attachment used as the card cover, when it is
   * deleted, then the cover is cleared". That is not a courtesy: measured,
   * `fk_cards_cover_attachment_id` is `ON DELETE SET NULL`, so the database
   * nulls `cards.cover_attachment_id` and **leaves `cover_source =
   * 'attachment'` and `cover_type = 'image'` behind** — the off-model row
   * `./card-cover.ts`'s `cardCoverSchema` docstring is written against, which
   * 500s the whole of `GET /boards/:id/contents` rather than one card. B27's
   * handler therefore clears all three columns in the same transaction and
   * emits this event, and `attachment.removed`'s payload carries
   * `coverCleared: true` as the cross-reference. **A block adding a third path
   * that can orphan a cover column owes the same pair.**
   *
   * Emitted **only when a cover existed** — `cards.cover_type <> 'none'`
   * read as the pre-image inside the same transaction, never `coverChanged`
   * against the cleared-columns shape (a card that never had a cover, or
   * one already cleared, must write and emit nothing; see the route).
   *
   * `true`, symmetrically with `cover.set`.
   *
   * Payload: `{ previousSource, previousColour, previousPhotoRef,
   * previousAttachmentId, previousSize, previousTextTreatment }` — the
   * WHOLE prior cover, unlike `cover.set`'s six-field "current state plus
   * two previous facts". Removal is the irrecoverable direction: once the
   * columns are cleared, nothing on `cards` says what the cover was, so
   * SC-1's audit argument ("an event history that was never recorded
   * cannot be recovered") is answered in full here rather than partially,
   * the way `cover.set`'s payload can afford to be because the row itself
   * still holds the current state a moment after the event is written.
   */
  'cover.removed': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Checklists — B26, `BE-26.1` (`FR-CARD-016`, the checklist), `BE-26.2`
  // (`FR-CARD-017`, its items) and `BE-26.3` (`FR-CARD-019`, the copy).
  // `apps/server/src/routes/checklists.ts` and
  // `apps/server/src/routes/checklist-items.ts`.
  //
  // ELEVEN names, the largest vocabulary any single block in this product
  // has added, and the first block since B15 whose subject is a *subsystem*
  // rather than a field. Asked on the three-question framework the two DDL
  // paragraphs in `CLAUDE.md` state:
  //
  // Q1 — does this block emit a name of its own? YES, ELEVEN.
  //
  // Q2 — for each name, is it already in the live constraint? PARTIAL —
  // FIVE yes, SIX no, checked ONE NAME AT A TIME in the dotted spelling
  // against live `molt_v1` (`pg_get_constraintdef` on
  // `ck_activity_entries_type`, 67 names before this block) rather than by
  // prefix or by count. That is B09's, B11's, B15's, B16's and B22's shape
  // arriving a SIXTH time, and no ordinal is claimed for it: *partial* is
  // the normal case here. The five reused (`checklist.added`,
  // `checklist.removed`, `checklist.item_added`,
  // `checklist.item_completed`, `checklist.item_uncompleted`) are `0004`
  // placeholders dotted by `0013`, unemitted by anything until now. The six
  // absent (`checklist.renamed`, `checklist.position_changed`,
  // `checklist.item_edited`, `checklist.item_removed`,
  // `checklist.item_position_changed`, `checklist.item_moved`) existed in
  // NO spelling and needed
  // `db/postgres/migrations/0021_checklist_activity.sql`, which takes the
  // directory to TWENTY-ONE files and the Postgres-only `activity_entries`
  // CHECK-change count from nine to TEN.
  //
  // ⚠⚠ THE EVIDENCE TRAP, AND IT IS THE ONE THIS FILE'S RULE WAS EXTRACTED
  // FOR. A bare `grep checklist` against the pre-`0021` constraint returns
  // SIX hits and every one of them is genuinely this product's own — no
  // decoy, no near-namesake, nothing borrowed. But only FIVE are B26's: the
  // sixth is `checklist.item_converted_to_card`, `FR-CARD-021`/`BE-62.2`,
  // which this block neither emits nor registers. So the count is LARGER
  // than the block's own share, and a reader counting hits against the
  // BLOCK gets a confirmation ("six names, I have checklists, nothing to
  // do") precisely where they should get a warning — and ships six names
  // into a runtime `ck_activity_entries_type` violation, one per route, on
  // the first rename, item edit, item delete, reorder and cross-checklist
  // drag. `0021`'s header names this as the sixth distinct collision shape:
  // a count inflated by a name belonging to a block that does not exist
  // yet. The rule that survives it is the file's standing one: grep each
  // name INDIVIDUALLY, count the hits against the NAMES.
  //
  // Q3 — does this block need a structure the database does not already
  // have? NO, verified against the live catalogue rather than assumed.
  // `idx_checklists_card_id_position` and
  // `idx_checklist_items_checklist_id_position` (both `0003`) serve every
  // ordered read this block performs, including the card back's checklists
  // and the aggregate `checklistItemCount` / `checklistCheckedItemCount`
  // the card front carries; every write is a PK- or FK-keyed lookup.
  // `0021` is therefore a vocabulary migration and nothing else — no table,
  // no column, no index. `checklists`, `checklist_items` AND
  // `checklist_hide_checked_prefs` have all existed since
  // `0003_card_detail.sql`, with both `position` columns already
  // `varchar(64) COLLATE "C"`.
  //
  // Registered here — a SEPARATE act from the constraint admitting the
  // name, on B21's recorded lesson. The constraint has held five of these
  // since `0004`; this registry withheld them until the block that first
  // emits one claimed it, and it continues to withhold
  // `checklist.item_converted_to_card` for exactly the same reason.
  //
  // ⚠ ONE MUTATION IN THIS BLOCK EMITS NOTHING, and it is named here rather
  // than left to be noticed: `PUT /checklists/:id/hide-checked`
  // (`FR-CARD-018`) writes `checklist_hide_checked_prefs` and registers no
  // event. The preference is per user per checklist and "must not change
  // what other members see" — that requirement's own edge case — so there
  // is nothing for another member to audit and nobody to reconstruct it
  // for; SC-1's argument is about facts other people can observe and
  // dispute. That is B17's argument for `notifications.read_at` arriving on
  // a second per-user column. `ck_activity_entries_scope` would refuse such
  // a row anyway (it names a checklist and a user and no card or board of
  // its own), which CONFIRMS the decision and must not lead it.
  //
  // ⚠ EVENT VOLUME IS ASYMMETRIC BETWEEN TWO GESTURES THAT BOTH PRODUCE
  // MANY ITEMS. A multi-line paste emits N `checklist.item_added` rows, one
  // per line (`FR-CARD-017`: "Pasting multi-line text creates one item per
  // line") — N independent facts a feed renders individually and the
  // `FR-AUTO-*` matcher reacts to individually. A COPY
  // (`FR-CARD-019`, `sourceChecklistId` on `POST /cards/:id/checklists`)
  // emits ONE `checklist.added` naming the source and the item count and NO
  // per-item events, because the checklist arrived whole as one gesture and
  // N rows would attribute to the copier the typing of items they did not
  // type. Symmetrically, `DELETE /checklists/:id` emits ONE
  // `checklist.removed` and no per-item storm. Both halves are B15's
  // `label.deleted` reasoning, once on a creation and once on a deletion.
  //
  // ⚠ ALL ELEVEN ARE `surfacesInFeed: true`, WHICH IS UNUSUAL ENOUGH TO BE
  // WORTH STATING AS A DECISION RATHER THAN LEFT TO LOOK LIKE A DEFAULT.
  // Two of them were argued at length before landing there and both
  // docstrings carry the argument: `checklist.item_edited` borrows its NAME
  // from `comment.edited` (`false`) and pointedly not its flag, because
  // that entry's `false` rests on the "(edited)" marker `FR-COLLAB-002`
  // puts beside the comment and a checklist item has no such second
  // surface; and `checklist.removed` is `true` where `board.deleted`,
  // `list.deleted` and `card.deleted` are all `false`, because their shared
  // reason is that the deleted subject IS the feed's own container, which
  // is not true of a checklist on a surviving card (`label.deleted`'s
  // footing). Neither is a departure from precedent — both are the correct
  // precedent applied instead of the nearest-looking one.
  // ---------------------------------------------------------------------

  /**
   * A checklist was added to a card — `FR-CARD-016` (`BE-26.1`) and
   * `FR-CARD-019` (`BE-26.3`), both through `POST /cards/:id/checklists`.
   *
   * A `0004` placeholder (`checklist_added`), dotted by `0013`, unemitted
   * by anything until now — so it needed no DDL, which is B12's reason
   * arriving again. 15 characters.
   *
   * `true`. `FR-CARD-016` AC2 makes a *rename* explicitly activity-worthy
   * ("recorded in the card's activity"), and a feed that reported renames
   * of a thing whose creation it never mentioned would be unreadable; this
   * is `list.created`'s and `card.created`'s footing one level down — the
   * first line of the checklist's own history and the entry that explains
   * where every item event after it happened.
   *
   * ⚠ **ONE ROW FOR A COPY, NOT ONE PER ITEM.** `FR-CARD-019`'s copy is a
   * single gesture, so the payload carries `sourceChecklistId` and
   * `copiedItemCount` and no `checklist.item_added` is written for any of
   * the copied items. `sourceChecklistId` is `null` and `copiedItemCount`
   * is `0` on an ordinary add — the two keys are always present, never
   * variant-shaped, on `cover.set`'s rule that a feed renderer must not
   * have to reparse a variant out of optional keys.
   *
   * Payload: `{ checklistId, title, position, sourceChecklistId,
   * copiedItemCount }`. `cardId` and `boardId` are on the COLUMNS, never
   * the payload — `publishEvents` fans out on the `boardId` column alone,
   * so an event missing it is written, satisfies the scope constraint on
   * `cardId`, and never reaches the board room B18 built (B22's recorded
   * lesson; it would work over a reload and never over the live socket).
   * The checklist id is in `payload` because `activity_entries` has no
   * `checklist_id` column — `0012`'s trade for the workspace id, `0016`'s
   * for the list, `0017`'s for the label, `0018`'s for the comment.
   */
  'checklist.added': { surfacesInFeed: true },
  /**
   * A checklist's title was changed — `FR-CARD-016`, `BE-26.1`,
   * `PATCH /checklists/:id/title`.
   *
   * ⚠ **NEW IN `0021`** — in the live constraint in no spelling before it.
   * 17 characters. Named on `list.renamed` / `label.renamed` /
   * `board.renamed`: one editable name on the row, so no field segment.
   *
   * `true`, **by the requirement rather than by judgement**, which is
   * `board.renamed`'s and `list.renamed`'s footing and the strongest any
   * event in this block stands on. `FR-CARD-016` AC2 is explicit: "when the
   * user renames the first, then the new title is shown on the card back
   * **and recorded in the card's activity**."
   *
   * ⚠ **A no-op emits nothing.** Renaming a checklist to the title it
   * already has is legal and idempotent and must write no row — B12's
   * drop-in-place lesson, decided by comparing the trimmed incoming title
   * against the pre-image inside the transaction, never against the raw
   * request body.
   *
   * Payload: `{ checklistId, previousTitle, title }`. `previousTitle`
   * because `activity_entries` is append-only with no backfill and B28
   * cannot render "renamed Design to Deployment" from one value against a
   * row that has since been renamed again — `board.visibility_changed`'s
   * and `card.position_changed`'s rule.
   */
  'checklist.renamed': { surfacesInFeed: true },
  /**
   * A checklist was reordered among the card's other checklists —
   * `FR-CARD-016`, `BE-26.1`, `POST /checklists/:id/move`.
   *
   * ⚠ **NEW IN `0021`** — 26 characters. Modelled on
   * `list.position_changed` and `card.position_changed` and, like them,
   * **not a reuse of either**: a checklist's rank among a card's checklists
   * is a different fact about a different table, and this registry is keyed
   * by the full dotted string.
   *
   * `true`, and the judgement worth stating is the one *against*
   * suppressing it as noise — `card.position_changed`'s docstring makes
   * exactly this argument one level up and it is followed here rather than
   * re-derived: `checklists.position` is a shared column on a shared card
   * back, so every member sees the same order and a reorder is a change to
   * shared state, however ordinary. ⚠ The counter-argument, and why it is
   * rejected: `FR-CARD-016`'s edge case says "reordering checklists changes
   * only display order; it does not affect item state". That sentence
   * separates order from ITEM STATE, not order from SHARED state — it is
   * there to stop a reorder being read as touching what is ticked, and it
   * says nothing about the order being private. There is no per-user
   * checklist order in this product; `FR-CARD-018`'s hide-checked
   * preference is the only per-user display state a checklist has, and that
   * one emits nothing at all.
   *
   * ⚠ **A drop in place emits nothing** — `FR-BOARD-025`'s edge case
   * applied to this subject by analogy rather than by its own words:
   * detected before any write by comparing the checklist's current rank
   * against the *resolved* gap between `previousChecklistId` and
   * `nextChecklistId`, never against the raw body.
   *
   * Payload: `{ checklistId, title, previousPosition, position }`. Both
   * ranks, on `card.position_changed`'s reasoning — a consumer cannot tell
   * "moved up" from "moved down" from a single value. `title` rides along
   * so the entry stays renderable after the checklist is deleted (a hard
   * delete; see `checklist.removed`).
   */
  'checklist.position_changed': { surfacesInFeed: true },
  /**
   * A checklist was deleted from a card — `FR-CARD-016`, `BE-26.1`,
   * `DELETE /checklists/:id`.
   *
   * A `0004` placeholder (`checklist_removed`), dotted by `0013`, so no
   * migration was needed for it. 17 characters. ⚠ Note the *name*: `0004`
   * spelt this `removed` rather than `deleted`, and `0021` follows it for
   * `checklist.item_removed` to match — in this constraint `deleted` has
   * come to mean the three subject-level deletes (`board.deleted`,
   * `list.deleted`, `card.deleted`), and a checklist is a member of a
   * collection on a card.
   *
   * ⚠ **`true`, and this is the one `surfacesInFeed` in this block where
   * the deletion precedents pull in two directions, so read the split
   * rather than the pattern.** `board.deleted`, `list.deleted` and
   * `card.deleted` are all `false`, and their shared reason is *not* that
   * they are deletions: it is that the subject they delete **is the feed's
   * own container**, so the entry would sit in a feed nobody can open —
   * "an audit record, not a feed entry", as `card.deleted` puts it.
   * Nothing of the sort happens here. The card survives, its activity
   * section survives, and the members reading it are precisely the people
   * who want to know that a checklist with fourteen items vanished from
   * the card they are working on. That is `label.deleted`'s `true`,
   * arriving on a second hard delete, and its docstring already states the
   * distinction from the other side ("a board's feed stays reachable after
   * one label vanishes from it").
   *
   * ⚠ **This is a HARD delete and the second in the product.**
   * `FR-CARD-016`'s edge case: "deletion is permanent — checklists are not
   * archived and cannot be restored." There is no `checklists.deleted_at`,
   * no `isLiveChecklist()`, and there is deliberately no
   * `checklist.restored` — the deletion is irreversible by requirement, so
   * unlike `list.archived`/`list.unarchived` there is no lifecycle for a
   * second name to complete. `label.deleted`'s reasoning transfers whole,
   * including why it is safe: `activity_entries` has **no `checklist_id`
   * column and no foreign key to `checklists`**, so removing the row
   * touches not one entry — this one included. It survives its own
   * subject.
   *
   * ⚠ **ONE ROW, AND NO PER-ITEM `checklist.item_removed` STORM.**
   * `fk_checklist_items_checklist_id` is `ON DELETE CASCADE`, so the items
   * go in the database and the application never sees them. A handler that
   * read them first and emitted one `checklist.item_removed` each would
   * pass every visible assertion — 204, the items are gone — while writing
   * N rows describing removals nobody performed, which B28 would render as
   * a member deleting fourteen items one at a time.
   *
   * Payload: `{ checklistId, title, itemCount, checkedItemCount }` — read
   * inside the transaction *before* the delete. It carries more than
   * `board.deleted` and `list.deleted` do, for `label.deleted`'s reason:
   * those can lean on a soft-deleted row still being there to join to, and
   * this cannot. The two counts are the whole record of what was lost and
   * are what makes `00-`'s second principle (tell the user what they are
   * about to lose) auditable after the fact as well as before it.
   */
  'checklist.removed': { surfacesInFeed: true },
  /**
   * An item was added to a checklist — `FR-CARD-017`, `BE-26.2`,
   * `POST /checklists/:id/items`.
   *
   * A `0004` placeholder (`checklist_item_added`), dotted by `0013`, so no
   * migration was needed for it. 20 characters.
   *
   * `true`, on `FR-COLLAB-020` and on the plain observation that a
   * checklist item is the smallest unit of *work* this product models —
   * `FR-CARD-017`'s whole user story is "so that I can track progress
   * inside a single card", and a feed that showed items being ticked but
   * never appearing could not explain where the denominator came from.
   *
   * ⚠ **ONE ROW PER ITEM ON A MULTI-LINE PASTE, N ROWS FOR N LINES.**
   * `FR-CARD-017`: "Pasting multi-line text creates one item per line", and
   * its AC2 makes the three pasted lines three separate items. They are N
   * independent facts — a feed renders them individually and the
   * `FR-AUTO-*` matcher must be able to fire per item — so this is
   * deliberately NOT the copy's shape. ⚠ **The copy is the other half of
   * that asymmetry and emits none of these**: `FR-CARD-019`'s
   * `sourceChecklistId` produces one `checklist.added` and no item events
   * at all. Do not "harmonise" the two; they are one gesture each, and the
   * gesture is what an activity entry records.
   *
   * ⚠ **A blank line creates no item and therefore emits nothing.**
   * `FR-CARD-017`'s edge case ("an empty item cannot be created; blank
   * input is ignored") means blank lines are skipped rather than stored,
   * so a three-line paste with one blank line writes two rows, not three,
   * and a wholly blank body writes none and is refused outright.
   *
   * Payload: `{ checklistId, checklistTitle, itemId, text, position }`.
   * `checklistTitle` is the title **as it stood when the item was added**
   * and is what keeps the entry renderable after the checklist is hard-
   * deleted — see `checklist.removed`, and see `0021`'s note on the orphan
   * this knowingly creates, which is `0018`'s comment orphan in a second
   * costume and B28's to drop at render time.
   */
  'checklist.item_added': { surfacesInFeed: true },
  /**
   * A checklist item's text was rewritten — `FR-CARD-017`, `BE-26.2`,
   * `PATCH /checklist-items/:id/text`.
   *
   * ⚠ **NEW IN `0021`** — 21 characters. Named on `comment.edited`
   * (`0018`) rather than on `card.renamed`: an item **is** its text, there
   * is no second editable column on the row, so `checklist.item_renamed`
   * would call a body of free text a name and
   * `checklist.item_text_edited` would carry a discriminator with nothing
   * to discriminate against.
   *
   * ⚠ **`true`, and the entry it is NOT modelled on is the interesting
   * part, because the name is borrowed from `comment.edited` and the flag
   * deliberately is not.** `comment.edited` is `false` on a reason that
   * does not transfer: `FR-COLLAB-002`'s surfacing mechanism is the
   * **"(edited)" marker on the comment itself**, read from
   * `comments.edited_at`, and `FR-COLLAB-017` AC4 ("only comments are
   * mutable") makes comment mutability a property handled *beside* the
   * comment rather than as a feed entry. **A checklist item has no such
   * marker and no such second surface.** `FR-CARD-017` describes items as
   * "editable inline" and specifies no edited-marker, no edit history and
   * no other place the fact could appear; `checklist_items.updated_at` is
   * a trigger-owned housekeeping column no requirement renders. So this is
   * `card.description_edited`'s case exactly — free text with no in-place
   * marker, where the feed is the only place an edit can appear — and that
   * entry's own docstring states the test from the other side: "a
   * description edit has no in-place marker and no other surface, so the
   * feed is the only place it can appear; a comment edit has one."
   *
   * ⚠ **The second argument, and the one that would decide it alone:
   * `checklist.item_added` above carries the item's `text` into the feed.**
   * If an edit were silent, the feed's last word on that item would be its
   * ORIGINAL wording, asserted as fact and never corrected — worse than a
   * noisy feed, because it is a confidently wrong one. `card.description_
   * edited` does not have this problem to the same degree (`card.created`
   * carries a title, not a description), which makes the case here stronger
   * than the precedent it follows rather than weaker.
   *
   * ⚠ The volume objection was considered and rejected on this block's own
   * consistency: `checklist.item_position_changed` below is `true`, and a
   * card whose fifty items are dragged about produces more entries than one
   * whose fifty items are typo-corrected. Suppressing the edit while
   * surfacing the drag would say that where a line of work sits matters and
   * what it says does not. If feed volume is ever judged a real problem,
   * the three granular item events should be reconsidered together rather
   * than one of them singled out now.
   *
   * ⚠ **`surfacesInFeed` would not have suppressed the row or the
   * broadcast in any case** — it is the §2.6 feed discriminator and nothing
   * else reads it; `publishEvents` does not consult it. Recorded here so
   * that a later block reversing this judgement understands what it is and
   * is not changing.
   *
   * ⚠ **A no-op emits nothing**: an item patched to the text it already
   * carries writes no row, compared after trimming and against the
   * pre-image inside the transaction.
   *
   * Payload: `{ checklistId, itemId, previousText, text }` —
   * `previousText` because this table is append-only with no backfill and
   * the prior wording is unrecoverable the moment the update lands, which
   * is the whole of SC-1's audit argument for an edit.
   */
  'checklist.item_edited': { surfacesInFeed: true },
  /**
   * A checklist item was ticked — `FR-CARD-017`, `BE-26.2`,
   * `PATCH /checklist-items/:id/checked` with `checked: true`.
   *
   * A `0004` placeholder (`checklist_item_completed`), dotted by `0013`, so
   * no migration was needed for it. 24 characters.
   *
   * `true`. This is the state change the whole subsystem exists to record:
   * `FR-CARD-018`'s progress counter, its card-front badge and
   * `FR-CARD-017` AC3 ("then it becomes checked and the checklist's
   * progress recalculates immediately") are all readings of this one fact,
   * and a member returning to a card asks who ticked it before anything
   * else. It is a toggle pair with one name per direction —
   * `due.date_completed` / `due.date_uncompleted`'s shape and
   * `list.archived` / `list.unarchived`'s before it — so neither row
   * carries a `checked` or `previousChecked` field: the type itself says
   * which way the toggle went, and a consumer branching on the payload for
   * that would be doing what this registry exists to stop.
   *
   * ⚠ **A no-op emits nothing.** Ticking an already-ticked item is legal
   * and idempotent (two members racing one checkbox) and must write no row,
   * decided by `UPDATE … WHERE checked <> $next RETURNING` rather than by
   * SELECT-then-write — `due.date_completed`'s precedent exactly, one
   * costume over. A handler that emitted unconditionally would pass every
   * visible assertion while filling an append-only feed with phantom ticks.
   *
   * ⚠ **Ticking an item is NOT completing the card**, and nothing here
   * touches `cards.completed` or emits `due.date_completed`.
   * `FR-CARD-017`'s edge case is explicit that the two are independent (see
   * `FR-CARD-004`), so a checklist reaching 100% leaves the card's own
   * completion exactly where it was.
   *
   * Payload: `{ checklistId, checklistTitle, itemId, text }` — enough for
   * B28 to render "ticked *Write the migration* in **Design**" from one
   * row, with `checklistTitle` and `text` captured at the moment of the
   * tick for the reason `checklist.item_added`'s docstring gives.
   */
  'checklist.item_completed': { surfacesInFeed: true },
  /**
   * A checklist item was un-ticked — `FR-CARD-017`, `BE-26.2`,
   * `PATCH /checklist-items/:id/checked` with `checked: false`.
   *
   * A `0004` placeholder (`checklist_item_uncompleted`), dotted by `0013`,
   * so no migration was needed for it either. 26 characters.
   *
   * `true`, symmetrically with `checklist.item_completed`: the pair is one
   * toggle and a feed showing only half of it would read as though a ticked
   * item can never come back — `list.unarchived`'s and
   * `due.date_uncompleted`'s footing. Same payload shape, same no-op rule
   * (an already-unticked item asked to un-tick writes and emits nothing).
   */
  'checklist.item_uncompleted': { surfacesInFeed: true },
  /**
   * An item was reordered **within its own checklist** — `FR-CARD-017`,
   * `BE-26.2`, `POST /checklist-items/:id/move`.
   *
   * ⚠ **NEW IN `0021`** — 31 characters, the longest name this block adds
   * and one under `EVENT_TYPE_MAX_LENGTH`. Modelled on
   * `card.position_changed` and `list.position_changed` and a reuse of
   * neither, for the reason `checklist.position_changed` above gives.
   *
   * `true`, following `card.position_changed`'s explicit argument against
   * suppressing a reorder as noise: `checklist_items.position` is shared
   * state on a shared card back. This is the most granular event in the
   * product and the case for `false` is therefore at its strongest here —
   * it is declined for consistency with the two `position_changed` events
   * already registered rather than on a fresh argument, and a later block
   * that decides feed volume is a real problem should change all three
   * together rather than singling this one out.
   *
   * ⚠ **A drop in place emits nothing** — the same detection
   * `checklist.position_changed` describes, against the resolved gap rather
   * than the request body.
   *
   * Payload: `{ checklistId, itemId, text, previousPosition, position }`.
   *
   * ⚠⚠ **THIS IS NOT `checklist.item_moved`, AND THE SPLIT IS THE POINT.**
   * See that entry, immediately below, for the argument; the two names come
   * off ONE route, exactly as `card.position_changed` and
   * `card.moved_between_lists` come off `POST /cards/:id/move`.
   */
  'checklist.item_position_changed': { surfacesInFeed: true },
  /**
   * An item moved **to a different checklist on the same card** —
   * `FR-CARD-017`, `BE-26.2`, `POST /checklist-items/:id/move`.
   *
   * ⚠ **NEW IN `0021`** — 20 characters. It is short because it has to be:
   * `checklist.item_moved_between_checklists` is 39 and
   * `checklist.item_moved_between_lists` is 34, both refused by
   * `activity_entries.type varchar(32)` and the second wrong besides. This
   * family's noun is long enough that the naming budget is genuinely tight;
   * `0021`'s header records it, and the ceiling is currently occupied by
   * `checklist.item_converted_to_card` at exactly 32.
   *
   * `true`, and more strongly than its reorder sibling: `FR-CARD-017` AC4
   * describes this gesture in full ("given an item in checklist A, when the
   * user drags it into checklist B on the same card, then it is removed
   * from A, appears in B at the drop position, and both progress counters
   * update"), and moving work between phases is the kind of change another
   * member is waiting on.
   *
   * ⚠⚠ **WHY A REORDER AND A MOVE ARE TWO NAMES ON ONE ROUTE — B12's
   * DECISION INHERITED WHOLE, NOT A NEW ONE.** `POST /checklist-items/:id/
   * move` serves both gestures exactly as `POST /cards/:id/move` serves
   * both of B12's, and the FEED must render them differently: "Ada moved
   * this item to Deployment" is a change to how the work is grouped, "Ada
   * reordered this item" is housekeeping. A reader cannot recover which
   * happened from a single name plus a payload without switching on
   * `payload`, which is the thing this registry exists to stop (see the
   * profile block at the top of the file). The `FR-AUTO-*` matcher matches
   * on `type`, so the split is load-bearing there too — "when an item moves
   * into checklist X" must not fire on a drag inside the checklist it is
   * already in.
   *
   * ⚠ **There is no cross-CARD item move and no name for one.**
   * `FR-CARD-017` confines the drag to "checklists on the same card", so
   * the route refuses a destination checklist on another card and this
   * event's two checklist ids always belong to one card — which is what
   * lets `cardId` and `boardId` stay single values on the columns, unlike
   * `card.moved_between_boards`, which has to be written twice per gesture
   * because its two boards are two rooms.
   *
   * Payload: `{ itemId, text, previousChecklistId, previousChecklistTitle,
   * checklistId, checklistTitle, previousPosition, position }`. Both
   * checklists by id AND by title, because B28 renders "moved *Write the
   * migration* from **Design** to **Deployment**" from one row and neither
   * title is recoverable if either checklist is later hard-deleted. The
   * discriminator against the reorder above is the event type and nothing
   * else; there is deliberately no `direction` or `kind` field, which would
   * be a second encoding of a fact the row already carries.
   */
  'checklist.item_moved': { surfacesInFeed: true },
  /**
   * A checklist item was deleted — `FR-CARD-017`, `BE-26.2`,
   * `DELETE /checklist-items/:id`.
   *
   * ⚠ **NEW IN `0021`** — 22 characters. `removed`, not `deleted`, to pair
   * with `checklist.removed` (which `0004` spelt that way) and because in
   * this constraint `deleted` names the three subject-level deletes; an
   * item is a member of a collection.
   *
   * `true`, on `checklist.removed`'s reasoning one level down: the deletion
   * is permanent ("individually deletable (permanent)", `FR-CARD-017`), the
   * card and its feed both survive it, and the item's text exists nowhere
   * else afterwards. `label.deleted`'s footing, not `card.deleted`'s.
   *
   * ⚠ **Emitted by the item route ONLY.** Deleting the parent checklist
   * writes ONE `checklist.removed` and none of these, because
   * `fk_checklist_items_checklist_id` is `ON DELETE CASCADE` and the
   * application never sees the rows — see `checklist.removed`, where the
   * argument is made in full. A block that later adds a bulk "clear checked
   * items" gesture inherits the same question and should answer it the same
   * way: one gesture, one row.
   *
   * Payload: `{ checklistId, itemId, text, checked }` — the text because it
   * is the only place that identity survives the commit, and `checked`
   * because deleting a ticked item changes the checklist's denominator AND
   * its numerator, and `FR-CARD-018`'s counter cannot be reconstructed from
   * a row that says only that something was removed.
   */
  'checklist.item_removed': { surfacesInFeed: true },
  'checklist.item_converted_to_card': { surfacesInFeed: true },
  'checklist.item_assigned': { surfacesInFeed: true },
  'checklist.item_unassigned': { surfacesInFeed: true },
  'checklist.item_due_date_set': { surfacesInFeed: true },
  'checklist.item_due_date_changed': { surfacesInFeed: true },
  'checklist.item_due_date_removed': { surfacesInFeed: true },

  // ---------------------------------------------------------------------
  // Attachments and related cards — B27, `BE-27.1` (`FR-CARD-022`, a file),
  // `BE-27.2` (the authorised download, SC-3), `BE-27.3` (`FR-CARD-023`, a
  // link), `BE-27.4` (`FR-COLLAB-005`, a comment attachment) and `BE-27.5`
  // (`FR-TMPL-028`, related cards).
  // `apps/server/src/routes/attachments.ts`,
  // `apps/server/src/routes/card-relations.ts` and
  // `apps/server/src/routes/comment-attachments.ts`.
  //
  // FIVE names, and the first block in this registry whose vocabulary spans
  // TWO PREFIXES. Asked on the three-question framework `CLAUDE.md` states:
  //
  // Q1 — does this block emit a name of its own? YES, FIVE.
  //
  // Q2 — for each name, is it already in the live constraint? PARTIAL — TWO
  // yes, THREE no, checked ONE NAME AT A TIME in the dotted spelling against
  // live `molt_v1` (`pg_get_constraintdef` on `ck_activity_entries_type`, 73
  // names before this block) rather than by prefix. `attachment.added` and
  // `attachment.removed` are `0004` placeholders dotted by `0013`, unemitted
  // by anything until B27, and reused exactly as they stand;
  // `attachment.renamed`, `card.relation_added` and `card.relation_removed`
  // existed in NO spelling and needed
  // `db/postgres/migrations/0022_attachment_activity.sql`, which takes the
  // directory to TWENTY-TWO files and the Postgres-only CHECK-change count
  // to TEN. That is B09's / B11's / B15's / B16's / B22's *partial* shape
  // arriving a sixth time, and no ordinal is claimed for it: partial is the
  // normal case in this product, not a rare one.
  //
  // ⚠⚠ **THE COLLISION SHAPE IS NEW EVEN THOUGH THE ANSWER SHAPE IS NOT,
  // AND IT IS THE ONLY THING ABOUT B27's VOCABULARY WORTH MEMORISING.** Both
  // prefix greps come back complete AND all-yours, and their union still
  // misses three names. `grep 'attachment.'` returns exactly TWO hits, both
  // genuinely B27's, neither a decoy — and that set is not merely
  // complete-LOOKING, it is a complete account of the `attachment.` prefix.
  // `grep 'card.'` returned THIRTEEN before this block, every one genuinely
  // this product's own, and not one of them a relation. Two individually
  // reassuring sets; fifteen names; three still missing. `0022`'s header
  // names this as the SEVENTH distinct collision shape and argues it at
  // length. The only question that survives it is the standing one: is every
  // name I emit a hit, asked one name at a time — never once per prefix.
  //
  // ⚠ AND THE REQUIREMENT PUSHES A READER TOWARDS THE WRONG PREFIX.
  // `FR-TMPL-028` is titled "Attach a card to another card" and its behaviour
  // text calls a related card an ATTACHMENT. A block reasoning from the prose
  // would emit `attachment.added` for a relation, ship no migration, and be
  // wrong twice: the constraint would accept it (so nothing reddens) and
  // `attachment.added`'s payload names an `attachments` row that a
  // `card_relations` row does not have — forcing `B28` to switch on `payload`
  // to learn which table a name refers to, which is what this registry exists
  // to stop (see the profile block at the top of this file, which rejected
  // `user.profile_updated` on the identical ground).
  //
  // Q3 — does this block need a structure the database does not already
  // have? NO, read from `pg_indexes` against the live catalogue rather than
  // assumed. `idx_attachments_card_id_created_at` serves both `FR-CARD-024`'s
  // most-recent-first listing and the card-front badge's grouped count
  // (`card_id` leading); `uq_card_relations_from_card_id_to_card_id` serves
  // the from-side listing and the reciprocal-existence probe;
  // `idx_card_relations_to_card_id` the reverse direction;
  // `idx_comment_attachments_comment_id` the per-comment fetch; and
  // `idx_cards_cover_attachment_id` the cover-clear lookup. Every access path
  // is a keyed lookup already served. `0022` adds no index, no column and no
  // table — `attachments` has existed since `0003`, `card_relations` and
  // `comment_attachments` since `0004`.
  //
  // SCOPE. All five carry `cardId` **and** `boardId` on the COLUMNS, so each
  // satisfies `ck_activity_entries_scope`'s first disjunct twice over and
  // `0022` added no by-name exemption. `boardId` is not politeness:
  // `publishEvents` fans out on the `boardId` column alone, so an event
  // carrying only `cardId` is written, passes the constraint, and never
  // reaches the board room B18 built — B22's D5, and it is sharpest here,
  // where a relation may legitimately span two boards. See
  // `card.relation_added` for which board each relation event carries.
  //
  // ⚠ ONE NAME THIS BLOCK EMITS IS NOT ITS OWN. `DELETE /attachments/:id`
  // also emits B25's `cover.removed` when the deleted attachment was the
  // card's cover (`FR-CARD-024` AC4) — see that entry, whose docstring is
  // corrected in place to name this second emitter.
  //
  // ⚠ TWO NAMES A READER WILL LOOK FOR AND NOT FIND. There is no
  // `comment.attachment_added` and no name of any kind for a comment
  // attachment of kind `card_link` or `board_link`: `POST
  // /comments/:id/attachments` emits `attachment.added` when and ONLY when
  // `kind='file'`, because that is the one kind that creates an `attachments`
  // row and joins the card's attachment section (`FR-COLLAB-005`). The other
  // two write a `comment_attachments` row and nothing else, and that row is
  // part of a comment whose creation `comment.added` already recorded. This
  // is the SYMMETRIC half of a decision this file already made on the removal
  // side — see `comment.deleted`, where comment attachments follow the row
  // through `ON DELETE CASCADE` and emit nothing, because a handler emitting
  // one `attachment.removed` each "would pass every visible assertion while
  // writing rows describing removals nobody performed". If a comment
  // attachment's destruction is not an event, its creation is not one either.
  // And `POST /cards/:id/attachments/presign` emits nothing because it writes
  // nothing: it signs a URL. An upload that is PUT and never confirmed leaves
  // an S3 object and no row, which is `FR-COLLAB-005` AC3's own promise.
  // ---------------------------------------------------------------------

  /**
   * A file or a link was attached to a card — `FR-CARD-022` (a file uploaded
   * from the device), `FR-CARD-023` (a URL, including a cloud-storage
   * document) and `FR-COLLAB-005` (a file attached to a comment, which joins
   * the card's attachment section). `BE-27.1`, `BE-27.3`, `BE-27.4`:
   * `POST /cards/:id/attachments` and `POST /comments/:id/attachments` with
   * `kind: 'file'`.
   *
   * A `0004` placeholder (`attachment_added`), dotted by `0013`, unemitted by
   * anything until B27 — so **no migration for this name**, and B27 is the
   * block that claims it. 16 characters.
   *
   * ⚠ **ONE NAME SERVES BOTH KINDS, and `kind` rides in the payload.** A
   * 250 MB upload and a pasted URL create the same `attachments` row, and the
   * feed sentence is the same verb either way ("Ada attached *report.pdf*").
   * `kind` is there so a renderer picks an icon, never so it picks a verb —
   * `cover.set`'s precedent, where one name serves three `source` variants,
   * rather than the profile block's rejected `user.profile_updated`, where
   * the payload would have had to choose the sentence.
   *
   * `true`. `FR-CARD-022` AC4 puts an attachment count badge on the card
   * front, so the fact is already board-visible at a glance; a feed silent on
   * it would leave "why did this card grow a paperclip" unanswerable.
   * `label.added`'s footing.
   *
   * Payload: `{ attachmentId, kind, displayName, sizeBytes, cloudProvider,
   * commentId }`. `displayName` because `DELETE /attachments/:id` is a HARD
   * delete (`FR-CARD-024`: "deleting an attachment is permanent; there is no
   * attachment archive") and nothing afterwards says what was attached —
   * `checklist.item_removed`'s reason arriving on the creation side.
   * `sizeBytes` is `null` for a link, which is the column's own shape.
   * `commentId` is `null` unless the attachment arrived through
   * `POST /comments/:id/attachments`, and it is the one key that lets `B28`
   * render "attached via a comment" without a second query.
   *
   * ⚠ **The payload carries NO url and NO storage key, on either kind.** For
   * `kind: 'file'` the storage key is private (SC-3: the only way to fetch
   * the object is `GET /attachments/:id/download`, which permission-checks
   * and `302`s to a ~60 second presigned GET), and an `activity_entries` row
   * is read by every board member for ever — a key in there is a durable copy
   * of exactly the thing the block exists to keep out of payloads. For
   * `kind: 'link'` the URL is omitted for symmetry and because it is
   * recoverable from the row while the row exists; a consumer that needs it
   * reads the attachment.
   */
  'attachment.added': { surfacesInFeed: true },
  /**
   * An attachment's display name was changed — `FR-CARD-024` AC3,
   * `BE-27.1`, `PATCH /attachments/:id/display-name`.
   *
   * ⚠ **NEW IN `0022`** — 18 characters, well inside
   * `EVENT_TYPE_MAX_LENGTH`. It existed in no spelling: `0004` gave an
   * attachment a birth and a death and no rename verb, while creating
   * `attachments.updated_at` and `trg_attachments_set_updated_at` in `0003`
   * for a row whose own comment says "FR-CARD-024 renames an attachment in
   * place, so the row is mutated in normal operation". The table and the
   * constraint have disagreed since `0004` and `0022` is where they stop —
   * `comment.edited`'s situation exactly, one noun over.
   *
   * ⚠ **`renamed`, not `edited`.** `comment.edited` is spelt that way because
   * a comment IS its body; an attachment is NOT its display name — the file
   * or URL underneath is untouched by a rename, and `FR-CARD-023`'s own edge
   * case ("renaming a document in the provider does not automatically rename
   * the attachment's display name") turns on exactly that separation. So this
   * follows `list.renamed` / `label.renamed` / `board.renamed` /
   * `checklist.renamed`: a title changed on a row with exactly one editable
   * name, hence no field segment.
   *
   * `true`, on `label.renamed`'s footing — `FR-CARD-024` AC3 is "the new
   * display name is shown to all board members", so the change is
   * board-visible by requirement and a feed silent on it would leave a
   * renamed attachment looking like a different one.
   *
   * Payload: `{ attachmentId, previousDisplayName, displayName }`. Both
   * values, on `board.visibility_changed`'s reason: `activity_entries` is
   * append-only with no backfill, and `B28` cannot render "renamed *draft*
   * to *final*" from one value without a second query against a row that may
   * since have been renamed again or hard-deleted.
   *
   * ⚠ **A rename to the identical string emits NOTHING**, detected against
   * the pre-image inside the transaction — `board.permission_changed`'s
   * no-op rule, and B14's. It is the *toggle* side of B16's boundary rather
   * than `comment.edited`'s explicit-Save side: a rename dialog pre-filled
   * with the current name and submitted unchanged is the ordinary accident,
   * and emitting on it fills an append-only feed with phantom renames while
   * passing every visible assertion.
   */
  'attachment.renamed': { surfacesInFeed: true },
  /**
   * An attachment was removed from a card — `FR-CARD-024`, `BE-27.1`,
   * `DELETE /attachments/:id`.
   *
   * A `0004` placeholder (`attachment_removed`), dotted by `0013`, unemitted
   * by anything until B27 — so **no migration for this name**. 18 characters.
   *
   * ⚠ **`removed`, and the deletion behind it is HARD** — `FR-CARD-024`'s
   * edge case is verbatim "deleting an attachment is permanent; there is no
   * attachment archive", `attachments` has no `deleted_at`, and no
   * `fk_activity_entries_*` points at `attachments`. So this row survives its
   * own subject, holding a `payload.attachmentId` that resolves to nothing —
   * `0018`'s comment orphan in a third costume, correct because SC-1 makes
   * this table append-only, and `B28` inherits the same render-time
   * obligation it already inherited for comments and checklists. Do not
   * "tidy" it with an `attachment_id` column and a cascade.
   *
   * `true`, symmetrically with `attachment.added`: the card front's badge
   * decrements (`FR-CARD-024`'s edge case says immediately), so the change is
   * board-visible and a feed showing only the additions would read as though
   * an attachment list can only grow.
   *
   * Payload: `{ attachmentId, kind, displayName, sizeBytes, coverCleared }`.
   * The first four are `attachment.added`'s, because removal is the
   * irrecoverable direction and after the commit nothing anywhere says what
   * was removed — `cover.removed`'s reasoning, which carries the whole prior
   * cover for the same reason.
   *
   * ⚠ **`coverCleared` is a boolean and not a second event's job.**
   * `FR-CARD-024` AC4: deleting an attachment used as the card cover clears
   * the cover. `fk_cards_cover_attachment_id` is `ON DELETE SET NULL`, so the
   * database nulls `cards.cover_attachment_id` **and leaves `cover_source =
   * 'attachment'` and `cover_type = 'image'` behind** — an off-model row that
   * 500s the WHOLE of `GET /boards/:id/contents` rather than one card (see
   * `./card-cover.ts`). The handler must therefore clear all three columns
   * itself, in the same transaction, and when it does it ALSO emits B25's
   * `cover.removed` — a second, separately-named fact rather than a flag,
   * because that event's payload is the only record anywhere of what the
   * cover was. `coverCleared` on THIS payload is the cross-reference that
   * lets `B28` render the two rows as one sentence; it is not the record.
   */
  'attachment.removed': { surfacesInFeed: true },
  /**
   * One card was attached to another — `FR-TMPL-028`, `BE-27.5`,
   * `POST /cards/:id/relations`.
   *
   * ⚠ **NEW IN `0022`** — 19 characters. Modelled on `checklist.item_added`:
   * a sub-noun of the parent with a table of its own, spelt
   * `<parent>.<subnoun>_<verb>`. The prefix is `card.` and **not**
   * `attachment.`, however hard `FR-TMPL-028`'s own prose pushes the other
   * way — see the block comment above, and `0022`'s header, which argues it
   * at length. It is not `relation.` either: that would be a fifteenth
   * top-level prefix for a row with no independent existence.
   *
   * `true`. `FR-TMPL-028` AC1 puts the relationship on card X's back where
   * every board member sees it, and AC4 ("given card Y is archived, when I
   * view card X, then the attachment reflects that card Y is archived") makes
   * it a live part of the card rather than a note.
   *
   * ⚠⚠ **ONE EVENT PER ROW, NOT ONE PER GESTURE — AND THIS IS EMPHATICALLY
   * NOT `card.moved_between_boards`'s DOUBLE-EMIT.** `reciprocal: true`
   * (`FR-TMPL-028`'s "connect cards") writes TWO `card_relations` rows and
   * emits this name TWICE: once scoped to (card X, X's board), once to
   * (card Y, Y's board). The resemblance to B21 is superficial and
   * misleading. B21 emits twice because ONE fact has TWO audiences — one card
   * moved, and `publishEvents` fans out on the `board_id` column alone, so
   * one row can only reach one room; its two payloads deliberately DIFFER
   * because they describe one fact from two sides. B27 emits twice because
   * there are TWO FACTS: card X now references card Y, and card Y now
   * references card X, each its own row with its own primary key, each
   * independently removable (`FR-TMPL-028`'s edge case), each with exactly
   * one audience. The two payloads have the SAME shape with `fromCardId` and
   * `toCardId` swapped. **`reciprocal: false` emits ONE event and that is not
   * half of anything** — a handler emitting a second "for the other side"
   * would announce a row that does not exist, on an append-only table, to a
   * board room whose members would then render a chip nothing backs.
   *
   * Payload: `{ fromCardId, toCardId, reciprocal }`. `boardId`/`cardId` are
   * on the COLUMNS and carry **`from_card_id`'s** board and card — the card
   * whose back gains the chip, hence the room that must repaint. A relation
   * may legitimately span two boards (`FR-TMPL-028` renders "the target
   * card's title, board, list and status"), so choosing the target's board
   * instead would broadcast a change to a room where nothing changed while
   * leaving the room where something did in the dark, with nothing red
   * anywhere. `reciprocal` records whether the mirror row was written in the
   * same gesture, which is not recoverable later: either side can be removed
   * independently afterwards.
   *
   * ⚠ **No title, no board name and no list name in the payload, for either
   * card.** B21's rule — no payload key names a row the audience of that
   * row's own `board_id` could not already see — bites hardest here, because
   * the target may sit on a board the source board's members cannot open.
   * `toCardId` itself is not a leak (it is what the row on this board already
   * names, and the chip is the feature), but a title would be:
   * `FR-COLLAB-005`'s edge case, applied here **by analogy** because
   * `FR-TMPL-028` has none of its own, requires a card the viewer cannot
   * access to render as "a card on a board you don't have access to", "never
   * leaking the title". A title in an append-only payload would defeat that
   * permanently. The read path resolves the chip behind a permission check;
   * the event names ids only.
   */
  'card.relation_added': { surfacesInFeed: true },
  /**
   * A card was detached from another — `FR-TMPL-028`, `BE-27.5`,
   * `DELETE /cards/:id/relations/:toCardId`.
   *
   * ⚠ **NEW IN `0022`** — 21 characters, the longest name B27 adds and eleven
   * under the ceiling. `removed`, not `deleted`, on `0021`'s rule: this
   * constraint reserves `deleted` for the soft deletes (`board.deleted`,
   * `list.deleted`) and the one hard subject delete it had to mint
   * (`card.deleted`), while `removed` has meant "taken out of a collection"
   * since `0004`.
   *
   * `true`, symmetrically with `card.relation_added`.
   *
   * ⚠⚠ **IT REMOVES ONE SIDE AND EMITS ONCE, AND THE ASYMMETRY IS THE
   * REQUIREMENT.** `FR-TMPL-028`'s edge case is verbatim: "removing an
   * attachment on one side does not remove the reciprocal one; that asymmetry
   * should be surfaced to the actor." So this route deletes exactly the
   * `(from_card_id, to_card_id)` row named in the path, never the mirror, and
   * emits exactly one event on `from_card_id`'s board. A handler that
   * "helpfully" removed both would satisfy every symmetry intuition, pass any
   * test that only checks card X, and silently contradict the one sentence in
   * the corpus that addresses this case.
   *
   * Payload: `{ fromCardId, toCardId, reciprocalSurvives }`. The third key is
   * what `FE-27.4` renders the asymmetry from — whether the mirror row
   * `(to, from)` still exists after this delete — computed inside the same
   * transaction, because a second read afterwards can disagree. Ids only, no
   * titles, for `card.relation_added`'s reason.
   */
  'card.relation_removed': { surfacesInFeed: true },
  'card.mirror_created': { surfacesInFeed: true },
  'card.mirror_removed': { surfacesInFeed: true },

  /**
   * Sticker placement and library changes — B58, `FR-CARD-032` and
   * `FR-EXP-027`. They are board-scoped so sockets refresh open boards; the
   * placement events are also card-scoped. Upload itself stays out of the
   * human feed because no card changed yet.
   */
  'sticker.uploaded': { surfacesInFeed: false },
  'sticker.placed': { surfacesInFeed: true },
  'sticker.moved': { surfacesInFeed: true },
  'sticker.removed': { surfacesInFeed: true },

  /**
   * Power-Up lifecycle — B72, `FR-AUTO-024` through `FR-AUTO-028`.
   *
   * These are board-scoped live-update events for generic Power-Up framework
   * state. They are not user-visible activity-feed rows in this backend slice;
   * the route that emits one must still set activity_entries.board_id so the
   * Redis board-room fan-out reaches open clients.
   */
  'power_up.enabled': { surfacesInFeed: false },
  'power_up.disabled': { surfacesInFeed: false },
  'power_up.configured': { surfacesInFeed: false },
  'power_up.button_visibility_set': { surfacesInFeed: false },

  /**
   * Custom-field definition and dropdown-option management — `FR-CARD-026`,
   * `BE-56.1`.
   *
   * These are board-scoped vocabulary changes, so every emitted row carries
   * `boardId` and broadcasts. They are not added to the user-visible activity
   * feed in this backend slice; no activity sentence stack ships with B56.
   */
  'custom.field_created': { surfacesInFeed: false },
  'custom.field_changed': { surfacesInFeed: false },
  'custom.field_deleted': { surfacesInFeed: false },
  'custom.option_created': { surfacesInFeed: false },
  'custom.option_changed': { surfacesInFeed: false },
  'custom.option_deleted': { surfacesInFeed: false },

  /**
   * A board-defined custom field value changed on a card — `FR-CARD-027`,
   * `BE-56.2`.
   *
   * `false` deliberately: this backend slice needs the durable row and the
   * board-room broadcast, but it does not ship the activity-feed sentence stack.
   * `surfacesInFeed` controls only feed projection; `publishEvents` still fans
   * this event out through its `boardId` column.
   *
   * Payload: `{ definitionId, type, cleared }`. The card and board are columns,
   * not payload keys, so live subscribers can update exactly the affected card.
   */
  'custom.field_value_changed': { surfacesInFeed: false },
} as const satisfies Record<string, EventTypeDeclaration>;

export type EventType = keyof typeof EVENT_TYPES;

export const EVENT_TYPE_NAMES = Object.keys(EVENT_TYPES) as [EventType, ...EventType[]];

export function surfacesInFeed(type: EventType): boolean {
  return EVENT_TYPES[type].surfacesInFeed;
}

/** Names too long for the column, if any. Asserted by a test. */
export function overlongEventTypes(): string[] {
  return EVENT_TYPE_NAMES.filter((n) => n.length > EVENT_TYPE_MAX_LENGTH);
}

// ---------------------------------------------------------------------------
// Which of those names the database can actually store — the filter alphabet.
// ---------------------------------------------------------------------------

/**
 * The names declared above that are **placeholders**: they exist in this
 * registry so that something can be published without borrowing a feature's
 * vocabulary, and `ck_activity_entries_type` — the live CHECK on
 * `activity_entries.type` — does not admit them. No row can ever carry one.
 *
 * `skeleton.noop` is the only one, and the header on `EVENT_TYPES` above says
 * why it is retained on purpose: it is the fixture
 * `apps/server/src/ws/board-socket.integration.test.ts` publishes to prove the
 * Redis → socket fan-out works. It is a **socket placeholder**, not a dead
 * name to be tidied away.
 *
 * ⚠ MEASURED, NOT ASSUMED, AND RE-MEASURABLE. The live constraint carries 128
 * names; this registry declares 129. `comm` over the two sorted sets gives
 * shared-minus-live = exactly `{skeleton.noop}` and live-minus-shared = empty
 * — the CHECK is a strict subset of the registry, short by this one name. The
 * per-name grep CLAUDE.md mandates agrees: 1 miss in 129.
 * `docs/notes/webhook-events-plan.md` §5.4 carries the command; run it rather
 * than trusting this paragraph before adding a second entry.
 *
 * ⚠ A DENY-LIST, AND THE POLARITY IS THE WHOLE POINT. A name added to
 * `EVENT_TYPES` **and** to the CHECK is in `EVENT_TYPE_NAMES` and not in here,
 * so it becomes filterable automatically and needs no edit. Only a deliberate
 * new placeholder needs a line here — and whoever adds one is already reading
 * this part of the file. An allow-list would have the opposite failure: every
 * new real name silently unfilterable, with nothing red.
 *
 * ⚠ `surfacesInFeed` CANNOT SUBSTITUTE FOR THIS. It is the only property an
 * `EventTypeDeclaration` has, and 35 real names carry `false`, so it does not
 * separate `skeleton.noop` from the 128 storable ones. Nothing else is
 * derivable; the exclusion has to be stated.
 */
export const PLACEHOLDER_EVENT_TYPE_NAMES = ['skeleton.noop'] as const;

/** The type of a member of {@link PLACEHOLDER_EVENT_TYPE_NAMES}. */
export type PlaceholderEventType = (typeof PLACEHOLDER_EVENT_TYPE_NAMES)[number];

/**
 * An `EventType` a row in `activity_entries` can actually hold — the registry
 * minus its placeholders, as a **type**.
 *
 * The `./activity` `FeedEventName` idiom, for the same reason its header
 * gives: a bare `EVENT_TYPE_NAMES.filter(...)` returns `EventType[]` and
 * narrows nothing, so the union is derived at the type level and the runtime
 * array is filtered through a type guard over the same constant.
 */
export type StorableEventType = Exclude<EventType, PlaceholderEventType>;

/**
 * Narrow an `EventType` to a `StorableEventType` at runtime, from the same
 * constant the type is derived from, so the value and the type cannot
 * disagree.
 *
 * Exported because it, rather than {@link STORABLE_EVENT_TYPE_NAMES}, is what
 * `./change-registration`'s create body validates with — see the ⚠ there on
 * why that refusal is a runtime `.refine` over the full enum instead of an
 * enum over the narrow tuple.
 */
export const isStorableEventName = (type: EventType): type is StorableEventType =>
  !(PLACEHOLDER_EVENT_TYPE_NAMES as readonly string[]).includes(type);

/**
 * **The alphabet a member may filter a change registration on** —
 * `FR-AUTO-034`. 128 names today.
 *
 * The predicate is deliberately *storability in `activity_entries.type`*, not
 * *emission*: the live CHECK demonstrably admits names nothing in the product
 * emits yet (`card.copied_from_card` is asserted as exactly that in two
 * specs), so an "emittable" alphabet would overclaim and would need churning
 * every time a placeholder gained an emitter, with no DDL to force the change.
 * "The database can store it" is the only boundary that is stable,
 * database-backed and already recorded.
 *
 * Cast to a non-empty tuple the way `EVENT_TYPE_NAMES` itself is, because
 * `z.enum` requires one.
 */
export const STORABLE_EVENT_TYPE_NAMES = EVENT_TYPE_NAMES.filter(isStorableEventName) as [
  StorableEventType,
  ...StorableEventType[],
];

/**
 * What a producer supplies. `id` and `createdAt` are assigned by the writer, so
 * a caller cannot accidentally control ordering or backdate an entry.
 *
 * Exactly one actor is expected: a member (`actorUserId`) or an automation
 * (`actorAutomationName`). `FR-AUTO-*` needs the distinction preserved so the
 * feed can say "Butler moved this card" rather than attributing it to a person.
 */
export const eventInputSchema = z.object({
  type: z.enum(EVENT_TYPE_NAMES),
  boardId: ulidSchema.nullable().default(null),
  cardId: ulidSchema.nullable().default(null),
  actorUserId: ulidSchema.nullable().default(null),
  actorAutomationName: z.string().max(255).nullable().default(null),
  payload: z.record(z.string(), z.unknown()).default({}),
});

export type EventInput = z.input<typeof eventInputSchema>;

/** A stored entry, as it is read back and as it goes out over the socket. */
export const eventSchema = z.object({
  id: ulidSchema,
  type: z.enum(EVENT_TYPE_NAMES),
  boardId: ulidSchema.nullable(),
  cardId: ulidSchema.nullable(),
  actorUserId: ulidSchema.nullable(),
  actorAutomationName: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});

export type MoltEvent = z.infer<typeof eventSchema>;

/** The pub/sub channel a board's live updates travel on (§3.E, `FR-COLLAB-025`). */
export function boardChannel(boardId: string): string {
  return `molt:board:${boardId}`;
}

export function notificationChannel(userId: string): string {
  return `molt:notifications:${userId}`;
}
